// =============================================================================
// Smart Tag Control Center — Dashboard JS  v4.2.0
// Upload to: Shopify Admin → Online Store → Themes → Edit code → assets/
//
// New in v3.0.0:
//   • Version numbering unified across all files (JS, CSS, Liquid)
//   • All icons migrated to Google Material Symbols Outlined
//   • Shared Sequences tab added (internal live-sequence feature)
//   • Fleet filter bar — visual indicator for active group/library filters
//   • Fleet row-click toggles selection; group cell click filters; scans cell
//     opens per-tag analytics drawer
//   • Sortable fleet columns (ID, Group, Scans, Active Action)
//   • Per-tag analytics drawer (timeseries, device, geo, scan log)
// =============================================================================

const SUPABASE_URL      = "https://mkmflvqaglqjemugliau.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1rbWZsdnFhZ2xxamVtdWdsaWF1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ0ODE5NjQsImV4cCI6MjA5MDA1Nzk2NH0.eD0etP-qm78jDQrM2GiW7Osl_nULCWXCZJ1StYhY0Zk";
const db = supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
// SHOPIFY_GID is injected by charishub-dashboard.liquid as window.SHOPIFY_GID
// (Liquid tags only render in .liquid files, not in static assets/)
// When logged out, Liquid renders the prefix but no ID: "gid://shopify/Customer/"
// Normalise to empty string so !SHOPIFY_GID reliably detects a logged-out visitor.
const _rawGID    = window.SHOPIFY_GID || "";
const SHOPIFY_GID = (_rawGID && !_rawGID.endsWith('/')) ? _rawGID : "";
const TAG_BASE_URL    = "https://charis.ink/";
const BILLING_WORKER  = "https://billing.charis.ink";

// v3.7.0: Stripe Payment Link URLs — paste your links from Stripe Dashboard → Payment Links
// The dashboard appends ?client_reference_id={SHOPIFY_GID} so the webhook knows who paid.
const STRIPE_PAYMENT_LINKS = {
  starter:      'https://buy.stripe.com/00w00ieKmfzJauE8s80Jq00',
  essential:    'https://buy.stripe.com/14A7sK8lY0EP5akcIo0Jq02',
  professional: 'https://buy.stripe.com/eVqdR8eKm3R1byI7o40Jq01',
};

let state = {
  profileId: null, isAdmin: false,
  fleet: [], groups: [], actions: [], scans: [], schedules: [], sequences: [],
  routes: [],          // v11.0.0: conditional_routes records
  scanCounts: {}, selected: [],
  searchTerm: '', sortKey: '', sortAsc: true,
  activeAssignId: null, activeAssignType: null, activeTab: 'new',
  editTarget: null, imageData: { pfp: '', logo: '', banner: '' },
  groupFilters: [], libraryFilters: [], insightsRange: 30,
  qrIdentifier: null,
  webhookTarget: { type: null, id: null },
  analyticsRange: 30,
  analyticsCharts: {},  // holds Chart.js instances so we can destroy before re-render
  quillInstance: null,  // v11.0.0: active Quill editor instance in modal
  submissionCounts: {},   // v2.7.0: { action_id: count } for form actions
  tagAnalytics: {},       // v2.8.0: { porterId, nickname, identifier, range }
  tagAnalyticsCharts: {}, // v2.8.0: Chart.js instances for tag analytics drawer
  publishedSequences:  [], // v3.0.0: sequences this profile has published
  sharedSubscriptions: [], // v3.0.0: active subscriptions for this profile's tags
  campaigns:          [], // v3.6.0: Lead generation campaigns
  tier:               'starter', // v3.1.0: profile subscription tier
  founderLegacyIds:   [],        // v3.1.0: porter IDs that carry founder_legacy = true
  // v4.0.0: Organization
  org:                null,  // { id, name, slug, owner_id, … } or null
  orgMembers:         [],    // get_org_members() rows
  orgInvites:         [],    // org_invites rows (status=pending)
  orgLoaded:          false, // true once org data has been fetched at least once
  myOrgRole:          null,  // 'owner' | 'admin' | 'manager' | 'member' | null
};

// =============================================================================
// SUBSCRIPTION TIER HELPERS  (v3.1.0)
// =============================================================================

const TIER_RANK = { starter: 0, essential: 1, professional: 2 };

// Feature → minimum tier required
// DB values (subscription_tier):  'starter' | 'essential' | 'professional'
// Display names:                   Starter   | Essential   | Professional
const TIER_GATE = {
  contact_card:    'essential',
  form:            'essential',
  seq_unlimited:   'essential',   // more than 3 sequence steps
  shared_seq:      'essential',
  sched_unlimited: 'essential',   // more than 1 schedule
  scan_limits:     'essential',
  per_device:      'essential',
  analytics_full:  'essential',   // history beyond 30 days
  routing:         'professional',
  ab_split:        'professional',
  csv_export:      'professional',
  webhook:         'essential',
};

// Returns the effective tier for a given porter (founder_legacy floors at 'essential')
function effectiveTierFor(porterId) {
  const profileRank = TIER_RANK[state.tier] ?? 0;
  if (porterId && state.founderLegacyIds.includes(porterId)) {
    return profileRank >= TIER_RANK['essential'] ? state.tier : 'essential';
  }
  return state.tier;
}

// Returns true if the current profile (or porter, if porterId given) can use a feature
function canUse(feature, porterId = null) {
  const tier     = effectiveTierFor(porterId);
  const required = TIER_GATE[feature] || 'starter';
  return (TIER_RANK[tier] ?? 0) >= (TIER_RANK[required] ?? 0);
}

// Returns the display name of the minimum tier needed for a feature
function minTierName(feature) {
  const t = TIER_GATE[feature] || 'starter';
  return { starter: 'Starter', essential: 'Essential', professional: 'Professional' }[t] || 'Essential';
}

// Renders a small locked-feature chip used inside modals and forms
function lockChip(feature) {
  return `<span class="tier-lock-chip"><span class="mi mi-sm">lock</span> ${minTierName(feature)} plan</span>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPGRADE GATE ANALYTICS  (v3.6.0)
// Tracks which feature gates users hit most. Logs to feature_gate_hits table
// (fire-and-forget — silently no-ops if table doesn't exist yet).
// ─────────────────────────────────────────────────────────────────────────────
function trackGateHit(feature) {
  if (!state.profileId || !feature) return;
  // Fire-and-forget — never blocks UI, never throws to caller
  db.from('feature_gate_hits')
    .insert({ profile_id: state.profileId, feature, tier_at_time: state.tier || 'starter' })
    .then(({ error }) => { if (error) console.debug('gate hit log (expected if table not yet created):', error.message); })
    .catch(() => {});
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN: Gate Analytics display
// Loads and renders the feature_gate_hits aggregation table.
// Called at the end of renderAdmin() for admin users only.
// ─────────────────────────────────────────────────────────────────────────────
async function renderGateAnalytics() {
  const section = document.getElementById('admin-gate-analytics');
  const tableEl = document.getElementById('admin-gate-table');
  if (!section || !tableEl) return;

  try {
    const { data, error } = await db
      .from('feature_gate_hits')
      .select('feature, tier_at_time')
      .order('feature', { ascending: true });

    if (error || !data || !data.length) {
      section.style.display = 'none';
      return;
    }

    // Aggregate: count by feature
    const counts = {};
    data.forEach(row => { counts[row.feature] = (counts[row.feature] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const maxCount = sorted[0]?.[1] || 1;

    const featureLabels = {
      contact_card:    'Contact Cards',
      form:            'Lead Capture Form',
      seq_unlimited:   'Unlimited Sequence Steps',
      shared_seq:      'Shared Sequences',
      sched_unlimited: 'Unlimited Schedules',
      scan_limits:     'Scan Limits & Expiration',
      per_device:      'Per-Device Tracking',
      analytics_full:  'Full Analytics History',
      routing:         'Conditional Routing',
      ab_split:        'A/B Split Testing',
      csv_export:      'CSV Data Export',
      webhook:         'Scan Webhooks',
    };

    const tierColors = { essential: '#2563eb', professional: '#7c3aed' };
    const rows = sorted.map(([feat, count]) => {
      const pct      = Math.round((count / maxCount) * 100);
      const label    = featureLabels[feat] || feat;
      const minTier  = TIER_GATE[feat] || 'starter';
      const tierCol  = tierColors[minTier] || '#6b7280';
      const tierLabel = { starter: 'Starter', essential: 'Essential', professional: 'Professional' }[minTier] || minTier;
      return `<tr>
        <td>${label}</td>
        <td style="font-weight:700;">${count.toLocaleString()}</td>
        <td class="gate-bar-wrap"><div class="gate-bar-bg"><div class="gate-bar-fill" style="width:${pct}%;background:${tierCol};"></div></div></td>
        <td><span style="font-size:11px;font-weight:700;color:${tierCol};">${tierLabel}</span></td>
      </tr>`;
    }).join('');

    tableEl.innerHTML = `<div class="fleet-card" style="margin-top:0;overflow:hidden;">
      <table class="gate-table">
        <thead><tr><th>Feature</th><th>Hits</th><th style="width:140px;">Demand</th><th>Min. Plan</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
    section.style.display = 'block';

  } catch (err) {
    console.debug('Gate analytics not available:', err.message);
    section.style.display = 'none';
  }
}

// Opens the main modal with a branded upgrade prompt for a gated feature
function showUpgradeModal(feature, context) {
  // Track gate hits for admin analytics (fire-and-forget, silent on error)
  trackGateHit(feature);

  const tierName  = minTierName(feature);
  const tierColor = tierName === 'Professional' ? '#7c3aed' : '#2563eb';
  const featureLabels = {
    contact_card:    'Contact Cards',
    form:            'Survey & Gated Actions',
    seq_unlimited:   'Unlimited Sequence Steps',
    shared_seq:      'Shared Sequences',
    sched_unlimited: 'Unlimited Schedules',
    scan_limits:     'Scan Limits & Expiration',
    per_device:      'Per-Device Tracking',
    analytics_full:  'Full Analytics History',
    routing:         'Conditional Routing',
    ab_split:        'A/B Split Testing',
    csv_export:      'CSV Data Export',
    webhook:         'Scan Webhooks',
  };
  const label = featureLabels[feature] || 'This feature';

  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText    = 'Upgrade Your Plan';
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('v-msg').innerText          = '';
  document.getElementById('modal-fields').innerHTML   = `
    <div style="text-align:center;padding:12px 0 20px;">
      <span class="mi" style="font-size:48px;color:${tierColor};font-variation-settings:'FILL' 1,'wght' 400,'GRAD' 0,'opsz' 48;">workspace_premium</span>
    </div>
    <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:14px;padding:18px;margin-bottom:16px;text-align:center;">
      <div style="font-size:15px;font-weight:800;color:#1f2937;margin-bottom:6px;">${label}</div>
      <div style="font-size:13px;color:#6b7280;line-height:1.55;">
        ${context || `This feature is available on the <strong style="color:${tierColor};">${tierName}</strong> plan.`}
      </div>
    </div>
    <div style="font-size:12px;color:#9ca3af;text-align:center;line-height:1.6;">
      Upgrade to unlock this feature and everything else in the <strong>${tierName}</strong> plan.
      You can manage or cancel your subscription at any time from the dashboard.
    </div>`;

  const btn = document.getElementById('modal-confirm-btn');
  btn.className        = 'btn-charis btn-primary';
  btn.innerText        = `Upgrade to ${tierName}`;
  btn.style.background = tierColor;
  btn.style.border     = 'none';
  // v3.7.0: open Stripe Payment Link in new tab with Shopify GID as client_reference_id
  const stripeLink = STRIPE_PAYMENT_LINKS[tierName.toLowerCase()] || STRIPE_PAYMENT_LINKS.professional;
  btn.onclick = () => {
    closeModal();
    const shopifyNumericId = SHOPIFY_GID.split('/').pop();
    const url = `${stripeLink}?client_reference_id=${shopifyNumericId}&prefilled_email=${encodeURIComponent(window.SHOPIFY_EMAIL || '')}`;
    window.location.href = url;
  };
  document.getElementById('charis-modal').classList.add('active');
}

// Updates the tier badge element in the dashboard header
function renderTierBadge() {
  const badge = document.getElementById('tier-badge');
  if (!badge) return;
  const labels = { starter: 'Starter', essential: 'Essential', professional: 'Professional' };
  const colors = { starter: '#6b7280', essential: '#2563eb', professional: '#7c3aed' };
  const t = state.tier || 'starter';
  badge.innerHTML = `<span class="mi mi-sm" style="color:${colors[t]};">workspace_premium</span> ${labels[t]}`;
  badge.style.color = colors[t];
}

// v3.7.0: Show or hide the Manage Billing button based on whether the user has a Stripe subscription
function renderManageBillingBtn() {
  const btn = document.getElementById('manage-billing-btn');
  if (!btn) return;
  // Show the button only if user is on a paid tier (not starter)
  btn.style.display = (state.tier && state.tier !== 'starter') ? 'inline-flex' : 'none';
}

document.getElementById('charis-modal').addEventListener('click', function(e) {
  if (e.target === this) closeModal();
});
document.getElementById('qr-modal').addEventListener('click', function(e) {
  if (e.target === this) closeQR();
});
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') { closeModal(); closeQR(); closeHelp(); closeCommandPalette(); }
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); openCommandPalette(); }
  if ((e.metaKey || e.ctrlKey) && e.key === 'd') { e.preventDefault(); toggleDarkMode(); }
});
initDarkMode();

// ─────────────────────────────────────────────────────────────────────────────
// NAVIGATION
// ─────────────────────────────────────────────────────────────────────────────
function switchView(view) {
  ['manager','insights','analytics','admin'].forEach(v => {
    const el = document.getElementById(`view-${v}`);
    const nav = document.getElementById(`nav-${v}`);
    if (el)  el.style.display  = v === view ? 'block' : 'none';
    if (nav) nav.classList.toggle('active', v === view);
  });
  if (view === 'insights')  refreshAndRenderInsights();
  if (view === 'analytics') loadAnalytics(state.analyticsRange);
  if (view === 'admin')     renderAdmin();
}

// ─────────────────────────────────────────────────────────────────────────────
// DATA LOADING
// ─────────────────────────────────────────────────────────────────────────────
function showLoginWall(claimIdentifier) {
  const wall  = document.getElementById('login-wall');
  const title = document.getElementById('login-wall-title');
  const sub   = document.getElementById('login-wall-sub');
  const btn   = document.getElementById('login-wall-btn');
  if (!wall) return;
  const returnPath = claimIdentifier
    ? `/pages/charis-hub-dashboard?claim=${encodeURIComponent(claimIdentifier)}`
    : '/pages/charis-hub-dashboard';
  if (claimIdentifier) {
    title.innerText = `Claim Tag ${claimIdentifier}`;
    sub.innerText   = `Log in to claim tag ${claimIdentifier} and add it to your fleet.`;
  } else {
    title.innerText = 'Sign in to your account';
    sub.innerText   = 'Log in to access your Smart Tag dashboard.';
  }
  btn.href = `/account/login?return_url=${encodeURIComponent(returnPath)}`;
  wall.style.display = 'flex';
}

async function loadData() {
  // ── Logged-out guard ─────────────────────────────────────────
  if (!SHOPIFY_GID) {
    const claimParam = new URLSearchParams(window.location.search).get('claim');
    showLoginWall(claimParam ? claimParam.toUpperCase() : null);
    return;
  }
  // ─────────────────────────────────────────────────────────────
  try {
    // maybeSingle() returns null (not a 406) when no rows match — safe for logged-out visitors
    const { data: profile } = await db.from('profiles').select('id, is_admin, subscription_tier, stripe_status, notify_on_scan, notify_email, display_name, email, phone, company')
      .eq('shopify_customer_id', SHOPIFY_GID).maybeSingle();

    if (!profile) {
      // ── v2.8.3: First-time Shopify customer — auto-create profile ──
      // handle_shopify_login upserts a profiles row for this shopify_customer_id.
      // If it fails (function missing or DB error), fall back to the login wall.
      const { error: regErr } = await db.rpc('handle_shopify_login', {
        p_shopify_customer_id: SHOPIFY_GID
      });
      if (regErr) {
        const claimParam = new URLSearchParams(window.location.search).get('claim');
        showLoginWall(claimParam ? claimParam.toUpperCase() : null);
        return;
      }
      // Re-fetch the profile that was just created
      const { data: newProfile } = await db.from('profiles').select('id, is_admin, subscription_tier, stripe_status')
        .eq('shopify_customer_id', SHOPIFY_GID).maybeSingle();
      if (!newProfile) {
        const claimParam = new URLSearchParams(window.location.search).get('claim');
        showLoginWall(claimParam ? claimParam.toUpperCase() : null);
        return;
      }
      state.profileId = newProfile.id;
      state.isAdmin   = newProfile.is_admin || false;
      state.tier      = newProfile.subscription_tier || 'starter';
      showPastDueBanner(newProfile.stripe_status);
    } else {
      state.profileId      = profile.id;
      state.isAdmin        = profile.is_admin || false;
      state.tier           = profile.subscription_tier || 'starter';
      state.notifyOnScan   = profile.notify_on_scan  || false;
      state.notifyEmail    = profile.notify_email     || null;
      state.displayName    = profile.display_name     || null;
      state.profileEmail   = profile.email            || null;
      state.profilePhone   = profile.phone            || null;
      state.profileCompany = profile.company          || null;
      showPastDueBanner(profile.stripe_status);
      // Auto-seed profile from Shopify data on first load if display_name not yet set
      if (!state.displayName && window.SHOPIFY_CUSTOMER) {
        const sc = window.SHOPIFY_CUSTOMER;
        const full = [sc.firstName, sc.lastName].filter(Boolean).join(' ');
        if (full || sc.email) {
          await db.rpc('set_profile_display', {
            p_shopify_user_id: SHOPIFY_GID,
            p_display_name:    full    || '',
            p_email:           sc.email   || '',
            p_phone:           sc.phone   || '',
            p_company:         sc.company || '',
          });
          state.displayName    = full || null;
          state.profileEmail   = sc.email   || null;
          state.profilePhone   = sc.phone   || null;
          state.profileCompany = sc.company || null;
        }
      }
    }
    if (state.isAdmin) document.getElementById('nav-admin').style.display = '';
    renderTierBadge();
    renderManageBillingBtn();

    const [fRes, gRes, aRes, sRes, schRes, seqRes, rteRes, psRes, ssRes, flRes, campRes] = await Promise.all([
      db.from('user_fleet_view').select('*').eq('owner_id', state.profileId),
      db.from('porter_groups').select('*, actions:active_action_id(id,title,type,primary_data)').eq('owner_id', state.profileId),
      db.from('actions').select('*').eq('owner_id', state.profileId).order('created_at', { ascending: false }),
      db.from('scans').select('*').eq('owner_id', state.profileId).order('scanned_at', { ascending: false }).limit(500),
      db.from('schedules')
        .select('*, actions:action_id(id,title,type), fallback_action:fallback_action_id(id,title), override_action:override_action_id(id,title), schedule_windows(*)')
        .eq('owner_id', state.profileId).order('name'),
      db.from('sequences').select('*').eq('owner_id', state.profileId).order('name'),
      db.from('conditional_routes')
        .select('*, fallback:fallback_action_id(id,title,type)')
        .eq('owner_id', state.profileId).order('name'),
      // v3.0.0: published sequences this profile owns
      db.from('published_sequences').select('*').eq('owner_id', state.profileId).order('created_at', { ascending: false }),
      // v3.0.0: active subscriptions for all tags in this profile
      db.from('sequence_subscriptions')
        .select('*, seq:published_sequence_id(id,name,advance_on,mode,calendar_interval)')
        .eq('subscriber_profile_id', state.profileId).eq('active', true),
      // v3.1.0: founder_legacy flags (lightweight — only porter IDs + flag)
      db.from('porters').select('id, founder_legacy').eq('owner_id', state.profileId),
      // v3.6.0: Lead campaigns
      db.from('campaigns').select('*').eq('owner_id', state.profileId).order('name')
    ]);

    state.fleet                = fRes.data   || [];
    state.groups               = gRes.data   || [];
    state.actions              = aRes.data   || [];
    state.schedules            = schRes.data || [];
    state.sequences            = seqRes.data || [];
    state.routes               = rteRes.data || [];
    state.publishedSequences   = psRes.data  || [];
    state.sharedSubscriptions  = ssRes.data  || [];
    state.founderLegacyIds     = (flRes.data || []).filter(p => p.founder_legacy).map(p => p.id);
    state.campaigns            = campRes.data || [];

    // v3.4.1: two-step scan fetch — owner_id first, then porter_id fallback
    // (worker writes scans without owner_id, so owner_id query alone returns 0)
    const _seenScanIds = new Set();
    state.scans = [];
    (sRes.data || []).forEach(s => { if (!_seenScanIds.has(s.id)) { _seenScanIds.add(s.id); state.scans.push(s); } });
    const _porterIds = state.fleet.map(f => f.porter_id).filter(Boolean);
    if (_porterIds.length > 0) {
      const { data: _porterScans } = await db.from('scans').select('*')
        .in('porter_id', _porterIds).order('scanned_at', { ascending: false }).limit(500);
      (_porterScans || []).forEach(s => { if (!_seenScanIds.has(s.id)) { _seenScanIds.add(s.id); state.scans.push(s); } });
      state.scans.sort((a, b) => new Date(b.scanned_at || b.created_at) - new Date(a.scanned_at || a.created_at));
    }

    state.scanCounts = {};
    state.scans.forEach(s => { state.scanCounts[s.porter_id] = (state.scanCounts[s.porter_id] || 0) + 1; });

    // Load submission counts for form actions  (v2.7.0)
    const formActionIds = state.actions.filter(a => a.type === 'form').map(a => a.id);
    state.submissionCounts = {};
    if (formActionIds.length) {
      const { data: subRows } = await db.from('form_submissions')
        .select('action_id').eq('owner_id', state.profileId)
        .in('action_id', formActionIds);
      (subRows || []).forEach(r => {
        state.submissionCounts[r.action_id] = (state.submissionCounts[r.action_id] || 0) + 1;
      });
    }

    renderGroups(); renderLibrary(); renderSchedules(); renderSequences(); renderRoutes(); renderShared(); renderCampaigns(); renderFleet();
    renderScanStatsStrip();
    renderNotifyToggle();
    document.getElementById('bridge-status-manager').innerText =
      `Dashboard Ready · ${state.fleet.length} tag${state.fleet.length !== 1 ? 's' : ''}`;

    // Check for ?claim= URL param — open claim modal if present
    checkClaimParam();

    // Check for ?subscribe= URL param — open subscribe modal if present (v3.0.0)
    checkSubscribeParam();

    // Show onboarding tour for first-time users (v2.8.3)
    initOnboarding();

  } catch (err) {
    console.error('loadData error:', err);
    document.getElementById('bridge-status-manager').innerText = 'Connection error — check console.';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// INSIGHTS
// ─────────────────────────────────────────────────────────────────────────────
async function refreshAndRenderInsights() {
  const statusEl  = document.getElementById('insights-status');
  if (statusEl) statusEl.innerText = 'Fetching scan data...';
  const porterIds = state.fleet.map(f => f.porter_id).filter(Boolean);
  const seen = new Set(); let allScans = [];

  if (state.profileId) {
    const { data } = await db.from('scans').select('*').eq('owner_id', state.profileId)
      .order('scanned_at', { ascending: false }).limit(500);
    (data || []).forEach(s => { if (!seen.has(s.id)) { seen.add(s.id); allScans.push(s); } });
  }
  if (porterIds.length > 0) {
    const { data } = await db.from('scans').select('*').in('porter_id', porterIds)
      .order('scanned_at', { ascending: false }).limit(500);
    (data || []).forEach(s => { if (!seen.has(s.id)) { seen.add(s.id); allScans.push(s); } });
  }
  allScans.sort((a,b) => new Date(b.scanned_at||b.created_at) - new Date(a.scanned_at||a.created_at));
  state.scans = allScans;
  state.scanCounts = {};
  allScans.forEach(s => { state.scanCounts[s.porter_id] = (state.scanCounts[s.porter_id]||0) + 1; });
  const total = allScans.length;
  if (statusEl) statusEl.innerText = total > 0
    ? `${total.toLocaleString()} total scans across your fleet.`
    : 'No scans recorded yet.';
  renderInsights();
}

function setInsightsRange(days) {
  state.insightsRange = days;
  ['7','30','all'].forEach(r => {
    const btn = document.getElementById(`range-${r}`);
    if (!btn) return;
    btn.classList.toggle('active', (r === 'all' && days === 0) || r == days);
  });
  renderInsights();
}

function getFilteredScans() {
  if (!state.insightsRange) return state.scans;
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - state.insightsRange);
  return state.scans.filter(s => new Date(s.scanned_at||s.created_at) >= cutoff);
}

function renderInsights() {
  const scans = getFilteredScans();
  const uniqueTags = new Set(scans.map(s => s.porter_id)).size;
  const cityCounts = {};
  scans.forEach(s => { const c = s.city_name||s.city||'Unknown'; cityCounts[c]=(cityCounts[c]||0)+1; });
  const topLocation = Object.keys(cityCounts).length ? Object.keys(cityCounts).reduce((a,b)=>cityCounts[a]>cityCounts[b]?a:b) : '—';
  const mobile = scans.filter(s => /mobile|ios|android|iphone|ipad/i.test(s.os_name||s.device_type||'')).length;
  document.getElementById('stat-total-scans').innerText  = scans.length.toLocaleString();
  document.getElementById('stat-unique-tags').innerText  = uniqueTags.toLocaleString();
  document.getElementById('stat-top-location').innerText = topLocation;
  document.getElementById('stat-device-split').innerText = `${mobile} / ${scans.length - mobile}`;
  const ptagLabel = document.getElementById('ptag-range-label');
  if (ptagLabel) ptagLabel.innerText = state.insightsRange===0?'All time':state.insightsRange===7?'Last 7 days':'Last 30 days';

  const trendEl = document.getElementById('insights-trend-chart');
  if (trendEl) {
    const now = new Date(); const days = [];
    for (let i=6;i>=0;i--) { const d=new Date(now); d.setDate(d.getDate()-i); const ds=d.toISOString().split('T')[0]; const label=d.toLocaleDateString([],{weekday:'short'}); const count=state.scans.filter(s=>new Date(s.scanned_at||s.created_at).toISOString().split('T')[0]===ds).length; days.push({label,count}); }
    const maxCount = Math.max(...days.map(d=>d.count), 1);
    trendEl.innerHTML = days.map(d=>`<div class="bar-col"><div class="bar-cnt">${d.count||''}</div><div class="bar-fill" style="height:${Math.max((d.count/maxCount)*80,d.count?4:2)}px;opacity:${d.count?1:0.2};"></div><div class="bar-lbl">${d.label}</div></div>`).join('');
  }

  const perTagEl = document.getElementById('per-tag-breakdown');
  if (perTagEl) {
    const tagCounts = {}; scans.forEach(s=>{tagCounts[s.porter_id]=(tagCounts[s.porter_id]||0)+1;});
    const maxTagCount = Math.max(...Object.values(tagCounts), 1);
    const sorted = Object.entries(tagCounts).sort((a,b)=>b[1]-a[1]);
    if (!sorted.length) { perTagEl.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>wifi_tethering</span></div><div class="empty-state-title">No scans in this period</div></div>`; }
    else { perTagEl.innerHTML=sorted.map(([pid,count])=>{ const tag=state.fleet.find(f=>f.porter_id===pid); const name=tag?(tag.nickname||tag.identifier):pid.substring(0,8)+'…'; return `<div class="ptag-row"><div class="ptag-name">${name}</div><div class="ptag-bar-bg"><div class="ptag-bar-fill" style="width:${Math.round((count/maxTagCount)*100)}%"></div></div><div class="ptag-count">${count}</div></div>`; }).join(''); }
  }

  const log = document.getElementById('activity-log-container');
  if (!scans.length) { log.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>search</span></div><div class="empty-state-title">No activity yet</div><div class="empty-state-sub">Scans appear here as people interact with your tags.</div></div>`; return; }
  let html=`<table><thead><tr><th>Time</th><th>Tag</th><th>OS</th><th>Location</th></tr></thead><tbody>`;
  scans.slice(0,100).forEach(s => { const tag=state.fleet.find(f=>f.porter_id===s.porter_id); const time=new Date(s.scanned_at||s.created_at).toLocaleString([],{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); const loc=[s.city_name||s.city,s.country_code||s.country].filter(Boolean).join(', ')||'—'; html+=`<tr><td style="font-size:12px;color:var(--charis-secondary);width:100px;">${time}</td><td style="font-weight:700;">${tag?(tag.nickname||tag.identifier):'—'}</td><td><span class="status-badge badge-type">${s.os_name||s.device_type||'—'}</span></td><td style="font-size:12px;">${loc}</td></tr>`; });
  log.innerHTML = html + '</tbody></table>';
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS TAB  (v10.6.0)
// ─────────────────────────────────────────────────────────────────────────────

// v3.4.1: compute all analytics shapes client-side from a filtered scans array
function _computeAnalytics(filteredScans, days) {
  const total_scans = filteredScans.length;
  const unique_tags = new Set(filteredScans.map(s => s.porter_id)).size;

  // pct_change: compare to the prior period of the same length
  let pct_change = null;
  if (days && days > 0) {
    const periodMs = days * 24 * 60 * 60 * 1000;
    const priorCutoff  = new Date(Date.now() - 2 * periodMs);
    const currentCutoff = new Date(Date.now() - periodMs);
    const priorCount = (state.scans || []).filter(s => {
      const t = new Date(s.scanned_at || s.created_at);
      return t >= priorCutoff && t < currentCutoff;
    }).length;
    if (priorCount > 0) {
      pct_change = Math.round(((total_scans - priorCount) / priorCount) * 100);
    }
  }

  const summary = { total_scans, unique_tags, pct_change };

  // timeseries: group by YYYY-MM-DD
  const dateMap = {};
  filteredScans.forEach(s => {
    const d = new Date(s.scanned_at || s.created_at).toISOString().split('T')[0];
    dateMap[d] = (dateMap[d] || 0) + 1;
  });
  const timeseries = Object.entries(dateMap)
    .map(([scan_date, scan_count]) => ({ scan_date, scan_count }))
    .sort((a, b) => a.scan_date.localeCompare(b.scan_date));

  // topTags: group by porter_id, join fleet for display names
  const tagMap = {};
  filteredScans.forEach(s => { tagMap[s.porter_id] = (tagMap[s.porter_id] || 0) + 1; });
  const topTags = Object.entries(tagMap)
    .map(([porter_id, scan_count]) => {
      const f = (state.fleet || []).find(f => f.porter_id === porter_id) || {};
      return { scan_count, nickname: f.nickname, identifier: f.identifier, group_name: f.group_name };
    })
    .sort((a, b) => b.scan_count - a.scan_count)
    .slice(0, 10);

  // devices: group by device_type
  const deviceMap = {};
  filteredScans.forEach(s => {
    const key = s.device_type || s.os_name || 'Unknown';
    deviceMap[key] = (deviceMap[key] || 0) + 1;
  });
  const devices = Object.entries(deviceMap)
    .map(([device_type, scan_count]) => ({ device_type, scan_count }))
    .sort((a, b) => b.scan_count - a.scan_count);

  // geo: group by country
  const geoMap = {};
  filteredScans.forEach(s => {
    const key = s.country || s.country_code || 'Unknown';
    geoMap[key] = (geoMap[key] || 0) + 1;
  });
  const geo = Object.entries(geoMap)
    .map(([country, scan_count]) => ({ country, scan_count }))
    .sort((a, b) => b.scan_count - a.scan_count);

  return { summary, timeseries, topTags, devices, geo };
}

async function loadAnalytics(days) {
  // v3.1.0: cap analytics range at 30 days for free tier
  if (!canUse('analytics_full') && (days === 0 || days > 30)) {
    days = 30;
    trackGateHit('analytics_full');
    showToast('Upgrade to Essential for full analytics history.');
  }
  state.analyticsRange = days;
  ['7','30','90','all'].forEach(r => {
    const btn = document.getElementById(`an-range-${r}`);
    if (btn) {
      btn.classList.toggle('active', (r === 'all' && days === 0) || r == days);
      // v3.1.0: visually lock 90-day and all-time buttons for free tier
      if (!canUse('analytics_full') && (r === '90' || r === 'all')) {
        btn.title = `Upgrade to Essential to access ${r === 'all' ? 'all-time' : '90-day'} analytics`;
        btn.style.opacity = '0.4';
        btn.style.cursor  = 'not-allowed';
      }
    }
  });
  const el = document.getElementById('analytics-loading');
  if (el) el.style.display = 'block';
  if (!state.profileId) return;

  try {
    // v3.4.1: compute client-side from state.scans (no broken RPC calls)
    const cutoff = days > 0 ? new Date(Date.now() - days * 24 * 60 * 60 * 1000) : null;
    const filtered = cutoff
      ? (state.scans || []).filter(s => new Date(s.scanned_at || s.created_at) >= cutoff)
      : (state.scans || []).slice();
    const { summary, timeseries, topTags, devices, geo } = _computeAnalytics(filtered, days);

    if (el) el.style.display = 'none';
    renderAnalyticsSummary(summary);
    renderTimeseriesChart(timeseries, days);
    renderTopTagsTable(topTags);
    renderDeviceChart(devices);
    renderGeoTable(geo);
  } catch(err) {
    console.error('Analytics load error:', err);
    if (el) el.innerText = 'Failed to load analytics. Check console.';
  }
}

function loadAnalyticsCustom() {
  const from = document.getElementById('an-custom-from')?.value;
  const to   = document.getElementById('an-custom-to')?.value;
  // v3.6.1: BUG-007 fix — deactivate preset pills as soon as either custom date field has a value,
  // not only when both are filled (previously the active pill persisted if only one field was changed)
  if (from || to) {
    ['7','30','90','all'].forEach(r => {
      const btn = document.getElementById(`an-range-${r}`);
      if (btn) btn.classList.remove('active');
    });
  }
  if (!from || !to) return;
  const start = new Date(from + 'T00:00:00').toISOString();
  const end   = new Date(to   + 'T23:59:59').toISOString();
  loadAnalyticsRange(start, end);
}

async function loadAnalyticsRange(start, end) {
  const el = document.getElementById('analytics-loading');
  if (el) el.style.display = 'block';
  if (!state.profileId) return;
  try {
    // v3.4.1: compute client-side from state.scans (no broken RPC calls)
    const startDate = new Date(start);
    const endDate   = new Date(end);
    const filtered = (state.scans || []).filter(s => {
      const t = new Date(s.scanned_at || s.created_at);
      return t >= startDate && t <= endDate;
    });
    const { summary, timeseries, topTags, devices, geo } = _computeAnalytics(filtered, null);
    if (el) el.style.display = 'none';
    renderAnalyticsSummary(summary);
    renderTimeseriesChart(timeseries, null, start, end);
    renderTopTagsTable(topTags);
    renderDeviceChart(devices);
    renderGeoTable(geo);
  } catch(err) {
    console.error('Analytics range load error:', err);
    if (el) el.innerText = 'Failed to load analytics. Check console.';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV EXPORT  (v3.6.0 — Professional tier)
// ─────────────────────────────────────────────────────────────────────────────
function exportScansCsv() {
  if (!canUse('csv_export')) {
    trackGateHit('csv_export');
    showUpgradeModal('csv_export',
      'Export your full scan history as a CSV file for use in spreadsheets, ' +
      'CRMs, and custom reporting tools. Available on the Professional plan.');
    return;
  }

  const scans = state.scans || [];
  if (!scans.length) { showToast('No scan data to export.'); return; }

  // Build a porter lookup for enriching rows with identifier/nickname
  const porterMap = {};
  (state.fleet || []).forEach(p => {
    porterMap[p.porter_id || p.id] = { identifier: p.identifier, nickname: p.nickname };
  });

  const esc = v => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const headers = ['Scanned At','Tag ID','Nickname','Device Type','Browser','OS',
                   'Country','City','Action ID','Referrer','Device UUID'];
  const rows = scans.map(s => {
    const p = porterMap[s.porter_id] || {};
    return [
      esc(s.scanned_at || s.created_at),
      esc(p.identifier  || s.porter_id),
      esc(p.nickname    || ''),
      esc(s.device_type || ''),
      esc(s.browser     || ''),
      esc(s.os_name     || ''),
      esc(s.country     || s.country_code || ''),
      esc(s.city        || s.city_name    || ''),
      esc(s.action_id   || ''),
      esc(s.referrer    || ''),
      esc(s.device_id   || '')
    ].join(',');
  });

  const csv     = [headers.join(','), ...rows].join('\n');
  const blob    = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url     = URL.createObjectURL(blob);
  const a       = document.createElement('a');
  const date    = new Date().toISOString().split('T')[0];
  a.href        = url;
  a.download    = `scan-history-${date}.csv`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);

  showToast(`Exported ${scans.length.toLocaleString()} scans ↓`);
}

function renderAnalyticsSummary(s) {
  const set = (id, val) => { const el = document.getElementById(id); if (el) el.innerText = val; };
  set('an-total',   (s.total_scans || 0).toLocaleString());
  set('an-tags',    (s.unique_tags || 0).toLocaleString());
  const pct = s.pct_change;
  const trendEl = document.getElementById('an-trend');
  if (trendEl) {
    if (pct == null) { trendEl.innerText = '—'; trendEl.className = 'an-trend-neutral'; }
    else {
      trendEl.innerText = `${pct > 0 ? '+' : ''}${pct}% vs prior period`;
      trendEl.className = pct > 0 ? 'an-trend-up' : pct < 0 ? 'an-trend-down' : 'an-trend-neutral';
    }
  }
}

function renderTimeseriesChart(rows, days) {
  const ctx = document.getElementById('an-timeseries-chart');
  if (!ctx) return;
  if (state.analyticsCharts.timeseries) { state.analyticsCharts.timeseries.destroy(); }

  // Fill date gaps so Chart.js gets a continuous line
  const dataMap = {};
  rows.forEach(r => { dataMap[r.scan_date] = Number(r.scan_count); });

  const labels = [], values = [];
  const numDays = days || 90;
  const now = new Date();
  for (let i = numDays - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(d.getDate() - i);
    const key = d.toISOString().split('T')[0];
    labels.push(d.toLocaleDateString([], { month: 'short', day: 'numeric' }));
    values.push(dataMap[key] || 0);
  }

  // For "all time" with many days, sample to keep the chart readable
  const maxPoints = 90;
  const step = Math.max(1, Math.floor(labels.length / maxPoints));
  const sampledLabels = labels.filter((_, i) => i % step === 0);
  const sampledValues = values.filter((_, i) => i % step === 0);

  state.analyticsCharts.timeseries = new Chart(ctx, {
    type: 'line',
    data: {
      labels: sampledLabels,
      datasets: [{
        label: 'Scans',
        data: sampledValues,
        borderColor: '#0071e3',
        backgroundColor: 'rgba(0,113,227,0.08)',
        borderWidth: 2,
        pointRadius: sampledLabels.length > 30 ? 0 : 3,
        tension: 0.3,
        fill: true
      }]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { maxTicksLimit: 8, font: { size: 11 } } },
        y: { beginAtZero: true, ticks: { font: { size: 11 } } }
      }
    }
  });
}

function renderDeviceChart(rows) {
  const ctx = document.getElementById('an-device-chart');
  if (!ctx) return;
  // v3.6.1: BUG-006 fix — always destroy previous chart before checking for empty data,
  // otherwise stale chart remains and shows a phantom blue ring on zero-scan ranges
  if (state.analyticsCharts.device) { state.analyticsCharts.device.destroy(); state.analyticsCharts.device = null; }
  if (!rows.length) {
    const parent = ctx.parentElement;
    if (parent) parent.innerHTML = '<div class="an-empty" style="padding:24px 0;text-align:center;color:#86868b;font-size:13px;">No device data for this period.</div>';
    return;
  }

  const palette = ['#0071e3','#34c759','#ff9500','#af52de','#ff2d55','#64d2ff'];
  state.analyticsCharts.device = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: rows.map(r => r.device_type),
      datasets: [{
        data: rows.map(r => Number(r.scan_count)),
        backgroundColor: rows.map((_, i) => palette[i % palette.length]),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12, padding: 10 } }
      },
      cutout: '62%'
    }
  });
}

function renderTopTagsTable(rows) {
  const el = document.getElementById('an-top-tags');
  if (!el) return;
  if (!rows.length) { el.innerHTML = '<div class="an-empty">No scans in this period.</div>'; return; }
  const max = Math.max(...rows.map(r => Number(r.scan_count)), 1);
  el.innerHTML = rows.map(r => {
    const pct = Math.round((Number(r.scan_count) / max) * 100);
    const name = r.nickname || r.identifier || '—';
    const group = r.group_name ? `<span class="an-tag-group">${r.group_name}</span>` : '';
    return `<div class="an-tag-row">
      <div class="an-tag-name">${name}${group}</div>
      <div class="an-tag-bar-bg"><div class="an-tag-bar-fill" style="width:${pct}%"></div></div>
      <div class="an-tag-count">${Number(r.scan_count).toLocaleString()}</div>
    </div>`;
  }).join('');
}

function renderGeoTable(rows) {
  const el = document.getElementById('an-geo-table');
  if (!el) return;
  if (!rows.length) { el.innerHTML = '<div class="an-empty">No location data yet.</div>'; return; }
  const total = rows.reduce((s, r) => s + Number(r.scan_count), 0);
  el.innerHTML = `<table class="an-geo"><thead><tr><th>Country</th><th>Scans</th><th>Share</th></tr></thead><tbody>` +
    rows.map(r => {
      const pct = total > 0 ? ((Number(r.scan_count) / total) * 100).toFixed(1) : '0.0';
      return `<tr><td>${r.country}</td><td>${Number(r.scan_count).toLocaleString()}</td><td>${pct}%</td></tr>`;
    }).join('') + '</tbody></table>';
}

// ─────────────────────────────────────────────────────────────────────────────
// PER-TAG ANALYTICS DRAWER  (v2.8.0)
// ─────────────────────────────────────────────────────────────────────────────
function openTagAnalytics(porterId, nickname, identifier) {
  state.tagAnalytics = { porterId, nickname, identifier, range: '30' };
  document.getElementById('tag-an-title').innerText = nickname || identifier;
  document.getElementById('tag-an-sub').innerText   = identifier;
  document.getElementById('tag-an-drawer').classList.add('open');
  document.getElementById('tag-an-overlay').classList.add('active');
  setTagAnalyticsRange('30');
}

function closeTagAnalytics() {
  document.getElementById('tag-an-drawer').classList.remove('open');
  document.getElementById('tag-an-overlay').classList.remove('active');
  // destroy charts to free memory
  if (state.tagAnalyticsCharts) {
    Object.values(state.tagAnalyticsCharts).forEach(c => { try { c.destroy(); } catch(e){} });
    state.tagAnalyticsCharts = {};
  }
}

function setTagAnalyticsRange(preset) {
  ['30','90','all'].forEach(r => {
    const btn = document.getElementById(`tag-an-btn-${r}`);
    if (btn) btn.classList.toggle('active', r === preset);
  });
  state.tagAnalytics.range = preset;
  const now   = new Date();
  let start, end = now.toISOString();
  if (preset === '30')  { const d = new Date(now); d.setDate(d.getDate()-30);  start = d.toISOString(); }
  if (preset === '90')  { const d = new Date(now); d.setDate(d.getDate()-90);  start = d.toISOString(); }
  if (preset === 'all') { start = new Date('2020-01-01').toISOString(); }
  if (preset === 'custom') {
    const fromVal = document.getElementById('tag-an-from')?.value;
    const toVal   = document.getElementById('tag-an-to')?.value;
    if (!fromVal || !toVal) return;
    start = new Date(fromVal + 'T00:00:00').toISOString();
    end   = new Date(toVal   + 'T23:59:59').toISOString();
  }
  loadTagAnalytics(state.tagAnalytics.porterId, start, end);
}

async function loadTagAnalytics(porterId, start, end) {
  const loading = document.getElementById('tag-an-loading');
  const body    = document.getElementById('tag-an-body');
  if (loading) loading.style.display = 'block';
  if (body)    body.style.display    = 'none';

  try {
    const [summary, timeseries, devices, geo, log] = await Promise.all([
      db.rpc('get_tag_summary',          { p_porter_id: porterId, p_start: start, p_end: end }),
      db.rpc('get_tag_timeseries',       { p_porter_id: porterId, p_start: start, p_end: end }),
      db.rpc('get_tag_device_breakdown', { p_porter_id: porterId, p_start: start, p_end: end }),
      db.rpc('get_tag_geo_breakdown',    { p_porter_id: porterId, p_start: start, p_end: end }),
      db.rpc('get_tag_scan_log',         { p_porter_id: porterId, p_start: start, p_end: end, p_limit: 25 })
    ]);

    if (loading) loading.style.display = 'none';
    if (body)    body.style.display    = 'block';

    renderTagSummary(summary.data || {});
    renderTagTimeseries(timeseries.data || [], start, end);
    renderTagDeviceChart(devices.data || []);
    renderTagGeoTable(geo.data || []);
    renderTagScanLog(log.data || []);
  } catch(err) {
    console.error('Tag analytics error:', err);
    if (loading) loading.innerText = 'Failed to load. Check console.';
  }
}

function renderTagSummary(s) {
  const set = (id, val) => { const el = document.getElementById(id); if (el) el.innerText = val; };
  set('tag-an-total',       (s.total_scans || 0).toLocaleString());
  set('tag-an-top-device',  s.top_device  || '—');
  set('tag-an-top-country', s.top_country || '—');
  if (s.peak_day && s.peak_count) {
    const d = new Date(s.peak_day);
    set('tag-an-peak', `${d.toLocaleDateString([],{month:'short',day:'numeric'})} (${s.peak_count})`);
  } else {
    set('tag-an-peak', '—');
  }
}

function renderTagTimeseries(rows, start, end) {
  const ctx = document.getElementById('tag-an-timeseries-chart');
  if (!ctx) return;
  if (state.tagAnalyticsCharts?.timeseries) { try { state.tagAnalyticsCharts.timeseries.destroy(); } catch(e){} }

  const dataMap = {};
  rows.forEach(r => { dataMap[r.scan_date] = Number(r.scan_count); });

  const labels = [], values = [];
  const startD = new Date(start), endD = new Date(end);
  const totalDays = Math.ceil((endD - startD) / 86400000);
  const step = Math.max(1, Math.floor(totalDays / 90));
  for (let i = 0; i <= totalDays; i += step) {
    const d = new Date(startD); d.setDate(d.getDate() + i);
    const key = d.toISOString().split('T')[0];
    labels.push(d.toLocaleDateString([], { month: 'short', day: 'numeric' }));
    values.push(dataMap[key] || 0);
  }

  if (!state.tagAnalyticsCharts) state.tagAnalyticsCharts = {};
  state.tagAnalyticsCharts.timeseries = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{ label: 'Scans', data: values, backgroundColor: 'rgba(0,113,227,0.75)', borderRadius: 4 }]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { size: 10 } } },
        y: { beginAtZero: true, ticks: { font: { size: 10 }, precision: 0 } }
      }
    }
  });
}

function renderTagDeviceChart(rows) {
  const ctx = document.getElementById('tag-an-device-chart');
  if (!ctx) return;
  if (state.tagAnalyticsCharts?.device) { try { state.tagAnalyticsCharts.device.destroy(); } catch(e){} }
  if (!rows.length) { ctx.parentElement.innerHTML = '<div class="tag-an-empty">No device data.</div>'; return; }

  const palette = ['#0071e3','#34c759','#ff9500','#af52de','#ff2d55'];
  if (!state.tagAnalyticsCharts) state.tagAnalyticsCharts = {};
  state.tagAnalyticsCharts.device = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: rows.map(r => r.device_type),
      datasets: [{ data: rows.map(r => Number(r.scan_count)), backgroundColor: palette, borderWidth: 2, borderColor: '#fff' }]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 10, padding: 8 } } },
      cutout: '60%'
    }
  });
}

function renderTagGeoTable(rows) {
  const el = document.getElementById('tag-an-geo');
  if (!el) return;
  if (!rows.length) { el.innerHTML = '<div class="tag-an-empty">No location data.</div>'; return; }
  const total = rows.reduce((s, r) => s + Number(r.scan_count), 0);
  el.innerHTML = '<table class="scan-log-table"><thead><tr><th>Country</th><th>Scans</th><th>%</th></tr></thead><tbody>' +
    rows.map(r => {
      const pct = total > 0 ? ((Number(r.scan_count)/total)*100).toFixed(1) : '0.0';
      return `<tr><td>${r.country}</td><td>${Number(r.scan_count).toLocaleString()}</td><td>${pct}%</td></tr>`;
    }).join('') + '</tbody></table>';
}

function renderTagScanLog(rows) {
  const el = document.getElementById('tag-an-log');
  if (!el) return;
  if (!rows.length) { el.innerHTML = '<div class="tag-an-empty">No scans in this period.</div>'; return; }
  el.innerHTML = '<table class="scan-log-table"><thead><tr><th>Time</th><th>Device</th><th>Country</th><th>Variant</th></tr></thead><tbody>' +
    rows.map(r => {
      const t = new Date(r.scanned_at).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
      const variant = r.ab_variant ? `<span class="status-badge" style="background:#f0f0f5;">${r.ab_variant}</span>` : '—';
      return `<tr>
        <td style="white-space:nowrap;">${t}</td>
        <td><span class="scan-log-device">${r.device_type||'Unknown'}</span></td>
        <td class="scan-log-country">${r.country_code||'Unknown'}</td>
        <td>${variant}</td>
      </tr>`;
    }).join('') + '</tbody></table>';
}

// ─────────────────────────────────────────────────────────────────────────────
// HELP DRAWER  (v10.6.0)
// ─────────────────────────────────────────────────────────────────────────────
function openHelp() {
  document.getElementById('help-drawer').classList.add('open');
  document.getElementById('help-overlay').classList.add('active');
  document.getElementById('help-search').value = '';
  renderHelpContent('');
}

function closeHelp() {
  document.getElementById('help-drawer').classList.remove('open');
  document.getElementById('help-overlay').classList.remove('active');
}

function filterHelp() {
  renderHelpContent(document.getElementById('help-search').value.toLowerCase().trim());
}

// ─────────────────────────────────────────────────────────────────────────────
// HELP SYSTEM  (v2.8.3 — two-column layout)
// ─────────────────────────────────────────────────────────────────────────────
let helpActiveId = null;

/* All help sections.  The `group` property drives the left-column nav dividers. */
const HELP_SECTIONS = [
  // ── Getting Started ───────────────────────────────────────────────────────
  {
    id: 'what-is-charishub', group: 'Getting Started', icon: `<span class='mi'>lightbulb</span>`,
    title: 'What is Smart Tag Control Center?',
    content: `At its core, Smart Tag Control Center is about two things: a <strong>trigger</strong> and an <strong>action</strong>.<br><br>
The <strong>trigger</strong> is a physical object — an NFC tag or a QR code — that a person taps or scans with their phone. The <strong>action</strong> is what happens next: a web page opens, a contact is saved, a WiFi network connects, a message appears.<br><br>
<strong>The problem we solve</strong><br>
QR codes and NFC tags have always suffered from the same limitation: they are static. Once printed or programmed, they point to exactly one destination, forever. Change your website? Reprint every tag. Run a seasonal campaign? Replace every QR code. The physical object and the digital destination are locked together.<br><br>
Smart Tag Control Center breaks that lock. Every tag points not to a destination, but to a <em>lookup URL</em> — <code>charis.ink/[ID]</code>. When someone scans it, our system decides in real time what action to serve, based on rules you define. The physical tag never changes. Everything else can.<br><br>
One tag can serve a business card today, a product page tomorrow, and a time-limited offer next week — without touching the hardware. 500 hotel room tags can all be updated to a new check-out form in seconds. You can serve different content to someone in France than someone in the US, or show a mobile visitor something different from a desktop user.`
  },
  {
    id: 'quick-start', group: 'Getting Started', icon: `<span class='mi'>rocket_launch</span>`,
    title: 'Quick Start',
    content: `<strong>3 steps to get a tag working:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Tap your unregistered tag with a phone — it auto-claims to your account and appears in your Fleet.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Find it in the Fleet, click the row to open the Assign modal, and choose an action type or pick an existing action from your library.</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Save — the tag is live immediately. Anyone who taps it now gets that action.</span></div>
</div><br>
Click the tag's identifier in the Fleet table to copy its live URL (<code>charis.ink/[ID]</code>) to your clipboard for quick testing in a browser.`
  },
  {
    id: 'porters', group: 'Getting Started', icon: `<span class='mi'>label</span>`,
    title: 'Your Fleet',
    content: `A <strong>Porter</strong> is your physical tag in the system. Each has a unique identifier that maps to <code>charis.ink/[ID]</code>. The same URL works for both NFC taps and QR code scans — you never need two tags for two channels.<br><br>
<strong>Actions from each tag row:</strong><br>
<em>Click the row</em> — opens the Assign modal to set or change the tag's action.<br>
<em>Rename</em> — give the tag a meaningful internal label (guests and scanners never see this).<br>
<em>QR</em> — generates a scannable QR code image you can download and print.<br>
<em>Limits</em> — configures a scan cap or expiry date with a fallback action.<br>
<em>Click the identifier</em> — copies the live tag URL to your clipboard.<br><br>
Use the search bar to filter by name, group, or identifier. Click column headers to sort the fleet table.`
  },
  // ── Actions ───────────────────────────────────────────────────────────────
  {
    id: 'actions-overview', group: 'Actions', icon: `<span class='mi'>bolt</span>`,
    title: 'What Are Actions?',
    content: `An <strong>Action</strong> is what happens when someone scans one of your tags. It is the digital destination or experience you deliver through the physical tag.<br><br>
Actions are stored in your <strong>Library</strong> — a central collection you can assign and reassign across any number of tags or groups. Change an action's content once and every tag using it updates instantly, with no reprinting or reprogramming.<br><br>
<strong>Available action types:</strong><br>
URL Redirect · Contact Card (vCard) · Phone Call · SMS · Email · WiFi Credentials · Rich Text Message · Webhook · A/B Split Test · Lead Capture Form<br><br>
You can create actions in the Library and assign them later, or create-and-assign in one step from the tag's Assign modal. Actions can also be layered with Schedules, Sequences, and Routes for more intelligent behaviour — see those sections for details.`
  },
  {
    id: 'howto-actions', group: 'Actions', icon: `<span class='mi'>build</span>`,
    title: 'Create & Edit Actions',
    content: `Actions are the destinations your tags serve. You can create them in the Library and assign them later, or create-and-assign in one step from the Assign modal.<br><br>
<strong>Create a new action from the Library:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>bolt</span> Library</em> tab in the command center, then tap <em>+ New Action</em>.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Choose an action type from the selector at the top of the modal (URL, Phone, SMS, Email, WiFi, Message, vCard, Webhook, Form, or A/B Split).</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Give the action a <strong>title</strong> — this is your internal label; scanners never see it.</span></div>
  <div class="help-step"><span class="help-step-num">4</span><span>Fill in the type-specific fields (e.g. URL for a redirect, or phone number for a call action).</span></div>
  <div class="help-step"><span class="help-step-num">5</span><span>Click <em>Save to Library</em>. The action appears in your library list and is ready to assign.</span></div>
</div><br>
<strong>Create and assign in one step:</strong><br>
Click a tag row in the Fleet → choose the <em>Create New</em> tab in the Assign modal → fill in the form → click <em>Save & Assign</em>. The action is created and immediately linked to that tag.<br><br>
<strong>Edit an existing action:</strong><br>
In the Library, find the action card and click <em><span class='mi mi-sm'>edit</span> Edit</em>. The same modal opens pre-filled. Change any fields and click <em>Update Action</em>. All tags using that action update automatically — no reassignment needed.<br><br>
<strong>Delete an action:</strong><br>
Click <em><span class='mi mi-sm'>edit</span> Edit</em> on the action card, scroll to the bottom, and click <em>Delete Action</em>. Tags or groups assigned to it are unassigned automatically.<br><br>
<strong>Notes:</strong><br>
• A/B Split and Form actions have extra setup — see their individual sections.<br>
• Images (profile photo, logo, banner on vCard) are uploaded in the action modal. Supported: JPG, PNG, WebP.<br>
• Action titles are searchable in the Library — descriptive names like "Trade Show Card 2026" keep things tidy.`
  },
  {
    id: 'action-url', group: 'Actions', icon: `<span class='mi'>link</span>`,
    title: 'URL Redirect',
    content: `Redirects the person scanning to any web address instantly. The most common and versatile action type.<br><br>
<strong>Required:</strong><br>
<em>URL</em> — the full destination address, including <code>https://</code>. Can be any publicly accessible URL: a website, PDF, Google Doc, Shopify product page, YouTube video, booking link, or anything else.<br><br>
<strong>Use cases:</strong><br>
A tag on a retail shelf display redirects to that product's page. When the product changes, update the URL in the dashboard — no new tag needed. A tag on event signage points to the registration page during the lead-up, then switches automatically (via a Schedule) to the post-event recording. A restaurant menu board tag links to the current day's specials PDF, swapped weekly without touching the tag.`
  },
  {
    id: 'action-vcard', group: 'Actions', icon: `<span class='mi'>person</span>`,
    title: 'Contact Card (vCard)',
    content: `Serves a branded digital business card with a one-tap "Save Contact" button. Writes a full vCard to the person's phone contacts — no app required.<br><br>
<strong>Required:</strong><br>
<em>First Name</em> and <em>Last Name</em> — the name that appears in the contact.<br><br>
<strong>Optional but recommended:</strong><br>
<em>Job Title, Company, Profile Photo, Company Logo</em> — appear on the card page and in the saved contact. <em>Phone Numbers</em> and <em>Email Addresses</em> — multiple entries, each labeled (mobile, work, personal). <em>Social Links</em> — LinkedIn, Instagram, Twitter/X, and more. <em>Address</em> — full street address. <em>Theme</em> — primary colour, background colour, and font to match your brand.<br><br>
<strong>Use cases:</strong><br>
A tag on the back of a phone case or a networking lanyard. When tapped, the recipient sees a polished card page and saves your details in one tap — no paper exchanged. Update your phone number or title in the dashboard and every existing tag reflects the change immediately.`
  },
  {
    id: 'action-phone', group: 'Actions', icon: `<span class='mi'>phone</span>`,
    title: 'Phone Call',
    content: `Opens the phone's dialer with a number pre-filled, ready to call with one tap.<br><br>
<strong>Required:</strong><br>
<em>Phone Number</em> — include the country code (e.g. <code>+15551234567</code>).<br><br>
<strong>Use cases:</strong><br>
A tag on a hotel room desk routes guests directly to the front desk. A tag on a product box connects customers to a support line in one tap. A tag on a field service vehicle calls dispatch immediately. Useful anywhere you want to eliminate the friction between a person and a phone call.`
  },
  {
    id: 'action-sms', group: 'Actions', icon: `<span class='mi'>chat_bubble</span>`,
    title: 'SMS Message',
    content: `Opens the phone's messaging app with a recipient number and message body pre-filled. The person just taps Send.<br><br>
<strong>Required:</strong><br>
<em>To</em> — the recipient's phone number, including country code.<br><br>
<strong>Optional:</strong><br>
<em>Pre-filled Message</em> — the body text ready to send. Can be a keyword (e.g. <code>JOIN</code>), a short message, or left blank so the person writes their own.<br><br>
<strong>Use cases:</strong><br>
A tag at a trade show pre-fills "I'd like more info" to your sales number — the prospect taps send and you have their number instantly. A real estate yard sign tag pre-fills "Send me details about [address]" to the agent. A music venue tag opens a text to an opt-in shortcode adding the person to an SMS list without any form.`
  },
  {
    id: 'action-email', group: 'Actions', icon: `<span class='mi'>email</span>`,
    title: 'Email',
    content: `Opens the device's email client with recipient, subject, and body pre-filled. One tap to send.<br><br>
<strong>Required:</strong><br>
<em>To</em> — the destination email address.<br><br>
<strong>Optional:</strong><br>
<em>Subject</em> — pre-filled subject line. <em>Body</em> — pre-filled message body. Both can include context, e.g. "Enquiry from Room 12 tag."<br><br>
<strong>Use cases:</strong><br>
A hotel room tag pre-fills an email to housekeeping — "Room 14 Towel Request" — with a blank body for the guest. A museum exhibit tag opens a pre-addressed email to the curator with the exhibit name in the subject, inviting feedback. A product tag opens a warranty registration email with the model pre-filled in the subject.`
  },
  {
    id: 'action-wifi', group: 'Actions', icon: `<span class='mi'>wifi</span>`,
    title: 'WiFi Credentials',
    content: `Displays the network name and password on a clean branded page with a one-tap copy button. Works for any WiFi security type.<br><br>
<strong>Required:</strong><br>
<em>Network Name (SSID)</em> — exactly as it appears in the device's network list.<br>
<em>Password</em> — stored securely and only shown to the person scanning.<br><br>
<strong>Optional:</strong><br>
<em>Security Type</em> — WPA/WPA2 (default), WEP, or None (open network).<br><br>
<strong>Use cases:</strong><br>
A tag on every café or restaurant table replaces the "ask staff for the WiFi password" experience. An Airbnb welcome card shows the home WiFi — when the password changes, update it once in the dashboard and all tags are instantly current. A conference venue provides quick network access without printing passwords on cards.`
  },
  {
    id: 'action-message', group: 'Actions', icon: `<span class='mi'>description</span>`,
    title: 'Rich Text Message',
    content: `Serves a branded full-page message using a built-in rich text editor. Supports headings, bold, italic, bullet lists, numbered lists, and hyperlinks — no HTML knowledge required.<br><br>
<strong>Required:</strong><br>
<em>Title</em> — the heading shown at the top of the page.<br>
<em>Content</em> — written in the rich text editor. Can include formatted text, lists, and clickable links.<br><br>
<strong>Use cases:</strong><br>
An Airbnb welcome card tag serves a full house manual — rules, appliance instructions, emergency contacts, local recommendations. A product tag serves assembly instructions as a numbered-step page. A gallery exhibit tag serves the artist biography and links to related works — far more content than a printed label allows.`
  },
  {
    id: 'action-webhook', group: 'Actions', icon: `<span class='mi'>bolt</span>`,
    title: 'Webhook',
    content: `Fires an HTTP request to a URL you control the moment a tag is scanned. Connects tags to any external system — a CRM, spreadsheet, Slack, Zapier, Make.com, or your own API.<br><br>
<strong>Required:</strong><br>
<em>Webhook URL</em> — the endpoint that will receive the request.<br><br>
<strong>Optional:</strong><br>
<em>Method</em> — POST (default) or GET. <em>Payload JSON</em> — a custom JSON body. Supports dynamic variables: <code>{{tag_id}}</code>, <code>{{identifier}}</code>, <code>{{nickname}}</code>, <code>{{timestamp}}</code>, <code>{{country}}</code>, <code>{{device_type}}</code>.<br><br>
<strong>Note:</strong> This action fires the request but shows no page to the scanner. Use it in a Sequence or pair it with the per-tag webhook field if you also want to serve visible content.<br><br>
<strong>Use cases:</strong><br>
A warehouse shelf tag fires a POST to a Google Sheet via Zapier on each scan, logging timestamp and location automatically. A conference session tag fires to your CRM creating attendance records. A retail fitting room tag sends a Slack message to the floor team — a simple staff call button.`
  },
  {
    id: 'action-ab', group: 'Actions', icon: `<span class='mi'>alt_route</span>`,
    title: 'A/B Split Test',
    content: `Randomly serves one of two actions at a ratio you set. Both variants are tracked separately in Analytics so you can compare performance and pick a winner with real data.<br><br>
<strong>Required:</strong><br>
<em>Variant A</em> — an existing action from your library.<br>
<em>Variant B</em> — a second existing action from your library.<br>
<em>Weight</em> — percentage of scans going to Variant A. Variant B gets the remainder. Default: 50/50.<br><br>
<strong>Setup note:</strong> Create both variant actions in your library first, then create the A/B Split action and select them. A/B splits can also be used as individual steps inside a Sequence.<br><br>
<strong>Use cases:</strong><br>
A product display tag splits traffic between a video-led and a text-led landing page to see which drives more conversions. A hospitality tag tests two welcome message tones (formal vs. conversational). A retail tag tests two promotional offers at 70/30 before committing the whole fleet to the winner.`
  },
  {
    id: 'action-form', group: 'Actions', icon: `<span class='mi mi-sm'>inbox</span>`,
    title: 'Lead Capture Form',
    content: `Serves a clean branded form page. The person scanning fills in fields and submits. Responses are stored in the Submissions drawer on the action card in your library. If a redirect URL is set, the person is sent there after submitting — gating the destination behind the form.<br><br>
<strong>Required:</strong><br>
<em>Form Title</em> — the heading shown at the top of the form page.<br>
<em>At least one Form Field</em> — each field has a label, a type (Text, Email, Phone, Number, or Select with custom options), and an optional Required toggle.<br><br>
<strong>Optional:</strong><br>
<em>Description</em> — a short intro above the fields. <em>Submit Button Label</em> — defaults to "Submit." <em>Redirect URL</em> — where to send the person after submitting. <em>Success Message</em> — shown when no redirect is set.<br><br>
<strong>Webhook integration:</strong> If a webhook is configured on the tag, form submissions include the full <code>submission</code> object in the payload — responses flow automatically to your CRM or Zapier workflow.<br><br>
<strong>Use cases:</strong><br>
<em>Gated content</em> — require an email before redirecting to a product catalog PDF. <em>Event registration</em> — collect name, email, and company from attendees in seconds. <em>Waitlist</em> — capture email addresses directly into your submissions log. <em>Feedback</em> — collect a star rating and comment with no app required from the submitter.`
  },
  // ── Groups ────────────────────────────────────────────────────────────────
  {
    id: 'groups-overview', group: 'Groups', icon: `<span class='mi'>folder</span>`,
    title: 'What Are Groups?',
    content: `A <strong>Group</strong> is a container that lets you assign one action to many tags at once. Change the group's action and every member tag updates instantly — no individual tag management needed.<br><br>
Groups are the answer to scale. Configuring five tags individually is easy. Configuring 500 is not — unless they're in groups.<br><br>
<strong>Example uses:</strong><br>
<em>Hotel property</em> — 200 room tags in a "Rooms" group pointing to the check-in page. Switch PMS systems — update one URL, all 200 follow.<br>
<em>Retail floor</em> — tags divided by department. Marketing updates "Outerwear" without touching "Footwear."<br>
<em>Event lanyards</em> — 500 attendee badges in a "Conference 2026" group. After the event, redirect the whole group to your main site in one click.<br><br>
<strong>Individual overrides:</strong> any tag in a group can still have its own action assigned directly. That tag's individual action takes priority, while all other group members are unaffected.`
  },
  {
    id: 'howto-groups', group: 'Groups', icon: `<span class='mi'>folder_open</span>`,
    title: 'Create & Manage Groups',
    content: `Groups let you assign one action to many tags at once. Any change to the group's action immediately applies to all member tags.<br><br>
<strong>Create a new group:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>folder</span> Groups</em> tab in the command center, then tap <em>+ New Group</em>.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Enter a group name (e.g. "Hotel Rooms", "Trade Show Floor") and click <em>Create Group</em>.</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>The group appears in the Groups list. It's empty until you add tags.</span></div>
</div><br>
<strong>Add tags to a group:</strong><br>
<em>Method A — checkboxes:</em> Check the boxes next to tags in the Fleet table, then click the target group card. All selected tags move instantly.<br>
<em>Method B — individual:</em> Click a tag row → Assign → the modal lets you change the tag's group assignment.<br><br>
<strong>Assign an action to a group:</strong><br>
Click a group card in the Groups list. The Assign modal opens with the group selected. Choose <em>Create New</em> or <em>Assign Existing</em>, fill in the action, and save. All tags in the group switch immediately.<br><br>
<strong>Remove a tag from a group:</strong><br>
Click the tag row → use the <em>Ungroup</em> option. The tag reverts to its individual action (or none if none was set).<br><br>
<strong>Rename or delete a group:</strong><br>
Use the menu on the group card. Deleting a group ungroups all its tags — the tags themselves are not deleted.<br><br>
<strong>Individual overrides:</strong><br>
Assign a direct action to any tag in a group and only that tag is affected. Remove the individual action to return it to group control.`
  },
  // ── Schedules ─────────────────────────────────────────────────────────────
  {
    id: 'schedules-overview', group: 'Schedules', icon: `<span class='mi'>calendar_month</span>`,
    title: 'What Are Schedules?',
    content: `A <strong>Schedule</strong> automatically serves different actions based on the current time of day or day of week — no manual intervention needed. The schedule evaluates on every single scan.<br><br>
You define one or more <strong>time windows</strong> (e.g. Mon–Fri 4pm–7pm) each with a specific action, plus a <strong>fallback action</strong> for any time outside the windows. The schedule uses the timezone you choose.<br><br>
<strong>Example uses:</strong><br>
<em>Restaurant happy hour</em> — a bar menu tag serves cocktail specials between 4–7pm and the regular drinks menu outside those hours. Zero staff involvement.<br>
<em>Business hours</em> — an office door tag shows "We're open — come in" during the day and "We're closed — book a meeting" at night.<br>
<em>Event check-in</em> — a conference registration tag shows "Welcome — check in here" during event hours, then switches to a feedback form automatically after the last session ends.<br><br>
<strong>Override:</strong> You can bypass the schedule instantly from the dashboard — force any action regardless of time, then clear the override to resume normal evaluation.`
  },
  {
    id: 'howto-schedules', group: 'Schedules', icon: `<span class='mi'>settings</span>`,
    title: 'Create & Edit Schedules',
    content: `Schedules automatically serve different actions based on time of day or day of week.<br><br>
<strong>Create a new schedule:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>schedule</span> Schedules</em> tab, then tap <em>+ New Schedule</em>.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Give the schedule a name (e.g. "Happy Hour Menu").</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Set the <strong>timezone</strong> — all time windows evaluate in this timezone.</span></div>
  <div class="help-step"><span class="help-step-num">4</span><span>Add one or more <strong>time windows</strong>. For each: select days of the week, start time, end time, and the action to serve during that window.</span></div>
  <div class="help-step"><span class="help-step-num">5</span><span>Set a <strong>fallback action</strong> — served any time that doesn't match a window.</span></div>
  <div class="help-step"><span class="help-step-num">6</span><span>Click <em>Save Schedule</em>.</span></div>
</div><br>
<strong>Assign a schedule to a tag or group:</strong><br>
Click the tag or group → Assign → choose the <em>Schedule</em> tab → select the schedule → Save.<br><br>
<strong>Instant override:</strong><br>
On the schedule card, click <em>Override</em> to force a specific action regardless of the current time window. Click <em>Clear Override</em> to resume normal evaluation.<br><br>
<strong>Edit a schedule:</strong><br>
Click <em><span class='mi mi-sm'>edit</span> Edit</em> on the schedule card. Add, remove, or change time windows and actions. Changes take effect on the very next scan.<br><br>
<strong>Priority note:</strong> Schedules evaluate after Sequences and Routes. Scan limits always evaluate first.`
  },
  // ── Sequences ─────────────────────────────────────────────────────────────
  {
    id: 'sequences-overview', group: 'Sequences', icon: `<span class='mi'>format_list_numbered</span>`,
    title: 'What Are Sequences?',
    content: `A <strong>Sequence</strong> serves a different action on each consecutive scan, advancing through a list you define. Ideal for multi-step journeys, onboarding flows, or reveal experiences.<br><br>
<strong>Playback modes:</strong><br>
<em>Loop</em> — returns to step 1 after the last step, cycling indefinitely.<br>
<em>Linear</em> — stays on the last step indefinitely once it's reached.<br>
<em>One-Shot</em> — deactivates after the last step is served.<br><br>
<strong>Tracking modes:</strong><br>
<em>Per Device</em> — each device gets its own independent pointer. New scanners always start at step 1 regardless of what others have seen.<br>
<em>Global</em> — one shared pointer advances for all scanners. Everyone moves through the sequence together.<br><br>
<strong>Example uses:</strong><br>
<em>Airbnb welcome flow</em> — scan 1: welcome message. Scan 2: WiFi details. Scan 3: house manual PDF. Scan 4: local restaurant guide. Per Device mode means each new guest starts fresh.<br>
<em>Product onboarding</em> — a tag in a product box guides new owners through setup, one step per tap.<br>
<em>Collectible reveal</em> — a One-Shot sequence shows a limited offer on the first scan and a "claimed" page on any after.`
  },
  {
    id: 'howto-sequences', group: 'Sequences', icon: `<span class='mi'>settings</span>`,
    title: 'Create & Edit Sequences',
    content: `Sequences serve a different action on each consecutive scan.<br><br>
<strong>Create a new sequence:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>format_list_numbered</span> Sequences</em> tab, then tap <em>+ New Sequence</em>.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Give it a name (e.g. "Airbnb Welcome Flow").</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Choose a <strong>playback mode</strong>: Loop (cycles back to step 1), Linear (holds at last step), or One-Shot (deactivates after last step).</span></div>
  <div class="help-step"><span class="help-step-num">4</span><span>Choose a <strong>tracking mode</strong>: Per Device (each scanner gets their own position) or Global (one shared position for everyone).</span></div>
  <div class="help-step"><span class="help-step-num">5</span><span>Add steps in order — each step is an existing action from your library. Use drag handles to reorder.</span></div>
  <div class="help-step"><span class="help-step-num">6</span><span>Click <em>Save Sequence</em>.</span></div>
</div><br>
<strong>Assign to a tag or group:</strong><br>
Click the tag or group → Assign → choose the <em>Sequence</em> tab → select the sequence → Save.<br><br>
<strong>Reset a sequence:</strong><br>
Click <em>Reset</em> on the sequence card to send the Global pointer back to step 1.<br><br>
<strong>Edit a sequence:</strong><br>
Click <em><span class='mi mi-sm'>edit</span> Edit</em> to add, remove, or reorder steps or change modes. The pointer position is preserved unless you also reset.`
  },
  // ── Routes ────────────────────────────────────────────────────────────────
  {
    id: 'routes-overview', group: 'Routes', icon: `<span class='mi'>alt_route</span>`,
    title: 'What Are Routes?',
    content: `A <strong>Route</strong> serves different actions based on who is scanning — their device type, country, or operating system. Rules are evaluated top-to-bottom; the <strong>first matching rule wins</strong>.<br><br>
<strong>Condition types:</strong><br>
<em>Device Type</em> — Mobile, Tablet, Desktop<br>
<em>Country</em> — ISO 3166-1 alpha-2 codes (US, GB, FR, DE, CA, AU…)<br>
<em>OS</em> — iOS, Android, Windows, macOS, Linux<br><br>
If no rule matches, an optional <strong>Fallback Action</strong> is served. If no fallback is set, the tag falls through to its schedule or default action.<br><br>
<strong>Example uses:</strong><br>
<em>International brand</em> — French visitors see a French page, German visitors see German, everyone else gets English. One tag, three audiences.<br>
<em>Mobile vs. desktop</em> — phone users get a quick-buy page; kiosk tablet users get a full product comparison.<br>
<em>Regional pricing</em> — US visitors see USD pricing; EU visitors see EUR — without exposing either URL to the wrong market.<br><br>
<strong>Priority:</strong> Routes evaluate after Sequences but before Schedules and default actions.`
  },
  {
    id: 'howto-routes', group: 'Routes', icon: `<span class='mi'>settings</span>`,
    title: 'Create & Edit Routes',
    content: `Routes serve different actions based on who is scanning — device type, country, or OS.<br><br>
<strong>Create a new route:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>alt_route</span> Routes</em> tab, then tap <em>+ New Route</em>.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Give it a name (e.g. "International Routing").</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Add one or more <strong>rules</strong>. For each: choose a condition type (Device Type, Country, or OS), a value, and the action to serve when matched.</span></div>
  <div class="help-step"><span class="help-step-num">4</span><span>Set a <strong>fallback action</strong> — served if no rules match.</span></div>
  <div class="help-step"><span class="help-step-num">5</span><span>Order rules with drag handles. <strong>First match wins</strong> — put specific rules above broader ones.</span></div>
  <div class="help-step"><span class="help-step-num">6</span><span>Click <em>Save Route</em>.</span></div>
</div><br>
<strong>Assign to a tag or group:</strong><br>
Click the tag or group → Assign → choose the <em>Route</em> tab → select the route → Save.<br><br>
<strong>Condition values:</strong><br>
• Device: Mobile, Tablet, Desktop<br>
• Country: US, GB, FR, DE, CA, AU, and any other ISO 3166-1 alpha-2 code<br>
• OS: iOS, Android, Windows, macOS, Linux`
  },
  // ── Campaigns ─────────────────────────────────────────────────────────────
  {
    id: 'campaigns-overview', group: 'Campaigns', icon: `<span class='mi'>campaign</span>`,
    title: 'What Are Campaigns?',
    content: `A <strong>Campaign</strong> is a named container that groups tags together for a shared lead generation or engagement goal. Every tag in a campaign can be tied to a lead capture form, and all submissions are tracked together so you see the full picture without digging through individual form action inboxes.<br><br>
<strong>Campaign status:</strong><br>
<em>Draft</em> — set up but not yet running. Tags are linked but the campaign isn't actively promoted.<br>
<em>Active</em> — live and capturing leads. Activate with one click.<br>
<em>Paused</em> — stops tracking new sessions but preserves all existing data.<br><br>
<strong>Example uses:</strong><br>
<em>Trade show campaign</em> — 20 badge tags at a conference all contribute to one lead pool. See every attendee who tapped a badge in a single submission view.<br>
<em>Retail promotion</em> — 50 shelf tags during a seasonal sale. Track engagement across the entire floor, not per-tag.<br>
<em>Event lead capture</em> — session tags at a multi-track conference feed one unified lead list for follow-up.`
  },
  {
    id: 'howto-campaigns', group: 'Campaigns', icon: `<span class='mi'>settings</span>`,
    title: 'Create & Manage Campaigns',
    content: `<strong>Create a new campaign:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>campaign</span> Campaigns</em> tab, then tap <em>+ New Campaign</em>.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Give it a name, optional description, and goal (e.g. "Capture email leads at Spring Trade Show").</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Optionally select a <strong>Default Lead Capture Form</strong> from your library — this becomes the default action for tags you link to the campaign.</span></div>
  <div class="help-step"><span class="help-step-num">4</span><span>Set status to <em>Active</em> and click <em>Save Campaign</em>.</span></div>
  <div class="help-step"><span class="help-step-num">5</span><span>In the Fleet, click a tag row → Assign → Campaign tab → select this campaign. Repeat for all tags in the campaign.</span></div>
</div><br>
<strong>View submissions:</strong><br>
Click <em><span class='mi mi-sm'>inbox</span> Submissions</em> on the campaign card. This opens the submission inbox for the lead capture form action most commonly used by tags in the campaign.<br><br>
<strong>Activate / Pause:</strong><br>
Use the <em>Activate</em> or <em>Pause</em> button on the campaign card to change status without editing the campaign.<br><br>
<strong>Delete a campaign:</strong><br>
Deleting a campaign unlinks all associated tags. Submissions in the form action inbox are preserved.`
  },
  // ── Organization & Teams ──────────────────────────────────────────────────
  {
    id: 'org-overview', group: 'Organization', icon: `<span class='mi'>corporate_fare</span>`,
    title: 'What Is an Organization?',
    content: `An <strong>Organization</strong> is a team workspace that lets multiple people collaborate on a shared Smart Tag fleet. Available on the <strong>Enterprise</strong> tier.<br><br>
Every organization has:<br>
<em>One Owner</em> — the account that created the org. Has full control over all tags, members, and settings.<br>
<em>Admins</em> — can manage members, invite new people, rename the org, and reassign tags.<br>
<em>Managers</em> — can invite new members and view all tags.<br>
<em>Members</em> — see only the tags assigned directly to them by an admin.<br><br>
<strong>Key capabilities:</strong><br>
Invite team members via email with a one-time magic link. Each member gets a personal <strong>member portal</strong> — a separate lightweight dashboard showing only their assigned tags, accessible without a full account setup. Admins can reassign any tag to any org member in one click from the fleet view.`
  },
  {
    id: 'org-setup', group: 'Organization', icon: `<span class='mi'>group_add</span>`,
    title: 'Set Up Your Organization',
    content: `<strong>Create your organization:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>Click the <em><span class='mi'>corporate_fare</span> Organization</em> tab in the command center.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Enter an organization name and URL slug (e.g. "Acme Corporation" → <code>acme-corporation</code>).</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>Click <em>Create Organization</em>. You are now the owner.</span></div>
</div><br>
<strong>Invite team members:</strong><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span>In the Organization tab, enter the member's email and choose their role.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span>Click <em>Send Invite</em>. An email is sent with a magic-link that takes them directly to the member portal to accept.</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span>The invite appears under Pending Invites until accepted. Use the <em>Resend</em> button to send again or the <em>Copy Link</em> button to share manually.</span></div>
</div><br>
<strong>Assign tags to members:</strong><br>
In the Fleet, find any tag row and click the <em><span class='mi mi-sm'>group_add</span></em> icon button (visible to admins/owners when multiple members exist). Select a member from the dropdown and click <em>Assign</em>. That member will see the tag in their member portal.<br><br>
<strong>Manage roles:</strong><br>
In the Organization tab, use the role dropdown on any member row to change their role. Only admins and owners can change roles.`
  },
  {
    id: 'org-member-portal', group: 'Organization', icon: `<span class='mi'>open_in_new</span>`,
    title: 'The Member Portal',
    content: `The <strong>Member Portal</strong> (<code>/pages/charishub-org</code>) is a lightweight standalone page for team members — no Shopify account needed, no full dashboard access.<br><br>
<strong>What members see:</strong><br>
Their org name and role, a table of all tags assigned to them (tag ID copy pill, action type badge, scan count), and a Sign Out button. Org owners also get an "Open Full Dashboard" link.<br><br>
<strong>How authentication works:</strong><br>
Members receive a magic link invite. Clicking it opens the portal and lets them accept via email + magic link — one tap, no password, no account creation. On future visits, they request a new magic link from the portal sign-in page. Sessions are browser-based and persist until sign-out.<br><br>
<strong>Invite flow summary:</strong><br>
Owner invites → member receives email → clicks link → enters email → magic link emailed → clicks link → lands on portal with org already joined. All automatic.<br><br>
<strong>Admin access:</strong><br>
From the portal, org owners see a link to open the full dashboard. Members see only their assigned tags and cannot access admin functions.`
  },
  {
    id: 'org-live-feed', group: 'Organization', icon: `<span class='mi'>sensors</span>`,
    title: 'Live Activity Feed & Map',
    content: `Available in the Organization tab for admins and owners, the <strong>Live Activity Feed</strong> and <strong>Live Scan Map</strong> give your team a real-time view of everything happening across the fleet.<br><br>
<strong>Live Activity Feed:</strong><br>
A streaming log of every scan across all org tags, updated the moment a scan is registered. Each entry shows the tag name, device type, and location (city + country when available). New entries animate in at the top. The feed holds the last 50 events.<br><br>
<strong>Live Scan Map:</strong><br>
A world map (OpenStreetMap) showing the geographic location of the last 50 scans. Each scan appears as a pin — bright purple for scans in the last 10 seconds, lighter for older ones. Click any pin to see the tag name, device type, location, and time. The map loads historical data on open and receives live pins as new scans arrive.<br><br>
<strong>Connection status:</strong><br>
A dot and label in the Live Activity header shows the real-time connection state: green "Live" when connected, or red with an error message if the connection drops. Uses Supabase Realtime under the hood — no polling, no delays.`
  },
  // ── Reference ─────────────────────────────────────────────────────────────
  {
    id: 'webhooks', group: 'Reference', icon: `<span class='mi mi-sm'>webhook</span>`,
    title: 'Webhooks Reference',
    content: `Configure a webhook URL on any tag or group — CharisHub POSTs a JSON payload to that URL on every scan, with no delay to the person scanning.<br><br>
<strong>Where to configure:</strong> Click the <em>webhook icon button</em> on any tag row (fleet table) or any group card. Group webhooks fire for all tags in the group unless a tag has its own webhook set.<br><br>
<strong>Payload fields:</strong><br>
<code>tag_id</code> · <code>identifier</code> · <code>nickname</code> · <code>group_name</code> · <code>timestamp</code> · <code>country</code> · <code>city</code> · <code>device_type</code> · <code>os</code> · <code>browser</code> · <code>action_type</code> · <code>action_title</code> · <code>ab_variant</code> · <code>route_matched</code><br><br>
<strong>Use cases:</strong><br>
Connect to Zapier, Make.com, your CRM, Slack, or any HTTP endpoint. Fire to a Google Sheet to build an automatic scan log. Push to your CRM to create contacts on every business card scan. Send a Slack message when a specific tag is scanned.`
  },
  {
    id: 'scan-limits', group: 'Reference', icon: `<span class='mi mi-sm'>lock</span>`,
    title: 'Scan Limits & Expiration',
    content: `Click <strong>Limits</strong> on any tag row to configure usage guardrails.<br><br>
<strong>Scan Limit</strong> — maximum number of scans before the tag switches to the limit action. Leave blank for unlimited.<br>
<strong>Expires At</strong> — date and time after which the tag stops serving its normal action. Leave blank for no expiry.<br>
<strong>Limit Action</strong> — the action served when the cap or expiry is hit. If none is set, a generic "expired" page is shown.<br><br>
The current scan count is shown in the Limits dialog and can be reset at any time.<br><br>
<strong>Priority:</strong> Limits are evaluated before all other logic — they always take precedence.<br><br>
<strong>Use cases:</strong><br>
<em>Limited-time offer</em> — valid for the first 100 scans only; shows "offer ended" after. <em>Event pass</em> — single-use entry: first scan confirms entry, subsequent scans show "already used." <em>Campaign expiry</em> — automatically reverts to your homepage after the campaign end date.`
  },
  {
    id: 'qr-codes', group: 'Reference', icon: '⬛',
    title: 'QR Codes & Batch Download',
    content: `Click <strong>QR</strong> on any tag row to generate a QR code for that tag's live URL. Download as PNG for print use. The QR code and NFC tap both point to the same URL — both channels work simultaneously from one tag.<br><br>
<strong>Batch QR ZIP:</strong><br>
Use the <em>QR ZIP</em> buttons in the fleet toolbar to download multiple codes in one click.<br>
<em>Filtered ZIP</em> — downloads QR codes for all tags matching the current search filter.<br>
<em>Selected ZIP</em> — downloads codes for the checked tags only.<br><br>
Each PNG in the ZIP is named with the tag's identifier and nickname for easy sorting.`
  },
  {
    id: 'analytics', group: 'Reference', icon: `<span class='mi mi-sm'>bar_chart</span>`,
    title: 'Analytics',
    content: `The Analytics tab gives you a full picture of how your fleet is performing. Switch time ranges with the 7D / 30D / 90D / All buttons.<br><br>
<strong>Summary cards</strong> — total scans and active tags for the period, with % change vs the prior equivalent period.<br>
<strong>Scan trend</strong> — daily scan volume charted over the selected range.<br>
<strong>Top Tags</strong> — ranked by scan volume for the period.<br>
<strong>Device breakdown</strong> — Mobile / Tablet / Desktop split.<br>
<strong>Top Countries</strong> — where scans are originating from.<br><br>
A/B split variants are tracked separately — use this view to evaluate which variant is performing better before committing to one.`
  },
  {
    id: 'tiers-pricing', group: 'Reference', icon: `<span class='mi mi-sm'>star</span>`,
    title: 'Plans & Feature Tiers',
    content: `CharisHub is available on four plans. Every tag works on every plan — you're upgrading your capabilities, not your hardware.<br><br>
<strong>Free</strong> — up to 3 tags, basic URL redirect and vCard actions, 7-day analytics. Sequences limited to 3 steps. No groups, no schedules.<br><br>
<strong>Essential</strong> — unlimited tags, all action types, 30-day analytics, groups, schedules, sequences (unlimited steps), routes, A/B split testing, scan limits.<br><br>
<strong>Professional</strong> — everything in Essential plus lead capture forms, form submissions inbox, CSV export, 90-day analytics, advanced webhooks, and tag analytics drill-down.<br><br>
<strong>Enterprise ($49.99/mo)</strong> — everything in Professional plus: <em>Organization & Teams</em> (RBAC, member portal, magic-link invites), <em>Live Activity Feed</em> (real-time scan stream), <em>Live Scan Map</em>, <em>Campaigns</em>, <em>Shared Sequences</em>, <em>Assign-to-member tag transfers</em>, and unlimited analytics history.<br><br>
Features gated to a higher tier display a lock indicator in the UI. Click it to see what plan unlocks the feature.`
  },
  {
    id: 'priority-order', group: 'Reference', icon: `<span class='mi mi-sm'>low_priority</span>`,
    title: 'Action Priority Order',
    content: `When a tag is scanned, the system evaluates in this exact order and serves the first result it finds:<br><br>
<div class="help-steps">
  <div class="help-step"><span class="help-step-num">1</span><span><strong>Scan Limits / Expiry</strong> — if a cap or date is breached, the limit action is served immediately. Nothing else is evaluated.</span></div>
  <div class="help-step"><span class="help-step-num">2</span><span><strong>Sequence</strong> — if the tag or its group has an active sequence, the current step is served (or a random step for Random mode).</span></div>
  <div class="help-step"><span class="help-step-num">3</span><span><strong>Route</strong> — if a route is assigned, rules are evaluated top-to-bottom. First match wins.</span></div>
  <div class="help-step"><span class="help-step-num">4</span><span><strong>Schedule</strong> — if a schedule is assigned and the current time matches a window, that window's action is served.</span></div>
  <div class="help-step"><span class="help-step-num">5</span><span><strong>Direct Assignment</strong> — the tag's individual action, if set.</span></div>
  <div class="help-step"><span class="help-step-num">6</span><span><strong>Group Action</strong> — the containing group's action, if the tag is in a group.</span></div>
  <div class="help-step"><span class="help-step-num">7</span><span><strong>Fallback</strong> — a generic "not configured" page if nothing matches.</span></div>
</div><br>
Understanding this priority lets you layer features precisely — e.g. a sequence that also enforces a scan limit, or a schedule that falls back to a route.`
  },
];

function openHelpSection(id) {
  helpActiveId = id;
  // Highlight active link in nav
  document.querySelectorAll('.help-nav-link').forEach(el => {
    el.classList.toggle('active', el.dataset.id === id);
  });
  // Render section content into detail column
  const section = HELP_SECTIONS.find(s => s.id === id);
  const detail  = document.getElementById('help-detail-col');
  if (!section || !detail) return;
  detail.scrollTop = 0;
  detail.innerHTML = `
    <div class="help-detail-header">
      <span class="help-section-icon">${section.icon}</span>
      <span class="help-section-title">${section.title}</span>
    </div>
    <div class="help-section-body">${section.content}</div>
  `;
}

function renderHelpContent(query) {
  const container = document.getElementById('help-content');

  if (query) {
    // Search mode — flat stacked results, single column
    const matches = HELP_SECTIONS.filter(s =>
      s.title.toLowerCase().includes(query) ||
      s.content.toLowerCase().includes(query)
    );
    container.innerHTML = !matches.length
      ? `<div class="help-search-results"><div class="help-empty">No results for "<strong>${query}</strong>"</div></div>`
      : `<div class="help-search-results">${matches.map(s => `
          <div class="help-section">
            <div class="help-section-header">
              <span class="help-section-icon">${s.icon}</span>
              <span class="help-section-title">${s.title}</span>
            </div>
            <div class="help-section-body">${s.content}</div>
          </div>`).join('')}</div>`;
    return;
  }

  // Normal mode — two-column layout
  if (!helpActiveId || !HELP_SECTIONS.find(s => s.id === helpActiveId)) {
    helpActiveId = HELP_SECTIONS[0].id;
  }

  // Build grouped nav
  const groups = {};
  HELP_SECTIONS.forEach(s => {
    if (!groups[s.group]) groups[s.group] = [];
    groups[s.group].push(s);
  });

  const navHtml = Object.entries(groups).map(([label, items]) => `
    <div class="help-nav-group">
      <div class="help-nav-divider">${label}</div>
      ${items.map(s => `<button class="help-nav-link${s.id === helpActiveId ? ' active' : ''}" data-id="${s.id}" onclick="openHelpSection('${s.id}')">${s.icon} ${s.title}</button>`).join('')}
    </div>`).join('');

  const active = HELP_SECTIONS.find(s => s.id === helpActiveId);
  const detailHtml = active ? `
    <div class="help-detail-header">
      <span class="help-section-icon">${active.icon}</span>
      <span class="help-section-title">${active.title}</span>
    </div>
    <div class="help-section-body">${active.content}</div>` : '';

  container.innerHTML = `
    <div class="help-two-col">
      <nav class="help-nav-col" id="help-nav-col">${navHtml}</nav>
      <div class="help-detail-col" id="help-detail-col">${detailHtml}</div>
    </div>`;
}


async function testScan() {
  if (!state.fleet.length) { alert('Register at least one tag first.'); return; }
  const btn = document.getElementById('diagnostic-scan-btn'); const tag = state.fleet[0];
  btn.innerText = 'Injecting…'; btn.disabled = true;
  try {
    const { error } = await db.from('scans').insert({ porter_id: tag.porter_id, owner_id: state.profileId, device_type: 'Mobile', os_name: 'iOS', browser_name: 'Safari', city_name: 'Indianapolis', country_code: 'US' });
    if (error) { alert(`Error: ${error.message}`); btn.disabled=false; btn.innerText='Send Test Scan'; }
    else { await refreshAndRenderInsights(); btn.innerText='Success! Send Another?'; btn.disabled=false; }
  } catch(err) { console.error(err); btn.disabled=false; btn.innerText='Send Test Scan'; }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function getPreview(type, data) {
  if (!type || !data) return '—';
  try {
    switch(type) {
      case 'url':     return data.url     || '—';
      case 'phone':   return data.number  || '—';
      case 'sms':     return data.to      ? `→ ${data.to}` : '—';
      case 'email':   return data.to      ? `→ ${data.to}` : '—';
      case 'wifi':    return data.ssid    ? `Network: ${data.ssid}` : '—';
      case 'webhook': return data.url     ? `→ ${data.url}` : '—';
      case 'html':    return 'HTML content';
      case 'message': { const txt=(data.content||'').replace(/<[^>]*>/g,'').trim(); return txt?txt.substring(0,50):'—'; }
      case 'vcard':    { const name=`${data.first_name||''} ${data.last_name||''}`.trim(); return name?(data.job_title?`${name} · ${data.job_title}`:name):'Contact Card'; }
    case 'ab_split': {
      const a = state.actions.find(x=>x.id===data.action_a_id); const b = state.actions.find(x=>x.id===data.action_b_id);
      const wa = data.weight_a!=null?data.weight_a:50;
      return `${a?a.title:'A'} ${wa}% / ${b?b.title:'B'} ${100-wa}%`;
    }
      case 'form': { const fc=(data.fields||[]).length; return data.title?`${data.title} (${fc} field${fc===1?'':'s'})`:`Form (${fc} field${fc===1?'':'s'})`; }
      default: return '—';
    }
  } catch(e) { return '—'; }
}

function getGroupColor(name) {
  if (!name) return '#86868b';
  const colors=['#0071e3','#34c759','#ff9500','#af52de','#ff2d55','#5856d6','#64d2ff'];
  let hash=0; for(let i=0;i<name.length;i++) hash=name.charCodeAt(i)+((hash<<5)-hash);
  return colors[Math.abs(hash)%colors.length];
}

function showToast(msg) { const t=document.getElementById('toast'); t.innerText=msg; t.classList.add('show'); setTimeout(()=>t.classList.remove('show'),2500); }
function showError(msg) { const el=document.getElementById('v-msg'); el.innerText=msg; el.style.display='block'; }
function copyToClipboard(identifier) {
  const fullUrl = `${TAG_BASE_URL}${identifier}`;
  navigator.clipboard.writeText(fullUrl).then(() => showToast(`Copied: ${fullUrl}`));
}
function handleSort(k) { if(state.sortKey===k) state.sortAsc=!state.sortAsc; else{state.sortKey=k;state.sortAsc=true;} renderFleet(); }
function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ─────────────────────────────────────────────────────────────────────────────
// SCHEDULE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function formatDays(days) {
  if (!days || !days.length) return 'All days';
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && [1,2,3,4,5].every(x=>days.includes(x))) return 'Weekdays';
  if (days.length === 2 && [0,6].every(x=>days.includes(x))) return 'Weekends';
  const names = {0:'Sun',1:'Mon',2:'Tue',3:'Wed',4:'Thu',5:'Fri',6:'Sat'};
  return [1,2,3,4,5,6,0].filter(x=>days.includes(x)).map(x=>names[x]).join(' ');
}

function tzLabel(tz) {
  const map = {'America/New_York':'ET','America/Chicago':'CT','America/Denver':'MT','America/Los_Angeles':'PT','America/Phoenix':'AZ','America/Anchorage':'AKT','Pacific/Honolulu':'HT','UTC':'UTC','Europe/London':'GMT','Europe/Paris':'CET','Asia/Dubai':'GST','Asia/Tokyo':'JST','Australia/Sydney':'AEST'};
  return map[tz] || tz;
}

function buildTimezoneOpts(selected) {
  const list = [['America/New_York','Eastern (ET) — New York'],['America/Chicago','Central (CT) — Chicago'],['America/Denver','Mountain (MT) — Denver'],['America/Los_Angeles','Pacific (PT) — Los Angeles'],['America/Phoenix','Arizona (no DST)'],['America/Anchorage','Alaska (AKT)'],['Pacific/Honolulu','Hawaii (HT)'],['UTC','UTC'],['Europe/London','GMT/BST — London'],['Europe/Paris','CET/CEST — Paris / Berlin'],['Asia/Dubai','GST — Dubai'],['Asia/Tokyo','JST — Tokyo'],['Australia/Sydney','AEST — Sydney']];
  return list.map(([v,l])=>`<option value="${v}" ${(selected||'America/New_York')===v?'selected':''}>${l}</option>`).join('');
}

function buildActionOpts(selectedId) {
  return state.actions.map(a=>{
    const label=a.type==='vcard'?`VCARD: ${a.title}`:`${a.type.toUpperCase()}: ${a.title}`;
    return `<option value="${a.id}" ${selectedId===a.id?'selected':''}>${label}</option>`;
  }).join('');
}

function addScheduleWindow(container, prefill={}) {
  if (!container) return;
  const row=document.createElement('div'); row.className='window-row';
  row.innerHTML=`
    <div style="flex:1;"><label class="field-label">Start Time</label><input type="time" class="charis-input win-start" value="${prefill.start_time||''}"></div>
    <div style="flex:1;"><label class="field-label">End Time</label><input type="time" class="charis-input win-end" value="${prefill.end_time||''}"></div>
    <div style="flex:1.5;"><label class="field-label">Label (optional)</label><input type="text" class="charis-input win-label" placeholder="e.g. Lunch…" value="${prefill.label||''}"></div>
    <button type="button" class="remove-entry" onclick="this.parentElement.remove()" style="margin-top:22px;"><span class='mi mi-sm'>close</span></button>`;
  container.appendChild(row);
}

async function toggleOverride(schedId, currentState) {
  try {
    await db.rpc('toggle_schedule_override', { p_schedule_id: schedId, p_shopify_user_id: SHOPIFY_GID, p_active: !currentState });
    loadData(); showToast(currentState ? 'Override deactivated' : 'Override activated');
  } catch(err) { showToast(`Failed: ${err.message}`); }
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDER: GROUPS
// ─────────────────────────────────────────────────────────────────────────────
function toggleGroupFilter(groupName) {
  if (state.groupFilters.includes(groupName)) state.groupFilters=state.groupFilters.filter(n=>n!==groupName);
  else state.groupFilters.push(groupName);
  renderGroups(); renderFleet();
}

function handleGroupClick(groupId, groupName) {
  if (state.selected.length > 0) moveTagsToGroup(groupId);
  else toggleGroupFilter(groupName);
}

async function moveTagsToGroup(groupId) {
  if (!state.selected.length) return;
  const count = state.selected.length;
  try {
    await db.rpc('batch_move_to_group', { porter_ids: state.selected, target_group_id: groupId });
    showToast(`${count} tag${count!==1?'s':''} moved ✓`);
    const card=document.getElementById(`group-card-${groupId}`);
    if (card) { card.classList.add('flash-success'); setTimeout(()=>card.classList.remove('flash-success'),900); }
    clearSelection(); loadData();
  } catch(err) { showToast(`Move failed: ${err.message}`); }
}

function renderGroups() {
  const container=document.getElementById('groups-list'); const batchActive=state.selected.length>0;
  if (!state.groups.length) { container.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>folder</span></div><div class="empty-state-title">No groups yet</div><div class="empty-state-sub">Create a group to assign one action to many tags at once.</div></div>`; return; }
  let html='';
  state.groups.forEach(g=>{
    const color=getGroupColor(g.name); const isFilter=!batchActive&&state.groupFilters.includes(g.name);
    const groupPorters=state.fleet.filter(p=>p.group_name===g.name);
    const tagCount=groupPorters.length;
    const scanCount=state.scans.filter(s=>groupPorters.map(p=>p.porter_id).includes(s.porter_id)).length;
    const action=g.actions;
    // Action type badge matching fleet table style
    const actType=action?action.type:null;
    const isVcard=actType==='vcard'; const isAb=actType==='ab_split'; const isForm=actType==='form';
    const typeColor=isVcard?'#4338ca':isAb?'#d97706':isForm?'#059669':'var(--charis-accent)';
    const typeLabel=actType?(isVcard?'VCARD':isAb?'A/B SPLIT':isForm?'FORM':actType.toUpperCase()):'';
    const typeBadge=typeLabel?`<span class="status-badge" style="background:${typeColor}15;color:${typeColor};font-size:9px;">${typeLabel}</span>`:'';
    html+=`<div id="group-card-${g.id}" class="grid-item ${isFilter?'selected':''} ${batchActive?'drop-target':''}" style="border-top-color:${color}" onclick="handleGroupClick('${g.id}','${g.name.replace(/'/g,"\\'")}')">
      <div class="selection-dot">✓</div>
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;gap:6px;"><div style="font-weight:800;font-size:15px;">${g.name}</div><span class="scan-pill" title="${tagCount} tag${tagCount!==1?'s':''} in this group">${tagCount} <span class='mi'>label</span></span></div>
        <div style="font-size:11px;color:var(--charis-secondary);margin-top:2px;"><span class='mi mi-sm'>bar_chart</span> ${scanCount} scan${scanCount!==1?'s':''}</div>
        <div style="margin-top:4px;">${typeBadge}<span style="font-size:13px;font-weight:600;">${action?action.title:'No Action'}</span></div>
        <div class="data-preview">${action?getPreview(action.type,action.primary_data):''}</div>
      </div>
      ${!batchActive?`<div style="display:flex;gap:5px;flex-shrink:0;flex-direction:column;"><div style="display:flex;gap:5px;"><button class="btn-charis" onclick="event.stopPropagation();openAssign('group','${g.id}')">Assign</button><button class="btn-charis btn-danger-outline" onclick="event.stopPropagation();confirmDeleteGroup('${g.id}','${g.name.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>close</span></button></div><button class="icon-btn${g.scan_webhook_url?' ib-active':''}" title="${g.scan_webhook_url?'Webhook active — click to edit':'Set up Webhook'}" onclick="event.stopPropagation();openWebhook('group','${g.id}','${(g.scan_webhook_url||'').replace(/'/g,"\\'")}','${g.name.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>webhook</span>${g.scan_webhook_url?'<span class="icon-dot" style="background:#0ea5e9;"></span>':''}</button></div>`:''}
    </div>`;
  });
  container.innerHTML=html;
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDER: LIBRARY
// ─────────────────────────────────────────────────────────────────────────────
function toggleLibraryFilter(id) {
  if (state.libraryFilters.includes(id)) state.libraryFilters=state.libraryFilters.filter(k=>k!==id);
  else state.libraryFilters.push(id);
  renderLibrary(); renderFleet();
}

function clearAllFilters() {
  state.groupFilters=[]; state.libraryFilters=[];
  renderGroups(); renderLibrary(); renderFleet();
}

function renderFilterBar(filteredCount, totalCount) {
  const bar=document.getElementById('fleet-filter-bar');
  if (!bar) return;
  const hasGroup=state.groupFilters.length>0;
  const hasLib=state.libraryFilters.length>0;
  const hasSearch=!!state.searchTerm;
  const isFiltered=hasGroup||hasLib||filteredCount!==totalCount;
  if (!isFiltered) { bar.innerHTML=''; bar.style.display='none'; return; }
  bar.style.display='flex';

  const groupChips=state.groupFilters.map(name=>{
    const color=getGroupColor(name);
    return `<span class="filter-chip" style="background:${color}18;color:${color};border-color:${color}40;"><span class='mi'>folder</span> ${name}<button class="filter-chip-x" onclick="toggleGroupFilter('${name.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>close</span></button></span>`;
  }).join('');

  const libChips=state.libraryFilters.map(id=>{
    const a=state.actions.find(x=>x.id===id); if(!a) return '';
    const isVcard=a.type==='vcard'; const isAb=a.type==='ab_split'; const isForm=a.type==='form';
    const color=isVcard?'#4338ca':isAb?'#d97706':isForm?'#059669':'var(--charis-accent)';
    return `<span class="filter-chip" style="background:${color}18;color:${color};border-color:${color}40;"><span class='mi'>bolt</span> ${a.title}<button class="filter-chip-x" onclick="toggleLibraryFilter('${id}')"><span class='mi mi-sm'>close</span></button></span>`;
  }).join('');

  const clearBtn=(hasGroup||hasLib)?`<button class="filter-chip-clear" onclick="clearAllFilters()">Clear filters</button>`:'';
  const countLabel=filteredCount===totalCount
    ?`${totalCount} tag${totalCount!==1?'s':''}`
    :`<strong>${filteredCount}</strong> of ${totalCount} tag${totalCount!==1?'s':''}`;

  bar.innerHTML=`<span class="filter-bar-count">${countLabel}</span>${groupChips}${libChips}${clearBtn}`;
}

function renderLibrary() {
  const container=document.getElementById('library-list');
  if (!state.actions.length) { container.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>bolt</span></div><div class="empty-state-title">Library is empty</div><div class="empty-state-sub">Add your first action — URL, contact card, WiFi, and more.</div></div>`; return; }
  let html='';
  state.actions.forEach(a=>{
    const isSelected=state.libraryFilters.includes(a.id); const isVcard=a.type==='vcard'; const isAb=a.type==='ab_split'; const isForm=a.type==='form';
    const typeColor=isVcard?'#4338ca':isAb?'#d97706':isForm?'#059669':'var(--charis-accent)';
    const typeLabel=isVcard?'CONTACT CARD':isAb?'A/B SPLIT':isForm?'FORM':a.type.toUpperCase();
    const subCount=state.submissionCounts?.[a.id]||0;
    const subBtn=isForm?`<button class="btn-charis" style="${subCount?'border-color:#059669;color:#059669;':''}" onclick="event.stopPropagation();openSubmissions('${a.id}','${a.title.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>inbox</span> ${subCount}</button>`:'';
    html+=`<div class="grid-item ${isSelected?'selected':''}" style="border-top-color:#eee" onclick="toggleLibraryFilter('${a.id}')">
      <div class="selection-dot">✓</div>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:800;font-size:14px;">${a.title}</div>
        <div style="font-size:11px;color:${typeColor};font-weight:700;">${typeLabel}</div>
        <div class="data-preview">${getPreview(a.type,a.primary_data)}</div>
      </div>
      <div style="display:flex;gap:5px;flex-shrink:0;flex-wrap:wrap;justify-content:flex-end;">${subBtn}<button class="btn-charis" onclick="event.stopPropagation();openEdit('${a.id}')">Edit</button><button class="btn-charis btn-danger-outline" onclick="event.stopPropagation();confirmDeleteAction('${a.id}','${a.title.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>close</span></button></div>
    </div>`;
  });
  container.innerHTML=html;
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDER: SCHEDULES
// ─────────────────────────────────────────────────────────────────────────────
function renderSchedules() {
  const container=document.getElementById('schedules-list');
  if (!state.schedules.length) { container.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>schedule</span></div><div class="empty-state-title">No schedules yet</div><div class="empty-state-sub">Schedules serve different content during specific time windows, with fallback and override actions.</div></div>`; return; }
  let html='';
  state.schedules.forEach(s=>{
    const windows=s.schedule_windows||[];
    const winSummary=windows.map(w=>`${(w.start_time||'').substring(0,5)}–${(w.end_time||'').substring(0,5)}${w.label?` (${w.label})`:''}`).join(' · ')||'No windows set';
    const dayStr=formatDays(s.day_of_week); const tz=tzLabel(s.timezone||'UTC'); const isOverride=!!s.override_active;
    const dateRange=(s.start_date||s.end_date)?`<div style="font-size:11px;color:var(--charis-secondary);margin-top:3px;"><span class='mi mi-sm'>event</span> ${s.start_date||'…'} → ${s.end_date||'…'}</div>`:'';
    html+=`<div class="sched-card">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div style="display:flex;align-items:center;gap:8px;flex:1;min-width:0;">
          <div style="font-weight:800;font-size:15px;">${s.name}</div>
          ${isOverride?'<span class="override-badge">OVERRIDE ON</span>':''}
        </div>
        <div style="display:flex;gap:5px;flex-shrink:0;">
          <button class="btn-charis" onclick="openEditSchedule('${s.id}')">Edit</button>
          <button class="btn-charis btn-danger-outline" onclick="confirmDeleteSchedule('${s.id}','${s.name.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>close</span></button>
        </div>
      </div>
      <div style="font-size:11px;color:var(--charis-secondary);margin-top:6px;">${tz} · ${dayStr}</div>
      <div style="font-size:11px;color:var(--charis-secondary);margin-top:2px;">⏱ ${winSummary}</div>
      ${dateRange}
      ${s.actions?`<div style="font-size:11px;margin-top:6px;"><span style="color:#bbb;font-weight:700;">SCHEDULED</span> ${s.actions.title}</div>`:''}
      ${s.fallback_action?`<div style="font-size:11px;margin-top:2px;"><span style="color:#bbb;font-weight:700;">FALLBACK</span> ${s.fallback_action.title}</div>`:''}
      ${s.override_action?`<div class="override-row ${isOverride?'':'override-row-off'}" onclick="event.stopPropagation()">
          <span style="font-size:11px;font-weight:700;color:var(--charis-warning);"><span class='mi'>bolt</span> ${s.override_action.title}</span>
          <button class="override-toggle ${isOverride?'on':'off'}" onclick="toggleOverride('${s.id}',${isOverride})">${isOverride?'Turn Off':'Activate'}</button>
        </div>`:''}
    </div>`;
  });
  container.innerHTML=html;
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDER: SEQUENCES  (v10.3 new)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// CAMPAIGNS  (v4.2.0)
// ─────────────────────────────────────────────────────────────────────────────
function renderCampaigns() {
  const container = document.getElementById('campaigns-list');
  if (!container) return;
  if (!state.campaigns.length) {
    container.innerHTML = `<div class="empty-state">
      <div class="empty-state-icon"><span class="mi">campaign</span></div>
      <div class="empty-state-title">No campaigns yet</div>
      <div class="empty-state-sub">Campaigns group tags into trackable lead generation flows. Create one to get started.</div>
    </div>`;
    return;
  }
  let html = '';
  state.campaigns.forEach(c => {
    const tagCount   = state.fleet.filter(f => f.campaign_id === c.id).length;
    const submCount  = state.campaigns._submCounts?.[c.id] || 0;
    const statusColor = c.status === 'active' ? '#22c55e' : c.status === 'paused' ? '#f59e0b' : '#aaa';
    const statusLabel = c.status === 'active' ? 'Active' : c.status === 'paused' ? 'Paused' : 'Draft';
    html += `<div class="fleet-card campaign-card" style="padding:14px 18px;margin-bottom:8px;">
      <div style="display:flex;align-items:flex-start;gap:12px;">
        <span class="mi" style="font-size:26px;color:#6366f1;margin-top:2px;">campaign</span>
        <div style="flex:1;min-width:0;">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:4px;">
            <div style="font-weight:800;font-size:15px;">${escHtmlSimple(c.name)}</div>
            <span class="status-badge" style="background:${statusColor}18;color:${statusColor};font-size:10px;font-weight:700;padding:2px 7px;border-radius:20px;">${statusLabel}</span>
          </div>
          ${c.description ? `<div style="font-size:12px;color:#888;margin-bottom:6px;line-height:1.4;">${escHtmlSimple(c.description)}</div>` : ''}
          <div style="display:flex;gap:16px;flex-wrap:wrap;">
            <span style="font-size:11px;color:#888;"><span class="mi mi-sm" style="font-size:13px;vertical-align:-2px;">label</span> ${tagCount} tag${tagCount!==1?'s':''}</span>
            ${c.goal ? `<span style="font-size:11px;color:#888;"><span class="mi mi-sm" style="font-size:13px;vertical-align:-2px;">flag</span> ${escHtmlSimple(c.goal)}</span>` : ''}
            <span style="font-size:11px;color:#888;"><span class="mi mi-sm" style="font-size:13px;vertical-align:-2px;">today</span> ${new Date(c.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'})}</span>
          </div>
        </div>
        <div style="display:flex;gap:5px;flex-shrink:0;flex-direction:column;align-items:flex-end;">
          <div style="display:flex;gap:5px;">
            <button class="btn-charis btn-small" onclick="openCampaignSubmissions('${c.id}','${escHtmlSimple(c.name).replace(/'/g,"\\'")}')"><span class="mi mi-sm">inbox</span> Submissions</button>
            <button class="btn-charis" onclick="openEditCampaign('${c.id}')"><span class="mi mi-sm">edit</span></button>
            <button class="btn-charis btn-danger-outline" onclick="confirmDeleteCampaign('${c.id}','${escHtmlSimple(c.name).replace(/'/g,"\\'")}')"><span class="mi mi-sm">close</span></button>
          </div>
          <div style="display:flex;gap:5px;margin-top:4px;">
            ${c.status !== 'active'  ? `<button class="btn-charis btn-small btn-primary" onclick="setCampaignStatus('${c.id}','active')"><span class="mi mi-sm">play_arrow</span> Activate</button>` : ''}
            ${c.status === 'active'  ? `<button class="btn-charis btn-small" onclick="setCampaignStatus('${c.id}','paused')"><span class="mi mi-sm">pause</span> Pause</button>` : ''}
          </div>
        </div>
      </div>
    </div>`;
  });
  container.innerHTML = html;
}

function openNewCampaign() {
  state.editTarget = null;
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText    = 'Create Campaign';
  document.getElementById('modal-subtitle').innerText = 'Group tags into a lead generation or engagement campaign.';
  renderCampaignForm({});
  document.getElementById('modal-confirm-btn').onclick = saveCampaign;
  document.getElementById('charis-modal').classList.add('active');
}

function openEditCampaign(campId) {
  const c = state.campaigns.find(x => x.id === campId); if (!c) return;
  state.editTarget = { id: campId, type: 'campaign' };
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText    = 'Edit Campaign';
  document.getElementById('modal-subtitle').innerText = '';
  renderCampaignForm(c);
  document.getElementById('modal-confirm-btn').onclick = saveCampaign;
  document.getElementById('charis-modal').classList.add('active');
}

function renderCampaignForm(c) {
  document.getElementById('modal-fields').innerHTML = `
    <label class="field-label">Campaign Name</label>
    <input type="text" id="camp-name" class="charis-input" placeholder="e.g. Spring Trade Show 2026" value="${escHtmlSimple(c.name||'')}">

    <label class="field-label">Description <span style="font-weight:400;color:#aaa;">(optional)</span></label>
    <input type="text" id="camp-desc" class="charis-input" placeholder="What is this campaign for?" value="${escHtmlSimple(c.description||'')}">

    <label class="field-label">Goal / Objective <span style="font-weight:400;color:#aaa;">(optional)</span></label>
    <input type="text" id="camp-goal" class="charis-input" placeholder="e.g. Capture email leads at trade show" value="${escHtmlSimple(c.goal||'')}">

    <label class="field-label">Status</label>
    <select id="camp-status" class="charis-input">
      <option value="draft"  ${(c.status||'draft')==='draft'?'selected':''}>Draft</option>
      <option value="active" ${c.status==='active'?'selected':''}>Active</option>
      <option value="paused" ${c.status==='paused'?'selected':''}>Paused</option>
    </select>

    <label class="field-label" style="margin-top:10px;">Default Lead Capture Form <span style="font-weight:400;color:#aaa;">(optional)</span></label>
    <select id="camp-form-action" class="charis-input">
      <option value="">— None —</option>
      ${state.actions.filter(a=>a.type==='form').map(a=>`<option value="${a.id}" ${c.default_form_action_id===a.id?'selected':''}>${escHtmlSimple(a.title)}</option>`).join('')}
    </select>
    <p style="font-size:11px;color:#888;margin-top:4px;line-height:1.5;">Tags in this campaign will use this form as their active action by default. You can override it per-tag from the Fleet.</p>
  `;
}

async function saveCampaign() {
  const name        = document.getElementById('camp-name')?.value?.trim();
  const description = document.getElementById('camp-desc')?.value?.trim() || null;
  const goal        = document.getElementById('camp-goal')?.value?.trim() || null;
  const status      = document.getElementById('camp-status')?.value || 'draft';
  const formActionId = document.getElementById('camp-form-action')?.value || null;
  document.getElementById('v-msg').style.display = 'none';
  if (!name) { showError('Campaign name is required.'); return; }
  const btn = document.getElementById('modal-confirm-btn');
  btn.innerText = 'Saving…'; btn.disabled = true;
  try {
    const payload = { name, description, goal, status,
      default_form_action_id: formActionId || null,
      owner_id: state.profileId };
    if (state.editTarget?.type === 'campaign') {
      const { error } = await db.from('campaigns').update(payload).eq('id', state.editTarget.id);
      if (error) throw error;
    } else {
      const { error } = await db.from('campaigns').insert({ ...payload, created_at: new Date().toISOString() });
      if (error) throw error;
    }
    closeModal(); loadData();
  } catch(err) { showError(`Save failed: ${err.message}`); btn.disabled = false; btn.innerText = 'Save Campaign'; }
}

async function setCampaignStatus(campId, newStatus) {
  try {
    const { error } = await db.from('campaigns').update({ status: newStatus }).eq('id', campId);
    if (error) throw error;
    loadData(); showToast(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'} ✓`);
  } catch(err) { showToast(`Failed: ${err.message}`); }
}

function confirmDeleteCampaign(campId, name) {
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText    = 'Delete Campaign?';
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('modal-fields').innerHTML   = `
    <div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${escHtmlSimple(name)}</div>
    <p style="color:var(--charis-danger);font-size:12px;margin-top:10px;">Tags in this campaign will be unlinked. Submissions are preserved in the form action's inbox.</p>`;
  const btn = document.getElementById('modal-confirm-btn');
  btn.className = 'btn-charis btn-danger'; btn.innerText = 'Delete Campaign';
  btn.onclick = async () => {
    btn.innerText = 'Deleting…'; btn.disabled = true;
    try {
      // Unlink any tags first
      await db.from('porters').update({ campaign_id: null }).eq('campaign_id', campId);
      const { error } = await db.from('campaigns').delete().eq('id', campId);
      if (error) throw error;
      closeModal(); loadData();
    } catch(err) { showError(`Delete failed: ${err.message}`); btn.disabled = false; btn.innerText = 'Delete Campaign'; }
  };
  document.getElementById('charis-modal').classList.add('active');
}

async function openCampaignSubmissions(campId, campName) {
  // Collect all form action IDs used by tags in this campaign
  const campTags = state.fleet.filter(f => f.campaign_id === campId);
  const actionIds = [...new Set(campTags.map(f => f.active_action_id).filter(Boolean))];
  if (!actionIds.length) {
    showToast('No tags with form actions are in this campaign yet.'); return;
  }
  // Open submissions for the first matching form action; if multiple, show combined
  // For now open the form action that appears most on campaign tags
  const formAction = state.actions.find(a => a.type === 'form' && actionIds.includes(a.id));
  if (formAction) {
    openSubmissions(formAction.id, `${campName} — ${formAction.title}`);
  } else {
    showToast('No lead capture form action found on campaign tags.');
  }
}

// Simple HTML escape helper (inline, for use in campaign/org render functions)
function escHtmlSimple(str) {
  return String(str||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function renderSequences() {
  const container=document.getElementById('sequences-list');
  if (!state.sequences.length) {
    container.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>format_list_numbered</span></div><div class="empty-state-title">No sequences yet</div><div class="empty-state-sub">Sequences cycle through actions across consecutive scans — loop, linear, or one-shot.</div></div>`;
    return;
  }
  const modeLabel     = { loop:"<span class='mi mi-sm'>loop</span> Loop", linear:"<span class='mi mi-sm'>arrow_forward</span> Linear", 'one-shot':"<span class='mi mi-sm'>looks_one</span> One-Shot", random:"<span class='mi mi-sm'>shuffle</span> Random" };
  const trackingLabel = { global:"<span class='mi mi-sm'>language</span> Global", per_device:"<span class='mi mi-sm'>smartphone</span> Per Device" };
  let html='';
  state.sequences.forEach(seq=>{
    const count=seq.action_ids?.length||0; const cur=seq.current_index||0;
    const tracking=seq.tracking||'global';
    const isRandom = seq.mode === 'random';
    const steps=(seq.action_ids||[]).map((id,i)=>{
      const action=state.actions.find(a=>a.id===id);
      const label=action?`${action.type.toUpperCase()}: ${action.title}`:'Unknown action';
      const isCur=!isRandom&&tracking==='global'&&i===cur&&seq.active;
      return `<div class="seq-step-item ${isCur?'current':''}">${isCur?'▶ ':'   '}${i+1}. ${label}</div>`;
    }).join('');
    const positionNote = isRandom
      ? `${count} step${count!==1?'s':''} · Random selection each scan`
      : tracking==='per_device'
        ? `${count} step${count!==1?'s':''} · Per-device progress`
        : `${count} step${count!==1?'s':''} · Position ${cur+1} of ${count}`;
    html+=`<div class="seq-card">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div style="flex:1;min-width:0;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:3px;flex-wrap:wrap;">
            <div style="font-weight:800;font-size:15px;">${seq.name}</div>
            <span class="seq-mode-badge">${modeLabel[seq.mode]||seq.mode}</span>
            <span class="seq-tracking-badge ${tracking==='per_device'?'per-device':''}">${trackingLabel[tracking]||tracking}</span>
          </div>
          <div style="font-size:11px;color:var(--charis-secondary);">${positionNote}</div>
          ${!seq.active&&tracking==='global'?'<div class="seq-expired-badge">COMPLETED</div>':''}
        </div>
        <div style="display:flex;gap:5px;flex-shrink:0;">
          <button class="btn-charis btn-small" onclick="resetSequence('${seq.id}')">Reset</button>
          <button class="btn-charis" onclick="openEditSequence('${seq.id}')">Edit</button>
          <button class="btn-charis btn-danger-outline" onclick="confirmDeleteSequence('${seq.id}','${seq.name.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>close</span></button>
        </div>
      </div>
      ${steps?`<div class="seq-step-list">${steps}</div>`:''}
    </div>`;
  });
  container.innerHTML=html;
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDER: FLEET
// ─────────────────────────────────────────────────────────────────────────────
function renderFleet() {
  const container=document.getElementById('fleet-container');
  let filtered=state.fleet.filter(item=>{
    const matchSearch=(item.nickname||'').toLowerCase().includes(state.searchTerm.toLowerCase())||(item.group_name||'').toLowerCase().includes(state.searchTerm.toLowerCase())||(item.identifier||'').toLowerCase().includes(state.searchTerm.toLowerCase());
    const matchGroups=state.groupFilters.length===0||state.groupFilters.includes(item.group_name);
    const matchLibrary=state.libraryFilters.length===0||state.libraryFilters.includes(item.active_action_id);
    return matchSearch&&matchGroups&&matchLibrary;
  });
  // Augment each item with a numeric scan count for sorting
  filtered=filtered.map(item=>({...item,_scanSort:state.scanCounts[item.porter_id]||0}));
  if (state.sortKey) filtered.sort((a,b)=>{
    if(state.sortKey==='_scanSort'){return state.sortAsc?a._scanSort-b._scanSort:b._scanSort-a._scanSort;}
    const vA=(a[state.sortKey]||'').toString().toLowerCase(); const vB=(b[state.sortKey]||'').toString().toLowerCase(); return state.sortAsc?vA.localeCompare(vB):vB.localeCompare(vA);
  });

  renderFilterBar(filtered.length, state.fleet.length);

  if (!state.fleet.length) { container.innerHTML=`<div class="fleet-card"><div class="empty-state" style="padding:50px;"><div class="empty-state-icon"><span class='mi'>label</span></div><div class="empty-state-title">No tags registered</div><div class="empty-state-sub">Scan or tap a Smart Tag for the first time to register it here.</div></div></div>`; return; }
  if (!filtered.length) { container.innerHTML=`<div class="fleet-card"><div class="empty-state" style="padding:40px;"><div class="empty-state-icon"><span class='mi'>search</span></div><div class="empty-state-title">No tags match</div><div class="empty-state-sub">Try adjusting or clearing the active filters above.</div></div></div>`; return; }

  const srt=(k)=>state.sortKey===k?(state.sortAsc?' ↑':' ↓'):' ↕';
  let html=`<div class="fleet-card"><table><thead><tr><th style="width:40px"><input type="checkbox" id="master-cb"></th><th style="width:100px;cursor:pointer;" onclick="handleSort('identifier')">ID${srt('identifier')}</th><th style="width:140px;cursor:pointer;" onclick="handleSort('nickname')">Nickname${srt('nickname')}</th><th style="width:110px;cursor:pointer;" onclick="handleSort('group_name')">Group${srt('group_name')}</th><th style="width:55px;text-align:center;cursor:pointer;" onclick="handleSort('_scanSort')">Scans${srt('_scanSort')}</th><th style="width:220px;cursor:pointer;" onclick="handleSort('active_action_type')">Active Action${srt('active_action_type')}</th><th>Controls</th></tr></thead><tbody>`;
  filtered.forEach(item=>{
    const count=state.scanCounts[item.porter_id]||0;
    const recent=state.scans.find(s=>s.porter_id===item.porter_id);
    const isRecent=recent&&(new Date()-new Date(recent.scanned_at||recent.created_at)<86400000);
    const lastScanAt=recent?(recent.scanned_at||recent.created_at):null;
    const lastScanLabel=lastScanAt?timeAgo(lastScanAt):(count>0?null:'never');
    // v3.0.0: check for active shared sequence subscription (takes highest priority)
    const sharedSub  = state.sharedSubscriptions.find(s => s.porter_id === item.porter_id);
    const isShared   = !!sharedSub;
    const isSeq      = !isShared && item.use_sequence;
    const isSchedule = !isShared && !isSeq && item.use_schedule;
    const isRoute    = !isShared && !isSeq && !isSchedule && item.effective_route_id;
    const isVcard    = item.active_action_type==='vcard';
    const isAb       = item.active_action_type==='ab_split';
    const isForm     = item.active_action_type==='form';
    const typeColor  = isShared?'#9333ea':isSeq?'var(--charis-seq)':isSchedule?'#0284c7':isRoute?'#7c3aed':isVcard?'#4338ca':isAb?'#d97706':isForm?'#059669':'var(--charis-accent)';
    const typeLabel  = isShared?'SHARED':isSeq?'SEQUENCE':isSchedule?'SCHEDULE':isRoute?'ROUTE':item.active_action_type?(isVcard?'VCARD':isAb?'A/B SPLIT':isForm?'FORM':item.active_action_type.toUpperCase()):'';
    const hasLimit   = item.scan_limit!=null||item.expires_at;
    const preview    = isShared
      ? (() => { const ps = sharedSub.seq; return ps ? `${ps.name} · ${ADVANCE_LABEL[ps.advance_on]||ps.advance_on}` : 'Shared Sequence'; })()
      : isSeq
        ? (() => { const seq=state.sequences.find(s=>s.id===(item.porter_sequence_id||item.group_sequence_id)); return seq?`${seq.name} · Step ${(seq.current_index||0)+1}`:'Sequence'; })()
        : isSchedule
          ? (() => { const schedId=item.porter_schedule_id||item.group_schedule_id; const sched=state.schedules.find(s=>s.id===schedId); return sched?sched.name:'Schedule'; })()
          : isRoute
            ? (() => { const rte=state.routes.find(r=>r.id===item.effective_route_id); return rte?`${rte.name} · ${rte.rules?.length||0} rules`:'Route'; })()
            : getPreview(item.active_action_type,item.primary_data);
    const gTdClick=item.group_name?`event.stopPropagation();toggleGroupFilter('${item.group_name.replace(/'/g,"\\'")}')`:' event.stopPropagation()';
    const qrIcon=`<svg width='14' height='14' viewBox='0 0 14 14' fill='currentColor' style='display:block;' xmlns='http://www.w3.org/2000/svg'><rect x='1' y='1' width='5' height='5' rx='0.5'/><rect x='2' y='2' width='3' height='3' fill='white'/><rect x='3' y='3' width='1' height='1'/><rect x='8' y='1' width='5' height='5' rx='0.5'/><rect x='9' y='2' width='3' height='3' fill='white'/><rect x='10' y='3' width='1' height='1'/><rect x='1' y='8' width='5' height='5' rx='0.5'/><rect x='2' y='9' width='3' height='3' fill='white'/><rect x='3' y='10' width='1' height='1'/><rect x='8' y='8' width='2' height='2'/><rect x='11' y='8' width='2' height='2'/><rect x='8' y='11' width='5' height='2'/><rect x='10' y='10' width='1' height='1'/></svg>`;
    html+=`<tr style="cursor:pointer;" onclick="togglePorterCheck('${item.porter_id}')">
      <td onclick="event.stopPropagation()"><input type="checkbox" class="porter-cb" value="${item.porter_id}"></td>
      <td style="font-family:monospace;font-size:11px;letter-spacing:1px;" onclick="event.stopPropagation();copyToClipboard('${item.identifier}')"><span style="cursor:pointer;">${item.identifier}</span></td>
      <td style="font-weight:700;">${isRecent?'<span class="pulse-dot"></span>':''}${item.nickname||'—'}<br>
        ${item.campaign_id ? `<span class="status-badge badge-type" style="background:#0ea5e915;color:#0ea5e9;border:0;padding:2px 6px;margin-top:2px;"><span class="mi mi-sm" style="font-size:11px;">campaign</span> ${item.campaign_name || 'Campaign'}</span>` : ''}
        ${item.customer_label ? `<span class="status-badge" style="background:#f0fdf415;color:#16a34a;border:0;padding:2px 6px;margin-top:2px;"><span class="mi mi-sm" style="font-size:11px;">person</span> ${escHtmlSimple(item.customer_label)}</span>` : ''}
      </td>
      <td style="cursor:${item.group_name?'pointer':'default'};" onclick="${gTdClick}"><span class="status-badge${item.group_name?'':' badge-individual'}" style="${item.group_name?`background:${getGroupColor(item.group_name)}15;color:${getGroupColor(item.group_name)};`:'background:#f5f5f7;color:#555;'}">${item.group_name||'Individual'}</span></td>
      <td style="font-weight:800;font-size:12px;text-align:center;cursor:pointer;" onclick="event.stopPropagation();openTagAnalytics('${item.porter_id}','${(item.nickname||item.identifier).replace(/'/g,"\\'")}','${item.identifier}')">${count}${hasLimit&&item.scan_limit!=null?`<span style="font-size:10px;color:#ff9500;font-weight:700;display:block;">${item.scan_count||0}/${item.scan_limit}</span>`:''}${hasLimit&&item.expires_at?`<span style="font-size:10px;color:#ff9500;display:block;">⏰</span>`:''}${lastScanLabel?`<span style="font-size:10px;color:#aeaeb2;font-weight:500;display:block;">${lastScanLabel}</span>`:''}</td>
      <td>
        <div>${typeLabel?`<span class="status-badge" style="background:${typeColor}15;color:${typeColor};">${typeLabel}</span>`:''}<span style="font-size:13px;font-weight:600;">${item.active_action_title||'—'}</span></div>
        <div class="data-preview">${preview}</div>
      </td>
      <td onclick="event.stopPropagation()">
        <div style="display:flex;gap:4px;align-items:center;justify-content:flex-end;flex-wrap:nowrap;">
          <button class="btn-charis btn-primary btn-small" onclick="openAssign('porter','${item.porter_id}')">Assign</button>
          <button class="icon-btn" title="Download QR Code" onclick="openQR('${item.identifier}','${(item.nickname||'').replace(/'/g,"\\'")}')"> ${qrIcon}</button>
          <button class="icon-btn" title="Preview Scan" onclick="window.open('${TAG_BASE_URL}${item.identifier}','_blank')"><span class='mi mi-sm'>open_in_new</span></button>
          <button class="icon-btn${item.scan_webhook_url?' ib-active':''}" title="${item.scan_webhook_url?'Webhook active — click to edit':'Set up Webhook'}" onclick="openWebhook('porter','${item.porter_id}','${(item.scan_webhook_url||'').replace(/'/g,"\\'")}','${(item.nickname||item.identifier).replace(/'/g,"\\'")}')"><span class='mi mi-sm'>webhook</span>${item.scan_webhook_url?'<span class="icon-dot" style="background:#0ea5e9;"></span>':''}</button>
          <button class="icon-btn${(item.scan_limit!=null||item.expires_at)?' ib-limit':''}" title="${(item.scan_limit!=null||item.expires_at)?'Scan limits active — click to edit':'Set Scan Limits'}" onclick="openScanLimits('${item.porter_id}','${(item.nickname||item.identifier).replace(/'/g,"\\'")}')"><span class='mi mi-sm'>lock</span>${(item.scan_limit!=null||item.expires_at)?'<span class="icon-dot" style="background:#ff9500;"></span>':''}</button>
          <button class="icon-btn" title="Rename Tag" onclick="renamePorter('${item.porter_id}','${(item.nickname||'').replace(/'/g,"\\'")}')"><span class='mi mi-sm'>edit</span></button>
          <button class="icon-btn" title="Tag Analytics" onclick="openTagAnalytics('${item.porter_id}','${(item.nickname||item.identifier).replace(/'/g,"\\'")}','${item.identifier}')"><span class='mi mi-sm'>bar_chart</span></button>
          ${item.group_name?`<button class="icon-btn ib-danger" title="Remove from Group" onclick="confirmUngroup('${item.porter_id}')"><span class='mi mi-sm'>content_cut</span></button>`:''}
          ${(state.org && (state.myOrgRole==='owner'||state.myOrgRole==='admin') && state.orgMembers.length>1)?`<button class="icon-btn" title="Assign to Org Member" onclick="openAssignPorterToMember('${item.porter_id}','${(item.nickname||item.identifier).replace(/'/g,"\\'")}')"><span class='mi mi-sm'>group_add</span></button>`:''}
          <button class="icon-btn${item.customer_label?' ib-active':''}" title="${item.customer_label?`Customer: ${item.customer_label} — click to edit`:'Tag to a Customer'}" onclick="openCustomerIdentity('${item.porter_id}','${(item.nickname||item.identifier).replace(/'/g,"\\'")}')"><span class='mi mi-sm'>person_pin</span>${item.customer_label?'<span class="icon-dot" style="background:#16a34a;"></span>':''}</button>
        </div>
      </td>
    </tr>`;
  });
  html+='</tbody></table></div>';
  container.innerHTML=html;
  attachBatchListeners();
}

// ─────────────────────────────────────────────────────────────────────────────
// QR CODE MODAL  (v10.3 new)
// ─────────────────────────────────────────────────────────────────────────────
function openQR(identifier, nickname) {
  state.qrIdentifier = identifier;
  document.getElementById('qr-title').innerText    = nickname || identifier;
  document.getElementById('qr-subtitle').innerText = `${TAG_BASE_URL}${identifier}`;
  const wrap = document.getElementById('qr-canvas-wrap');
  wrap.innerHTML = '';
  // qrcode.js is loaded via CDN in the liquid shell
  new QRCode(wrap, {
    text:         `${TAG_BASE_URL}${identifier}`,
    width:        240,
    height:       240,
    colorDark:    '#1d1d1f',
    colorLight:   '#ffffff',
    correctLevel: QRCode.CorrectLevel.H
  });
  document.getElementById('qr-modal').classList.add('active');
}

function closeQR() {
  document.getElementById('qr-modal').classList.remove('active');
  state.qrIdentifier = null;
}

function downloadQR() {
  const canvas = document.querySelector('#qr-canvas-wrap canvas');
  if (!canvas) return;
  const link = document.createElement('a');
  link.download = `charishub-${state.qrIdentifier||'tag'}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
  showToast('QR code downloaded ✓');
}

// ─────────────────────────────────────────────────────────────────────────────
// WEBHOOK MODAL  (v3.5.0 polished)
// ─────────────────────────────────────────────────────────────────────────────

function openWebhook(type, id, currentUrl, label) {
  // v3.7.0: gate — per-scan webhooks require Essential tier
  if (!canUse('webhook')) {
    trackGateHit('webhook');
    showUpgradeModal('webhook');
    return;
  }
  state.webhookTarget = { type, id };
  document.getElementById('webhook-label').innerText   = label || id;
  document.getElementById('webhook-url-input').value   = currentUrl || '';
  const hasCurrent = !!(currentUrl && currentUrl.trim());
  document.getElementById('webhook-clear-btn').style.display = hasCurrent ? 'inline-flex' : 'none';
  // Reset test button state
  const testBtn = document.getElementById('webhook-test-btn');
  if (testBtn) { testBtn.innerText = 'Send Test'; testBtn.disabled = false; }
  // Reset payload preview + response panel
  const prev = document.getElementById('webhook-payload-preview');
  if (prev) prev.style.display = 'none';
  const respPanel = document.getElementById('webhook-response-panel');
  if (respPanel) respPanel.style.display = 'none';
  document.getElementById('webhook-modal').classList.add('active');
}

function closeWebhook() {
  document.getElementById('webhook-modal').classList.remove('active');
  state.webhookTarget = { type: null, id: null };
}

// v3.7.0: Open Stripe Customer Portal for subscription management
async function manageBilling() {
  const btn = document.getElementById('manage-billing-btn');
  if (btn) { btn.innerText = 'Opening…'; btn.disabled = true; }
  try {
    const res = await fetch(`${BILLING_WORKER}/billing-portal`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        shopify_gid: SHOPIFY_GID,
        return_url:  window.location.href,
      }),
    });
    const data = await res.json();
    if (data.url) {
      window.open(data.url, '_blank');
    } else if (data.error === 'no_subscription') {
      showToast('No active subscription found. Use the upgrade button to subscribe.');
    } else {
      showToast('Could not open billing portal — try again shortly.');
    }
  } catch (err) {
    showToast('Billing portal unavailable — check your connection.');
    console.error('manageBilling error:', err);
  } finally {
    if (btn) { btn.innerText = 'Manage Billing'; btn.disabled = false; }
  }
}

// v3.7.0: Show a banner if the account has a past_due Stripe status
function showPastDueBanner(stripeStatus) {
  const existing = document.getElementById('past-due-banner');
  if (existing) existing.remove();
  if (stripeStatus !== 'past_due') return;
  const banner = document.createElement('div');
  banner.id = 'past-due-banner';
  banner.style.cssText = 'background:#fef3c7;border:1px solid #f59e0b;border-radius:12px;padding:12px 16px;font-size:13px;color:#92400e;display:flex;align-items:center;gap:10px;margin-bottom:18px;';
  banner.innerHTML = `<span class="mi mi-sm" style="color:#f59e0b;flex-shrink:0;">warning</span>
    <span><strong>Payment issue on your account.</strong> Please update your payment method to keep your features active.
    <a href="#" onclick="event.preventDefault();manageBilling()" style="color:#b45309;font-weight:700;margin-left:4px;">Update payment →</a></span>`;
  const header = document.querySelector('#view-manager header');
  if (header) header.insertAdjacentElement('afterend', banner);
}

async function saveWebhook() {
  const url   = (document.getElementById('webhook-url-input').value || '').trim() || null;
  const { type, id } = state.webhookTarget;
  if (!type || !id) return;

  if (url) {
    try { new URL(url); }
    catch { showError('Enter a valid URL (must start with https://).'); return; }
    if (!url.startsWith('https://')) { showError('Webhook URL must start with https://'); return; }
  }

  const btn = document.getElementById('webhook-save-btn');
  btn.innerText = 'Saving…'; btn.disabled = true;
  try {
    if (type === 'porter') {
      await db.from('porters').update({ scan_webhook_url: url }).eq('id', id);
    } else {
      await db.from('porter_groups').update({ scan_webhook_url: url }).eq('id', id);
    }
    closeWebhook();
    showToast(url ? 'Webhook saved ✓' : 'Webhook cleared');
    loadData();
  } catch (err) {
    showError(`Save failed: ${err.message}`);
    btn.innerText = 'Save Webhook'; btn.disabled = false;
  }
}

async function clearWebhook() {
  document.getElementById('webhook-url-input').value = '';
  document.getElementById('webhook-clear-btn').style.display = 'none';
  await saveWebhook();
}

async function testWebhook() {
  const url = (document.getElementById('webhook-url-input').value || '').trim();
  if (!url) { showError('Enter a webhook URL to test.'); return; }
  try { new URL(url); } catch { showError('Enter a valid URL first.'); return; }
  if (!url.startsWith('https://')) { showError('Webhook URL must start with https://'); return; }

  const testBtn = document.getElementById('webhook-test-btn');
  testBtn.innerText = 'Sending…'; testBtn.disabled = true;

  const samplePayload = {
    tag_id:         state.webhookTarget.id || 'test-id',
    identifier:     'TEST01',
    nickname:       'Test Tag',
    group_name:     null,
    customer_label: null,
    customer_id:    null,
    campaign_id:    null,
    timestamp:      new Date().toISOString(),
    country:        'US',
    city:           'New York',
    device_type:    'mobile',
    os:             'iOS',
    browser:        'Safari',
    action_type:    'url',
    action_title:   'Test Action',
    ab_variant:     null,
  };

  // Show payload preview panel
  const prev = document.getElementById('webhook-payload-preview');
  if (prev) {
    prev.style.display = 'block';
    document.getElementById('webhook-payload-json').innerText = JSON.stringify(samplePayload, null, 2);
  }

  // Hide previous response panel
  const respPanel = document.getElementById('webhook-response-panel');
  if (respPanel) respPanel.style.display = 'none';

  const t0 = Date.now();
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(samplePayload)
    });
    const elapsed = Date.now() - t0;
    let bodyText = '';
    try { bodyText = await res.text(); } catch (_) {}

    // Populate response panel
    if (respPanel) {
      const statusEl  = document.getElementById('webhook-response-status');
      const timeEl    = document.getElementById('webhook-response-time');
      const labelEl   = document.getElementById('webhook-response-label');
      const bodyEl    = document.getElementById('webhook-response-body');
      const isOk      = res.status >= 200 && res.status < 300;
      statusEl.innerText  = `HTTP ${res.status}`;
      statusEl.style.color = isOk ? '#16a34a' : res.status >= 500 ? '#dc2626' : '#d97706';
      timeEl.innerText    = `${elapsed}ms`;
      labelEl.innerText   = isOk ? '✓ Delivered' : '⚠ Not delivered';
      labelEl.style.background = isOk ? '#dcfce7' : '#fef3c7';
      labelEl.style.color      = isOk ? '#15803d' : '#92400e';
      bodyEl.innerText    = bodyText ? bodyText.slice(0, 400) : '(empty response body)';
      respPanel.style.display = 'block';
    }

    testBtn.innerText = res.ok ? '✓ Delivered' : `⚠ ${res.status}`;
    setTimeout(() => { testBtn.innerText = 'Send Test'; testBtn.disabled = false; }, 4000);
  } catch (err) {
    const elapsed = Date.now() - t0;
    if (respPanel) {
      document.getElementById('webhook-response-status').innerText = 'Request failed';
      document.getElementById('webhook-response-status').style.color = '#dc2626';
      document.getElementById('webhook-response-time').innerText = `${elapsed}ms`;
      document.getElementById('webhook-response-label').innerText = '✗ Connection error';
      document.getElementById('webhook-response-label').style.background = '#fee2e2';
      document.getElementById('webhook-response-label').style.color = '#991b1b';
      document.getElementById('webhook-response-body').innerText = err.message;
      respPanel.style.display = 'block';
    }
    testBtn.innerText = '✗ Failed';
    setTimeout(() => { testBtn.innerText = 'Send Test'; testBtn.disabled = false; }, 4000);
  }
}

function toggleWebhookPayload() {
  const prev = document.getElementById('webhook-payload-preview');
  const btn  = document.getElementById('webhook-payload-toggle');
  if (!prev) return;
  const showing = prev.style.display !== 'none';
  prev.style.display = showing ? 'none' : 'block';
  if (btn) btn.innerText = showing ? 'Show example payload ▾' : 'Hide payload ▴';
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSIGN MODAL (updated for Sequence tab)
// ─────────────────────────────────────────────────────────────────────────────
function openAssign(type, id) {
  state.activeAssignType=type; state.activeAssignId=id; state.editTarget=null;
  document.getElementById('modal-tab-container').style.display='flex';
  switchAssignTab('new');
  document.getElementById('charis-modal').classList.add('active');
}

function switchAssignTab(tab) {
  state.activeTab=tab;
  ['new','lib','sch','seq','rte','sub'].forEach(t=>{const b=document.getElementById(`tab-btn-${t}`);if(b)b.classList.toggle('active',t===tab);});
  renderAssignModalContent();
}

// Command-center tab switcher (v11.0.0)
function switchCommandTab(name) {
  // Tear down live feed when leaving org tab
  const leavingOrg = document.getElementById('ctab-org')?.classList.contains('active') && name !== 'org';
  if (leavingOrg && _orgRealtimeChannel) {
    db.removeChannel(_orgRealtimeChannel);
    _orgRealtimeChannel = null;
  }
  document.querySelectorAll('.cmd-tab').forEach(b=>b.classList.remove('active'));
  document.querySelectorAll('.cmd-panel').forEach(p=>p.classList.remove('active'));
  const tab=document.getElementById('ctab-'+name);
  const panel=document.getElementById('cpanel-'+name);
  if(tab) tab.classList.add('active');
  if(panel) panel.classList.add('active');
}

function renderAssignModalContent() {
  const f=document.getElementById('modal-fields'); const b=document.getElementById('modal-confirm-btn');
  b.className='btn-charis btn-primary'; b.innerText='Confirm';

  if (state.activeTab==='sch') {
    document.getElementById('modal-title').innerText='Assign Schedule';
    document.getElementById('modal-subtitle').innerText="The tag will use the schedule's action during the active window.";
    const opts=state.schedules.map(s=>`<option value="${s.id}">${s.name} (${tzLabel(s.timezone||'UTC')})</option>`).join('');
    f.innerHTML=`<label class="field-label">Select Schedule</label><select id="sch-select" class="charis-input"><option value="">-- Disable Schedule --</option>${opts}</select><p style="font-size:11px;color:#888;margin-top:4px;">Outside the window the tag's default action applies.</p>`;
    b.onclick=async()=>{
      const schedId=document.getElementById('sch-select').value||null;
      if(state.activeAssignType==='porter') { await db.from('porters').update({schedule_id:schedId,use_schedule:!!schedId,use_sequence:false}).eq('id',state.activeAssignId); }
      else { await db.from('porter_groups').update({schedule_id:schedId}).eq('id',state.activeAssignId); }
      closeModal(); loadData();
    };

  } else if (state.activeTab==='seq') {
    document.getElementById('modal-title').innerText='Assign Sequence';
    document.getElementById('modal-subtitle').innerText="Each scan advances through the sequence steps in order.";
    const opts=state.sequences.map(s=>`<option value="${s.id}">${s.name} (${s.mode})</option>`).join('');
    f.innerHTML=`<label class="field-label">Select Sequence</label><select id="seq-select" class="charis-input"><option value="">-- Disable Sequence --</option>${opts}</select><p style="font-size:11px;color:#888;margin-top:4px;">Sequence takes priority over schedule and direct action.</p>`;
    b.onclick=async()=>{
      const seqId=document.getElementById('seq-select').value||null;
      if(state.activeAssignType==='porter') { await db.from('porters').update({sequence_id:seqId,use_sequence:!!seqId,use_schedule:false}).eq('id',state.activeAssignId); }
      else { await db.from('porter_groups').update({sequence_id:seqId}).eq('id',state.activeAssignId); }
      closeModal(); loadData();
    };

  } else if (state.activeTab==='rte') {
    // v11.0.0 — Conditional Route assignment
    document.getElementById('modal-title').innerText='Assign Conditional Route';
    document.getElementById('modal-subtitle').innerText='Serve different actions based on device, country, or OS. First matching rule wins.';
    const opts=state.routes.map(r=>`<option value="${r.id}">${r.name} (${r.rules?.length||0} rule${r.rules?.length!==1?'s':''})</option>`).join('');
    f.innerHTML=`<label class="field-label">Select Route</label><select id="rte-select" class="charis-input"><option value="">-- Disable Routing --</option>${opts}</select><p style="font-size:11px;color:#888;margin-top:4px;">Route is evaluated after sequences. If no rule matches and no fallback is set, the tag's default action applies.</p>`;
    b.onclick=async()=>{
      const routeId=document.getElementById('rte-select').value||null;
      if(state.activeAssignType==='porter') { await db.from('porters').update({route_id:routeId,use_route:!!routeId,use_sequence:false}).eq('id',state.activeAssignId); }
      else { await db.from('porter_groups').update({route_id:routeId,use_route:!!routeId}).eq('id',state.activeAssignId); }
      closeModal(); loadData();
    };

  } else if (state.activeTab==='sub') {
    // v3.0.0 — Subscribe to a Shared Sequence
    document.getElementById('modal-title').innerText='Subscribe to Shared Sequence';
    document.getElementById('modal-subtitle').innerText='This tag will follow a centrally-managed sequence. Overrides any direct action while active.';
    // Current subscription for this porter (if porter-level assign)
    const currentSub = state.activeAssignType==='porter'
      ? state.sharedSubscriptions.find(s=>s.porter_id===state.activeAssignId) : null;
    const psOpts = state.publishedSequences
      .filter(ps=>ps.active)
      .map(ps=>{
        const label=`${ps.name} · ${ADVANCE_LABEL[ps.advance_on]||ps.advance_on} · ${ps.action_ids?.length||0} steps`;
        const selected=currentSub&&currentSub.published_sequence_id===ps.id?'selected':'';
        return `<option value="${ps.id}" ${selected}>${label}</option>`;
      }).join('');
    if (!psOpts) {
      f.innerHTML=`<p style="color:var(--charis-secondary);font-size:13px;">You haven't published any shared sequences yet. Create one in the <strong>Shared</strong> tab first.</p>`;
      b.style.display='none'; return;
    }
    b.style.display='';
    f.innerHTML=`
      <label class="field-label">Shared Sequence</label>
      <select id="sub-seq-select" class="charis-input">
        <option value="">— Unsubscribe —</option>
        ${psOpts}
      </select>
      <p style="font-size:11px;color:#888;margin-top:4px;">Shared sequences take priority over all other assignments while active.</p>
      ${currentSub?`<p style="font-size:11px;color:var(--charis-secondary);margin-top:4px;">Currently subscribed to: <strong>${currentSub.seq?.name||'a shared sequence'}</strong></p>`:''}`;
    b.innerText='Save Subscription';
    b.onclick=async()=>{
      const psId=document.getElementById('sub-seq-select').value||null;
      b.innerText='Saving…'; b.disabled=true;
      try {
        if (psId) {
          await db.rpc('subscribe_tag_to_shared_sequence',{
            p_porter_id:state.activeAssignId,
            p_published_sequence_id:psId,
            p_subscriber_profile_id:state.profileId
          });
        } else {
          // Unsubscribe: deactivate any active subscription for this porter
          await db.from('sequence_subscriptions')
            .update({active:false})
            .eq('porter_id',state.activeAssignId)
            .eq('active',true);
        }
        closeModal(); loadData();
        showToast(psId?'Tag subscribed!':'Tag unsubscribed from shared sequence.');
      } catch(err) {
        showError(`Failed: ${err.message}`);
        b.disabled=false; b.innerText='Save Subscription';
      }
    };

  } else if (state.activeTab==='cam') {
    // v3.6.0 — Campaign assignment
    if (state.activeAssignType !== 'porter') {
      f.innerHTML = `<p style="color:var(--charis-secondary);font-size:13px;">Campaigns can only be assigned to individual tags, not to entire groups.</p>`;
      b.style.display = 'none';
      return;
    }
    b.style.display = '';
    document.getElementById('modal-title').innerText = 'Assign to Campaign';
    document.getElementById('modal-subtitle').innerText = 'When tapped, this tag will start or update a background Visit Session for this Campaign.';
    const opts = state.campaigns.map(c => `<option value="${c.id}">${c.name}</option>`).join('');
    f.innerHTML = `<label class="field-label">Select Campaign</label>
      <select id="cam-select" class="charis-input"><option value="">-- Remove from Campaign --</option>${opts}</select>
      <p style="font-size:11px;color:#888;margin-top:4px;">Campaign assignment does not change the tag's action directly; it just logs background sessions.</p>`;
    b.onclick = async () => {
      const campId = document.getElementById('cam-select').value || null;
      b.innerText = 'Saving…'; b.disabled = true;
      try {
        await db.from('porters').update({ campaign_id: campId }).eq('id', state.activeAssignId);
        closeModal(); loadData();
      } catch(err) {
        showError(`Failed: ${err.message}`);
        b.disabled = false; b.innerText = 'Confirm';
      }
    };

  } else if (state.activeTab==='lib') {
    document.getElementById('modal-title').innerText='Assign from Library';
    document.getElementById('modal-subtitle').innerText='Select an existing action to assign.';
    f.innerHTML=`<label class="field-label">Library Item</label><select id="lib-select" class="charis-input">${state.actions.map(a=>`<option value="${a.id}">${a.type==='vcard'?'VCARD':a.type.toUpperCase()}: ${a.title}</option>`).join('')}</select>`;
    b.onclick=async()=>{
      const actionId=document.getElementById('lib-select').value;
      b.innerText='Saving…'; b.disabled=true;
      try {
        if(state.activeAssignType==='porter') { await db.from('porters').update({individual_action_id:actionId,use_schedule:false,use_sequence:false,use_route:false,schedule_id:null,sequence_id:null,route_id:null}).eq('id',state.activeAssignId); }
        else { await db.from('porter_groups').update({active_action_id:actionId,use_route:false,schedule_id:null,sequence_id:null,route_id:null}).eq('id',state.activeAssignId); }
        closeModal(); loadData();
      } catch(err) { showError(`Failed to assign action: ${err.message}`); b.disabled=false; b.innerText='Assign'; }
    };

  } else {
    document.getElementById('modal-title').innerText=state.activeAssignType?'Create & Assign Action':'Create New Action';
    document.getElementById('modal-subtitle').innerText='';
    // v3.1.0: build action-type options with tier gating
    const _porterId = state.activeAssignType === 'porter' ? state.activeAssignId : null;
    const _lo = (feat, label) => canUse(feat, _porterId)
      ? `<option value="${feat === 'contact_card' ? 'vcard' : feat === 'form' ? 'form' : feat === 'webhook' ? 'webhook' : 'ab_split'}">${label}</option>`
      : `<option value="${feat === 'contact_card' ? 'vcard' : feat === 'form' ? 'form' : feat === 'webhook' ? 'webhook' : 'ab_split'}" disabled class="tier-locked">${label} — ${minTierName(feat)}</option>`;
    f.innerHTML=`${!canUse('contact_card', _porterId) || !canUse('form', _porterId) || !canUse('webhook', _porterId) || !canUse('ab_split', _porterId) ? `<div class="tier-upgrade-banner"><span class="mi mi-sm">lock</span> Some action types require an upgraded plan. <a href="#">Upgrade →</a></div>` : ''}<label class="field-label">Action Type</label><select id="a-type" class="charis-input" onchange="renderDynamicFields()"><option value="url">Website URL</option>${_lo('contact_card','Contact Card (vCard)')}<option value="phone">Phone Number</option><option value="sms">SMS Message</option><option value="email">Email</option><option value="wifi">WiFi Credentials</option>${_lo('webhook','Webhook')}<option value="message">Rich Text Message</option><option value="html">HTML Page</option>${_lo('form','Lead Capture Form')}${_lo('ab_split','A/B Split Test')}</select><label class="field-label">Internal Label</label><input type="text" id="a-title" class="charis-input" placeholder="e.g. Office WiFi, John's Card..."><div id="dynamic-zone"></div>`;
    renderDynamicFields(); b.onclick=saveAction;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DYNAMIC FORM FIELDS
// ─────────────────────────────────────────────────────────────────────────────
function renderDynamicFields(prefill) {
  const type=document.getElementById('a-type')?.value; const zone=document.getElementById('dynamic-zone');
  if (!type||!zone) return; const d=prefill||{};
  switch(type) {
    case 'url':     zone.innerHTML=`<label class="field-label">URL</label><input type="url" id="d-url" class="charis-input" placeholder="https://example.com" autocomplete="off" value="${d.url||''}">`;break;
    case 'phone':   zone.innerHTML=`<label class="field-label">Phone Number</label><input type="tel" id="d-number" class="charis-input" placeholder="+15551234567" value="${d.number||''}">`;break;
    case 'sms':     zone.innerHTML=`<label class="field-label">To</label><input type="tel" id="d-to" class="charis-input" placeholder="+15551234567" value="${d.to||''}"><label class="field-label">Pre-filled Message</label><textarea id="d-body" class="charis-input" style="height:80px;">${d.body||''}</textarea>`;break;
    case 'email':   zone.innerHTML=`<label class="field-label">To</label><input type="email" id="d-to" class="charis-input" value="${d.to||''}"><label class="field-label">Subject</label><input type="text" id="d-subject" class="charis-input" value="${d.subject||''}"><label class="field-label">Body</label><textarea id="d-body" class="charis-input" style="height:80px;">${d.body||''}</textarea>`;break;
    case 'wifi': {
      // v2.8.2: if editing an action that already has an encrypted password, show placeholder
      // rather than exposing the encrypted blob in the input value.
      const isEncrypted = (d.password||'').startsWith('enc:v1:');
      zone.innerHTML=`<label class="field-label">Network Name (SSID)</label><input id="d-ssid" class="charis-input" value="${d.ssid||''}"><label class="field-label">Password ${isEncrypted?'<span style="color:#059669;font-weight:400;font-size:11px;">● saved &amp; encrypted — leave blank to keep unchanged</span>':''}</label><input type="password" id="d-password" class="charis-input" placeholder="${isEncrypted?'Leave blank to keep saved password':''}" value=""><input type="hidden" id="d-password-existing" value="${isEncrypted?d.password:''}"><label class="field-label">Security</label><select id="d-security" class="charis-input"><option value="WPA" ${d.security==='WPA'||!d.security?'selected':''}>WPA/WPA2</option><option value="WEP" ${d.security==='WEP'?'selected':''}>WEP</option><option value="nopass" ${d.security==='nopass'?'selected':''}>None</option></select>`;
      break;
    }
    case 'webhook': zone.innerHTML=`<label class="field-label">Webhook URL</label><input type="text" id="d-url" class="charis-input" value="${d.url||''}"><label class="field-label">Method</label><select id="d-method" class="charis-input"><option value="POST" ${d.method==='POST'||!d.method?'selected':''}>POST</option><option value="GET" ${d.method==='GET'?'selected':''}>GET</option></select><label class="field-label">Payload JSON</label><textarea id="d-payload" class="charis-input" style="height:80px;font-family:monospace;">${d.payload||'{}'}</textarea><div style="margin-top:4px;"><span class="variable-tag">{{tag_id}}</span><span class="variable-tag">{{nickname}}</span><span class="variable-tag">{{timestamp}}</span></div>`;break;
    case 'message': {
      zone.innerHTML=`<label class="field-label">Title</label><input type="text" id="d-title" class="charis-input" value="${d.title||''}"><label class="field-label">Content (Rich Text)</label><div id="quill-editor" style="background:#fff;border:1px solid #e5e5ea;border-radius:0 0 8px 8px;min-height:160px;font-size:15px;"></div>`;
      setTimeout(()=>{
        if(state.quillInstance){try{state.quillInstance=null;}catch(e){}}
        if(typeof Quill==='undefined'){zone.innerHTML+=`<p style="color:red;font-size:11px;">Quill editor not loaded — check liquid file includes the Quill CDN scripts.</p>`;return;}
        state.quillInstance=new Quill('#quill-editor',{theme:'snow',placeholder:'Write your message…',modules:{toolbar:[[{header:[1,2,3,false]}],['bold','italic','underline','strike'],[{list:'ordered'},{list:'bullet'}],['link','clean']]}});
        if(d.content) state.quillInstance.root.innerHTML=d.content;
      },0);
      break;
    }
    case 'html':    zone.innerHTML=`<label class="field-label">HTML</label><textarea id="d-content" class="charis-input" style="height:160px;font-family:monospace;">${d.content||''}</textarea>`;break;
    case 'form':    renderFormFields(d);break;
    case 'vcard':    renderVcardFields(d);break;
    case 'ab_split': renderAbSplitFields(d);break;
  }
}

function renderAbSplitFields(d) {
  d = d || {};
  const weightA = d.weight_a != null ? d.weight_a : 50;
  const weightB = 100 - weightA;
  // Build action options — exclude other ab_split actions to avoid infinite loops
  const eligible = state.actions.filter(a => a.type !== 'ab_split');
  if (eligible.length < 2) {
    document.getElementById('dynamic-zone').innerHTML = `
      <div style="background:#fff7ed;border:1px solid #ff9500;border-radius:12px;padding:16px 20px;color:#92400e;">
        <div style="font-weight:800;margin-bottom:4px;"><span class='mi'>warning</span> Not enough actions in your library</div>
        <div style="font-size:13px;">A/B Split requires at least 2 non-split actions to choose from. Create them in the Library tab first, then come back to set up a split.</div>
      </div>`;
    return;
  }
  const opts = eligible
    .map(a => `<option value="${a.id}" ${d.action_a_id===a.id?'selected':''}>${a.type==='vcard'?'VCARD':a.type.toUpperCase()}: ${a.title}</option>`)
    .join('');
  const optsB = eligible
    .map(a => `<option value="${a.id}" ${d.action_b_id===a.id?'selected':''}>${a.type==='vcard'?'VCARD':a.type.toUpperCase()}: ${a.title}</option>`)
    .join('');

  document.getElementById('dynamic-zone').innerHTML = `
    <div class="ab-split-panel">
      <div class="ab-variant-block ab-a">
        <div class="ab-variant-label">Variant A</div>
        <select id="d-action-a" class="charis-input" onchange="updateAbWeightLabel()">
          <option value="">— Select Action —</option>${opts}
        </select>
      </div>
      <div class="ab-weight-block">
        <label class="field-label">Weight A <span id="ab-weight-display" style="font-weight:800;color:var(--charis-accent);">${weightA}% / ${weightB}%</span></label>
        <input type="range" id="d-weight-a" min="0" max="100" value="${weightA}" class="ab-slider" oninput="updateAbWeightLabel()">
        <div class="ab-weight-hint">Slide to set how often Variant A is served. Variant B gets the remainder.</div>
      </div>
      <div class="ab-variant-block ab-b">
        <div class="ab-variant-label">Variant B</div>
        <select id="d-action-b" class="charis-input" onchange="updateAbWeightLabel()">
          <option value="">— Select Action —</option>${optsB}
        </select>
      </div>
    </div>
    <div class="ab-note">Both variants must already exist in your Action Library. Create them first, then come back to set up this split.</div>`;
}

function updateAbWeightLabel() {
  const slider = document.getElementById('d-weight-a');
  const label  = document.getElementById('ab-weight-display');
  if (!slider || !label) return;
  const a = parseInt(slider.value);
  label.innerText = `${a}% / ${100 - a}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM ACTION BUILDER  (v2.7.0)
// ─────────────────────────────────────────────────────────────────────────────
function renderFormFields(d) {
  d = d || {};
  document.getElementById('dynamic-zone').innerHTML = `
    <label class="field-label">Form Title</label>
    <input type="text" id="d-form-title" class="charis-input" placeholder="e.g. Join our waitlist" value="${d.title||''}">
    <label class="field-label">Description (optional)</label>
    <input type="text" id="d-form-desc" class="charis-input" placeholder="Short intro shown above the form" value="${d.description||''}">
    <label class="field-label">Submit Button Label</label>
    <input type="text" id="d-form-btn" class="charis-input" placeholder="Submit" value="${d.button_label||'Submit'}">
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Form Fields</div>
    <div id="form-fields-container"></div>
    <button type="button" class="add-entry-btn" onclick="addFormField()">+ Add Field</button>
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">On Submit</div>
    <label class="field-label">Redirect URL <span style="font-weight:400;color:#aaa;">(optional — leave blank to show success message)</span></label>
    <input type="text" id="d-form-redirect" class="charis-input" placeholder="https://example.com/catalog.pdf" value="${d.redirect_url||''}">
    <label class="field-label">Success Message</label>
    <input type="text" id="d-form-success" class="charis-input" placeholder="Thanks! We'll be in touch." value="${d.success_message||"Thanks! We'll be in touch."}">`;
  (d.fields||[]).forEach(f => addFormField(f));
  if (!(d.fields&&d.fields.length)) addFormField();
}

function addFormField(prefill) {
  prefill = prefill || {};
  const container = document.getElementById('form-fields-container'); if (!container) return;
  const row = document.createElement('div'); row.className = 'form-field-row window-row';
  const isSelect = prefill.type === 'select';
  const typeOpts = ['text','email','phone','number','select'].map(t =>
    `<option value="${t}" ${prefill.type===t?'selected':''}>${t.charAt(0).toUpperCase()+t.slice(1)}</option>`
  ).join('');
  row.innerHTML = `
    <div style="flex:0 0 100px;"><label class="field-label">Type</label>
      <select class="charis-input ff-type" style="margin-bottom:0;" onchange="toggleFormFieldOptions(this)">${typeOpts}</select></div>
    <div style="flex:2;"><label class="field-label">Label</label>
      <input type="text" class="charis-input ff-label" style="margin-bottom:0;" placeholder="e.g. Email Address" value="${prefill.label||''}"></div>
    <div style="flex:0 0 80px;display:flex;align-items:flex-end;padding-bottom:2px;">
      <label style="display:flex;align-items:center;gap:5px;font-size:12px;color:#555;cursor:pointer;white-space:nowrap;">
        <input type="checkbox" class="ff-required" ${prefill.required?'checked':''}> Required</label></div>
    <button type="button" class="remove-entry" onclick="this.parentElement.remove()" style="margin-top:22px;"><span class='mi mi-sm'>close</span></button>
    <div class="ff-options" style="flex-basis:100%;${isSelect?'':'display:none;'}margin-top:6px;">
      <label class="field-label">Options (one per line)</label>
      <textarea class="charis-input ff-options-text" style="height:60px;margin-bottom:0;">${(prefill.options||[]).join('\n')}</textarea></div>`;
  container.appendChild(row);
}

function toggleFormFieldOptions(sel) {
  const opts = sel.closest('.form-field-row').querySelector('.ff-options');
  if (opts) opts.style.display = sel.value === 'select' ? 'flex' : 'none';
}

function renderVcardFields(d) {
  d=d||{}; const sl=d.social_links||{}; const th=d.theme||{}; const addr=(d.addresses&&d.addresses[0])?d.addresses[0]:{};
  document.getElementById('dynamic-zone').innerHTML=`
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Name</div>
    <div class="form-row">
      <div style="flex:0 0 90px;"><label class="field-label">Prefix</label><select id="d-prefix" class="charis-input" style="padding:14px 6px;"><option value="">—</option>${['Mr.','Mrs.','Ms.','Dr.','Prof.','Rev.'].map(p=>`<option value="${p}" ${d.prefix===p?'selected':''}>${p}</option>`).join('')}</select></div>
      <div style="flex:2;"><label class="field-label">First Name *</label><input id="d-fn" class="charis-input" value="${d.first_name||''}"></div>
      <div style="flex:1;"><label class="field-label">Middle</label><input id="d-mn" class="charis-input" value="${d.middle_name||''}"></div>
    </div>
    <div class="form-row">
      <div style="flex:2;"><label class="field-label">Last Name *</label><input id="d-ln" class="charis-input" value="${d.last_name||''}"></div>
      <div style="flex:1;"><label class="field-label">Suffix</label><input id="d-suffix" class="charis-input" value="${d.suffix||''}"></div>
      <div style="flex:1;"><label class="field-label">Nickname</label><input id="d-nickname" class="charis-input" value="${d.nickname||''}"></div>
    </div>
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Work</div>
    <div class="form-row"><div style="flex:1;"><label class="field-label">Job Title</label><input id="d-job" class="charis-input" value="${d.job_title||''}"></div><div style="flex:1;"><label class="field-label">Department</label><input id="d-dept" class="charis-input" value="${d.department||''}"></div></div>
    <label class="field-label">Company</label><input id="d-company" class="charis-input" value="${d.company_name||''}">
    <label class="field-label">Tagline</label><input id="d-headline" class="charis-input" value="${d.company_headline||''}">
    <label class="field-label">Bio</label><textarea id="d-bio" class="charis-input" style="height:70px;">${d.bio||''}</textarea>
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Images</div>
    <label class="field-label">Profile Photo</label><input type="file" accept="image/*" class="charis-input" onchange="toBase64(this.files[0],'pfp')">
    ${state.imageData.pfp?`<img src="${state.imageData.pfp}" style="width:60px;height:60px;border-radius:50%;object-fit:cover;margin-bottom:10px;">`:(d.profile_picture_url?`<img src="${d.profile_picture_url}" style="width:60px;height:60px;border-radius:50%;object-fit:cover;margin-bottom:10px;">`:'') }
    <label class="field-label">Company Logo</label><input type="file" accept="image/*" class="charis-input" onchange="toBase64(this.files[0],'logo')">
    ${state.imageData.logo?`<img src="${state.imageData.logo}" style="width:80px;height:40px;object-fit:contain;margin-bottom:10px;">`:(d.company_logo_url?`<img src="${d.company_logo_url}" style="width:80px;height:40px;object-fit:contain;margin-bottom:10px;">`:'') }
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Phone Numbers</div>
    <div id="phones-container"></div><button type="button" class="add-entry-btn" onclick="addContactEntry('phones','phone','mobile')">+ Add Phone</button>
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Email Addresses</div>
    <div id="emails-container"></div><button type="button" class="add-entry-btn" onclick="addContactEntry('emails','email','work')">+ Add Email</button>
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Websites</div>
    <div id="websites-container"></div><button type="button" class="add-entry-btn" onclick="addContactEntry('websites','url','work')">+ Add Website</button>
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Address</div>
    <label class="field-label">Street</label><input id="d-street" class="charis-input" value="${addr.street||''}">
    <div class="form-row"><div style="flex:2;"><label class="field-label">City</label><input id="d-city" class="charis-input" value="${addr.city||''}"></div><div style="flex:1;"><label class="field-label">State</label><input id="d-state" class="charis-input" value="${addr.state||''}"></div><div style="flex:1;"><label class="field-label">ZIP</label><input id="d-zip" class="charis-input" value="${addr.zip||''}"></div></div>
    <label class="field-label">Country</label><input id="d-country" class="charis-input" value="${addr.country||'US'}">
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Social Links</div>
    ${[['linkedin','LinkedIn'],['twitter','Twitter / X'],['instagram','Instagram'],['facebook','Facebook'],['youtube','YouTube'],['tiktok','TikTok'],['github','GitHub'],['snapchat','Snapchat'],['pinterest','Pinterest'],['threads','Threads']].map(([key,label])=>`<label class="field-label">${label}</label><input type="url" id="d-social-${key}" class="charis-input" placeholder="https://..." value="${sl[key]||''}">`).join('')}
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Important Dates</div>
    <div class="form-row"><div style="flex:1;"><label class="field-label">Birthday</label><input type="date" id="d-birthday" class="charis-input" value="${d.birthday||''}"></div><div style="flex:1;"><label class="field-label">Anniversary</label><input type="date" id="d-anniversary" class="charis-input" value="${d.anniversary||''}"></div></div>
    <hr class="hr-label">
    <div style="font-size:11px;font-weight:800;color:var(--charis-accent);margin-bottom:10px;text-transform:uppercase;">Card Theme</div>
    <div class="form-row"><div style="flex:1;"><label class="field-label">Primary Color</label><input type="color" id="d-color" class="charis-input" style="padding:8px;height:48px;" value="${th.primary_color||'#0071e3'}"></div><div style="flex:1;"><label class="field-label">Background</label><input type="color" id="d-bg-color" class="charis-input" style="padding:8px;height:48px;" value="${th.background_color||'#ffffff'}"></div><div style="flex:2;"><label class="field-label">Font</label><select id="d-font" class="charis-input">${['Inter','Helvetica','Georgia','Montserrat','Playfair Display','Roboto'].map(f=>`<option value="${f}" ${th.font_family===f?'selected':''}>${f}</option>`).join('')}</select></div></div>`;
  (d.phones||[]).forEach(p=>addContactEntry('phones','phone',p.type||'work',p.number||''));
  (d.emails||[]).forEach(e=>addContactEntry('emails','email',e.type||'work',e.address||''));
  (d.websites||[]).forEach(w=>addContactEntry('websites','url',w.type||'work',w.url||''));
  if(!(d.phones&&d.phones.length)) addContactEntry('phones','phone','mobile');
  if(!(d.emails&&d.emails.length)) addContactEntry('emails','email','work');
  if(!(d.websites&&d.websites.length)) addContactEntry('websites','url','work');
  initAddressAutocomplete();
}

function addContactEntry(section,inputType,defaultType,prefillValue) {
  const container=document.getElementById(`${section}-container`); if(!container) return;
  const row=document.createElement('div'); row.className='entry-row';
  const typeOpts={phones:['mobile','work','home','fax','other'],emails:['work','personal','other'],websites:['work','personal','portfolio','other']}[section]||['work','other'];
  const ph={phones:'+15551234567',emails:'name@example.com',websites:'https://example.com'}[section]||'';
  row.innerHTML=`<select class="charis-input entry-type" style="flex:0 0 110px;padding:14px 6px;margin-bottom:0;">${typeOpts.map(t=>`<option value="${t}" ${t===defaultType?'selected':''}>${t.charAt(0).toUpperCase()+t.slice(1)}</option>`).join('')}</select><input type="${inputType}" class="charis-input entry-value" placeholder="${ph}" style="flex:1;margin-bottom:0;" value="${prefillValue||''}"><button type="button" class="remove-entry" onclick="this.parentElement.remove()"><span class='mi mi-sm'>close</span></button>`;
  container.appendChild(row);
}

// ─────────────────────────────────────────────────────────────────────────────
// WIFI PASSWORD ENCRYPTION  (v2.8.2 — AES-GCM via Web Crypto API)
// Encrypted values are stored as: enc:v1:<base64_iv>:<base64_ciphertext>
// The key is derived from a fixed app salt + the user's profile UUID via PBKDF2.
// This makes plain-text passwords unreadable in the database without also
// knowing the profileId and the application source code.
// ─────────────────────────────────────────────────────────────────────────────
const _WIFI_APP_SALT = 'charishub-wifi-enc-v1';

async function _deriveWifiKey(profileId) {
  const raw = new TextEncoder().encode(_WIFI_APP_SALT + profileId);
  const baseKey = await crypto.subtle.importKey('raw', raw, { name: 'PBKDF2' }, false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: new TextEncoder().encode(profileId), iterations: 100000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptWifiPassword(password, profileId) {
  if (!password || !profileId) return password;
  try {
    const key = await _deriveWifiKey(profileId);
    const iv  = crypto.getRandomValues(new Uint8Array(12));
    const ct  = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(password));
    const toB64 = buf => btoa(String.fromCharCode(...new Uint8Array(buf)));
    return `enc:v1:${toB64(iv.buffer)}:${toB64(ct)}`;
  } catch(e) {
    console.warn('WiFi encrypt failed — storing plaintext:', e);
    return password;
  }
}

async function decryptWifiPassword(stored, profileId) {
  if (!stored || !stored.startsWith('enc:v1:') || !profileId) return stored || '';
  try {
    const parts = stored.split(':');
    // Format: enc : v1 : ivB64 : ctB64  (4 colons total, so split gives 4 parts)
    const fromB64 = b64 => Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const iv  = fromB64(parts[2]);
    const ct  = fromB64(parts[3]);
    const key = await _deriveWifiKey(profileId);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return new TextDecoder().decode(plain);
  } catch(e) {
    console.warn('WiFi decrypt failed:', e);
    return '';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// BUILD PAYLOAD
// ─────────────────────────────────────────────────────────────────────────────
function buildPayload(type) {
  switch(type) {
    case 'url':     return {url:document.getElementById('d-url')?.value?.trim()||''};
    case 'phone':   return {number:document.getElementById('d-number')?.value?.trim()||''};
    case 'sms':     return {to:document.getElementById('d-to')?.value?.trim()||'',body:document.getElementById('d-body')?.value||''};
    case 'email':   return {to:document.getElementById('d-to')?.value?.trim()||'',subject:document.getElementById('d-subject')?.value||'',body:document.getElementById('d-body')?.value||''};
    case 'wifi': {
      const pwdNew = document.getElementById('d-password')?.value || '';
      const pwdExisting = document.getElementById('d-password-existing')?.value || '';
      // Use new password if provided; otherwise fall back to the existing encrypted blob
      return {ssid:document.getElementById('d-ssid')?.value||'', password: pwdNew || pwdExisting, security:document.getElementById('d-security')?.value||'WPA'};
    }
    case 'webhook': return {url:document.getElementById('d-url')?.value?.trim()||'',method:document.getElementById('d-method')?.value||'POST',payload:document.getElementById('d-payload')?.value||'{}'};
    case 'message': return {title:document.getElementById('d-title')?.value||'',content:state.quillInstance?state.quillInstance.root.innerHTML:(document.getElementById('d-content')?.value||'')};
    case 'html':    return {content:document.getElementById('d-content')?.value||''};
    case 'form': {
      const fields=Array.from(document.querySelectorAll('.form-field-row')).map(row=>({
        id:'f_'+Math.random().toString(36).slice(2,7),
        type:row.querySelector('.ff-type')?.value||'text',
        label:row.querySelector('.ff-label')?.value?.trim()||'',
        required:row.querySelector('.ff-required')?.checked||false,
        options:row.querySelector('.ff-type')?.value==='select'?(row.querySelector('.ff-options-text')?.value||'').split('\n').map(o=>o.trim()).filter(Boolean):undefined
      })).filter(f=>f.label);
      return {
        title:document.getElementById('d-form-title')?.value?.trim()||'',
        description:document.getElementById('d-form-desc')?.value?.trim()||'',
        button_label:document.getElementById('d-form-btn')?.value?.trim()||'Submit',
        fields,
        redirect_url:document.getElementById('d-form-redirect')?.value?.trim()||null,
        success_message:document.getElementById('d-form-success')?.value?.trim()||"Thanks! We'll be in touch."
      };
    }
    case 'vcard':    return buildVcardPayload();
    case 'ab_split': return {
      action_a_id: document.getElementById('d-action-a')?.value || null,
      action_b_id: document.getElementById('d-action-b')?.value || null,
      weight_a:    parseInt(document.getElementById('d-weight-a')?.value || '50')
    };
    default:        return {};
  }
}

function buildVcardPayload() {
  const ce=(section,vk)=>Array.from(document.querySelectorAll(`#${section}-container .entry-row`)).map(r=>({type:r.querySelector('.entry-type')?.value||'other',[vk]:r.querySelector('.entry-value')?.value?.trim()||''})).filter(e=>e[vk]);
  return {
    prefix:document.getElementById('d-prefix')?.value||'',first_name:document.getElementById('d-fn')?.value||'',middle_name:document.getElementById('d-mn')?.value||'',last_name:document.getElementById('d-ln')?.value||'',suffix:document.getElementById('d-suffix')?.value||'',nickname:document.getElementById('d-nickname')?.value||'',
    job_title:document.getElementById('d-job')?.value||'',department:document.getElementById('d-dept')?.value||'',company_name:document.getElementById('d-company')?.value||'',company_headline:document.getElementById('d-headline')?.value||'',bio:document.getElementById('d-bio')?.value||'',
    profile_picture_url:state.imageData.pfp||'',company_logo_url:state.imageData.logo||'',banner_image_url:state.imageData.banner||'',
    phones:ce('phones','number'),emails:ce('emails','address'),websites:ce('websites','url'),
    addresses:[{type:'work',street:document.getElementById('d-street')?.value||'',city:document.getElementById('d-city')?.value||'',state:document.getElementById('d-state')?.value||'',zip:document.getElementById('d-zip')?.value||'',country:document.getElementById('d-country')?.value||'US'}],
    social_links:['linkedin','twitter','instagram','facebook','youtube','tiktok','github','snapchat','pinterest','threads'].reduce((acc,k)=>{acc[k]=document.getElementById(`d-social-${k}`)?.value?.trim()||'';return acc;},{}),
    birthday:document.getElementById('d-birthday')?.value||'',anniversary:document.getElementById('d-anniversary')?.value||'',
    theme:{primary_color:document.getElementById('d-color')?.value||'#0071e3',background_color:document.getElementById('d-bg-color')?.value||'#ffffff',font_family:document.getElementById('d-font')?.value||'Inter'}
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVE ACTION
// ─────────────────────────────────────────────────────────────────────────────
async function saveAction() {
  const type=document.getElementById('a-type')?.value; const title=document.getElementById('a-title')?.value?.trim(); const btn=document.getElementById('modal-confirm-btn');
  const vMsg=document.getElementById('v-msg'); vMsg.style.display='none'; vMsg.style.color='#dc2626';
  // v3.6.1: gate — block ab_split save if tier insufficient (catches any path that bypassed the UI gate)
  if (type==='ab_split' && !canUse('ab_split')) { trackGateHit('ab_split'); showUpgradeModal('ab_split'); return; }
  if (!title) { showError('Internal label is required.'); return; }
  // ── v3.3.1: soft validation — warn on first click, allow save on second ──
  if(type==='email'&&state._softWarnType!=='email'){
    const emailVal=document.getElementById('d-to')?.value?.trim();
    if(emailVal&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)){
      const el=document.getElementById('d-to'); if(el){el.style.borderColor='#f59e0b';el.style.boxShadow='0 0 0 3px rgba(245,158,11,0.15)';}
      vMsg.style.display='block'; vMsg.style.color='#92400e';
      vMsg.innerText='\u26a0\ufe0f This doesn\'t look like a valid email address. Click "Save Anyway" to confirm.';
      btn.innerText='Save Anyway'; state._softWarnType='email'; return;
    }
  }
  if(type==='phone'&&state._softWarnType!=='phone'){
    const phoneVal=document.getElementById('d-number')?.value?.trim();
    if(phoneVal&&!/^\+?[\d\s\-().]{6,}$/.test(phoneVal)){
      const el=document.getElementById('d-number'); if(el){el.style.borderColor='#f59e0b';el.style.boxShadow='0 0 0 3px rgba(245,158,11,0.15)';}
      vMsg.style.display='block'; vMsg.style.color='#92400e';
      vMsg.innerText='\u26a0\ufe0f This doesn\'t look like a valid phone number. Click "Save Anyway" to confirm.';
      btn.innerText='Save Anyway'; state._softWarnType='phone'; return;
    }
  }
  if (type==='url') {
    const urlVal=document.getElementById('d-url')?.value?.trim();
    if (!urlVal) { showError('A URL is required for this action type.'); return; }
    if (!/^https?:\/\/.+/.test(urlVal)) { showError('URL must start with http:// or https://'); return; }
  }
  if (type==='vcard'&&(!document.getElementById('d-fn')?.value?.trim()||!document.getElementById('d-ln')?.value?.trim())) { showError('First and last name are required.'); return; }
  if (type==='wifi'&&!document.getElementById('d-ssid')?.value?.trim()) { showError('Network name (SSID) is required.'); return; }
  if (type==='ab_split') {
    const aId=document.getElementById('d-action-a')?.value; const bId=document.getElementById('d-action-b')?.value;
    if (!aId||!bId) { showError('Select both Variant A and Variant B actions.'); return; }
    if (aId===bId)  { showError('Variant A and Variant B must be different actions.'); return; }
  }
  const payload=buildPayload(type);
  // ── v2.8.2: encrypt WiFi password before saving ──
  if (type==='wifi' && payload.password && state.profileId) {
    payload.password = await encryptWifiPassword(payload.password, state.profileId);
  } btn.innerText='Saving…'; btn.disabled=true;
  try {
    // ── v2.8.3: destructure result to catch Supabase errors (400/422) that don't throw ──
    let rpcResult;
    if (state.editTarget)                             { rpcResult = await db.rpc('update_action',{target_action_id:state.editTarget.id,shopify_user_id:SHOPIFY_GID,action_title:title,payload_data:payload}); }
    else if (state.activeAssignType==='porter')       { rpcResult = await db.rpc('assign_action_to_porter',{target_porter_id:state.activeAssignId,shopify_user_id:SHOPIFY_GID,action_title:title,action_type:type,payload_data:payload}); }
    else if (state.activeAssignType==='group')        { rpcResult = await db.rpc('assign_action_to_group',{target_group_id:state.activeAssignId,shopify_user_id:SHOPIFY_GID,action_title:title,action_type:type,payload_data:payload}); }
    else                                              { rpcResult = await db.rpc('save_action_to_library',{shopify_user_id:SHOPIFY_GID,action_title:title,action_type:type,payload_data:payload}); }
    if (rpcResult?.error) throw new Error(rpcResult.error.message || rpcResult.error.details || JSON.stringify(rpcResult.error));
    // v3.4.1: assign_action_to_group RPC doesn't clear use_route — patch it now
    if (state.activeAssignType==='group' && state.activeAssignId) {
      await db.from('porter_groups').update({use_route:false,route_id:null}).eq('id',state.activeAssignId);
    }
    state.imageData={pfp:'',logo:'',banner:''}; closeModal(); loadData();
  } catch(err) { showError(`Save failed: ${err.message||'Unknown error'}`); btn.disabled=false; btn.innerText='Confirm'; }
}

// ─────────────────────────────────────────────────────────────────────────────
// EDIT ACTION
// ─────────────────────────────────────────────────────────────────────────────
function openEdit(actionId) {
  const action=state.actions.find(a=>a.id===actionId); if(!action) return;
  state.editTarget={id:action.id}; state.imageData={pfp:action.primary_data?.profile_picture_url||'',logo:action.primary_data?.company_logo_url||'',banner:action.primary_data?.banner_image_url||''};
  document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Edit Action'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<input type="hidden" id="a-type" value="${action.type}"><div style="display:inline-block;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:800;background:#f0f4ff;color:#4338ca;margin-bottom:12px;">${action.type.toUpperCase()}</div><label class="field-label">Internal Label</label><input id="a-title" class="charis-input" value="${action.title}"><div id="dynamic-zone"></div>`;
  renderDynamicFields(action.primary_data); document.getElementById('modal-confirm-btn').onclick=saveAction;
  document.getElementById('charis-modal').classList.add('active');
}

// ─────────────────────────────────────────────────────────────────────────────
// SCHEDULES CRUD
// ─────────────────────────────────────────────────────────────────────────────
function openNewSchedule() {
  // v3.1.0: free tier limited to 1 schedule
  if (!canUse('sched_unlimited') && state.schedules.length >= 1) {
    showUpgradeModal('sched_unlimited',
      'The Starter plan includes <strong>1 schedule</strong>. Upgrade to <strong style="color:#2563eb;">Essential</strong> for unlimited time-based schedules across all your porters.');
    return;
  }
  state.editTarget=null; document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Create New Schedule'; document.getElementById('modal-subtitle').innerText='';
  renderScheduleForm({}); document.getElementById('modal-confirm-btn').onclick=saveSchedule;
  document.getElementById('charis-modal').classList.add('active');
}

function openEditSchedule(schedId) {
  const s=state.schedules.find(x=>x.id===schedId); if(!s) return;
  state.editTarget={id:schedId,type:'schedule'}; document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Edit Schedule'; document.getElementById('modal-subtitle').innerText='';
  renderScheduleForm(s); document.getElementById('modal-confirm-btn').onclick=saveSchedule;
  document.getElementById('charis-modal').classList.add('active');
}

function renderScheduleForm(s) {
  const days=s.day_of_week||[];
  document.getElementById('modal-fields').innerHTML=`
    <label class="field-label">Schedule Name</label>
    <input type="text" id="sch-name" class="charis-input" placeholder="e.g. Lunch Specials" value="${s.name||''}">
    <label class="field-label">Timezone <span class="help-icon" data-tip="All time windows evaluated in this timezone.">?</span></label>
    <select id="sch-tz" class="charis-input">${buildTimezoneOpts(s.timezone)}</select>
    <hr class="hr-label">
    <label class="field-label">Active Days</label>
    <div class="day-selector">${[['1','M'],['2','T'],['3','W'],['4','T'],['5','F'],['6','S'],['0','S']].map(([v,l])=>`<div class="day-box"><input type="checkbox" id="dw-${v}" value="${v}" ${days.includes(parseInt(v))?'checked':''}><label for="dw-${v}">${l}</label></div>`).join('')}</div>
    <hr class="hr-label">
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;"><div class="field-label" style="margin-bottom:0;">Date Range (optional)</div></div>
    <div class="form-row">
      <div style="flex:1;"><label class="field-label">Active From</label><input type="date" id="sch-start-date" class="charis-input" value="${s.start_date||''}"></div>
      <div style="flex:1;"><label class="field-label">Active Until</label><input type="date" id="sch-end-date" class="charis-input" value="${s.end_date||''}"></div>
    </div>
    <hr class="hr-label">
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;"><div class="field-label" style="margin-bottom:0;">Time Windows</div></div>
    <div id="windows-container"></div>
    <button type="button" class="add-entry-btn" onclick="addScheduleWindow(document.getElementById('windows-container'))">+ Add Time Window</button>
    <hr class="hr-label">
    <div class="field-label">Scheduled Action</div>
    <select id="sch-action" class="charis-input"><option value="">— Select Action —</option>${buildActionOpts(s.action_id)}</select>
    <div class="field-label">Fallback Action (outside window)</div>
    <select id="sch-fallback" class="charis-input"><option value="">— None (use tag default) —</option>${buildActionOpts(s.fallback_action?.id)}</select>
    <div class="field-label">Override Action (emergency)</div>
    <select id="sch-override" class="charis-input"><option value="">— None —</option>${buildActionOpts(s.override_action?.id)}</select>`;
  const winContainer=document.getElementById('windows-container');
  const windows=s.schedule_windows||[];
  if (windows.length>0) windows.forEach(w=>addScheduleWindow(winContainer,w)); else addScheduleWindow(winContainer);
}

async function saveSchedule() {
  const name=document.getElementById('sch-name')?.value?.trim(); const timezone=document.getElementById('sch-tz')?.value||'UTC';
  const actionId=document.getElementById('sch-action')?.value||null; const fallbackId=document.getElementById('sch-fallback')?.value||null; const overrideId=document.getElementById('sch-override')?.value||null;
  const startDate=document.getElementById('sch-start-date')?.value||null; const endDate=document.getElementById('sch-end-date')?.value||null;
  const days=Array.from(document.querySelectorAll('.day-box input:checked')).map(i=>parseInt(i.value));
  const windows=Array.from(document.querySelectorAll('#windows-container .window-row')).map(row=>({start_time:row.querySelector('.win-start')?.value,end_time:row.querySelector('.win-end')?.value,label:row.querySelector('.win-label')?.value?.trim()||null})).filter(w=>w.start_time&&w.end_time);
  document.getElementById('v-msg').style.display='none';
  if (!name) { showError('Schedule name is required.'); return; }
  if (!days.length) { showError('Select at least one active day.'); return; }
  if (!windows.length) { showError('At least one time window is required.'); return; }
  // v3.6.1: BUG-005 fix — Scheduled Action is required
  if (!actionId) { showError('Select a Scheduled Action to serve during the active time window.'); return; }
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Saving…'; btn.disabled=true;
  try {
    await db.rpc('save_schedule',{p_shopify_user_id:SHOPIFY_GID,p_schedule_id:state.editTarget?.type==='schedule'?state.editTarget.id:null,p_name:name,p_timezone:timezone,p_day_of_week:days,p_start_date:startDate,p_end_date:endDate,p_action_id:actionId,p_fallback_action_id:fallbackId,p_override_action_id:overrideId,p_override_active:false,p_windows:windows});
    closeModal(); loadData();
  } catch(err) { showError(`Save failed: ${err.message}`); btn.disabled=false; btn.innerText='Confirm'; }
}

// ─────────────────────────────────────────────────────────────────────────────
// SEQUENCES CRUD  (v10.3 new)
// ─────────────────────────────────────────────────────────────────────────────
function openNewSequence() {
  state.editTarget=null; document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Create New Sequence'; document.getElementById('modal-subtitle').innerText='';
  renderSequenceForm({}); document.getElementById('modal-confirm-btn').onclick=saveSequence;
  document.getElementById('charis-modal').classList.add('active');
}

function openEditSequence(seqId) {
  const seq=state.sequences.find(s=>s.id===seqId); if(!seq) return;
  state.editTarget={id:seqId,type:'sequence'}; document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Edit Sequence'; document.getElementById('modal-subtitle').innerText='';
  renderSequenceForm(seq); document.getElementById('modal-confirm-btn').onclick=saveSequence;
  document.getElementById('charis-modal').classList.add('active');
}

function renderSequenceForm(seq) {
  document.getElementById('modal-fields').innerHTML=`
    <label class="field-label">Sequence Name</label>
    <input type="text" id="seq-name" class="charis-input" placeholder="e.g. Product Demo Flow" value="${seq.name||''}">
    <label class="field-label">Mode <span class="help-icon" data-tip="Loop: cycles forever. Linear: stops on last step. One-Shot: deactivates after the final scan. Random: serves a random step on every scan.">?</span></label>
    <select id="seq-mode" class="charis-input">
      <option value="loop"     ${(seq.mode||'loop')==='loop'?'selected':''}>Loop — repeats from step 1</option>
      <option value="linear"   ${seq.mode==='linear'?'selected':''}   >Linear — stays on last step</option>
      <option value="one-shot" ${seq.mode==='one-shot'?'selected':''} >One-Shot — deactivates after last step</option>
      <option value="random"   ${seq.mode==='random'?'selected':''}   >Random — serves a random step each scan</option>
    </select>
    <label class="field-label">Tracking <span class="help-icon" data-tip="Global: one shared pointer for all scanners. Per Device: each device independently progresses through the sequence.">?</span></label>
    <select id="seq-tracking" class="charis-input">
      <option value="global"     ${(seq.tracking||'global')==='global'?'selected':''}    >Global — everyone shares one position</option>
      <option value="per_device" ${seq.tracking==='per_device'?'selected':''}             >Per Device — each device starts at step 1</option>
    </select>
    <hr class="hr-label">
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
      <div class="field-label" style="margin-bottom:0;">Steps</div>
      <span class="help-icon" data-tip="Each scan advances to the next step in order.">?</span>
    </div>
    <div id="seq-steps-container"></div>
    <button type="button" class="add-entry-btn" id="seq-add-step-btn" onclick="addSequenceStep()">+ Add Step</button>
    <div id="seq-step-limit-notice" style="display:none;margin-top:6px;">${lockChip('seq_unlimited')} <span style="font-size:12px;color:#92400e;margin-left:4px;">Free accounts are limited to 3 steps. <a href="#" style="color:#7c3aed;font-weight:700;">Upgrade to Essential →</a></span></div>`;
  const container=document.getElementById('seq-steps-container');
  const steps=seq.action_ids||[];
  if (steps.length>0) steps.forEach(id=>addSequenceStep(id));
  else addSequenceStep();
}

function addSequenceStep(prefillActionId) {
  const container=document.getElementById('seq-steps-container'); if(!container) return;
  const currentCount = container.querySelectorAll('.seq-step-row').length;
  // v3.1.0: enforce 3-step limit for free tier (allow pre-filling existing steps past the limit on edit)
  if (!prefillActionId && !canUse('seq_unlimited') && currentCount >= 3) {
    const notice = document.getElementById('seq-step-limit-notice');
    const btn    = document.getElementById('seq-add-step-btn');
    if (notice) notice.style.display = 'block';
    if (btn)    btn.style.display    = 'none';
    trackGateHit('seq_unlimited');
    return;
  }
  const idx=currentCount+1;
  const row=document.createElement('div'); row.className='window-row seq-step-row';
  row.innerHTML=`
    <div style="min-width:20px;font-size:11px;font-weight:800;color:var(--charis-secondary);padding-top:26px;text-align:right;">${idx}</div>
    <div style="flex:1;">
      <label class="field-label">Action</label>
      <select class="charis-input seq-step-action" style="margin-bottom:0;">
        <option value="">— Select Action —</option>
        ${state.actions.map(a=>`<option value="${a.id}" ${prefillActionId===a.id?'selected':''}>${a.type==='vcard'?'VCARD':a.type.toUpperCase()}: ${a.title}</option>`).join('')}
      </select>
    </div>
    <button type="button" class="remove-entry" onclick="this.parentElement.remove()" style="margin-top:22px;"><span class='mi mi-sm'>close</span></button>`;
  container.appendChild(row);
}

async function saveSequence() {
  const name     = document.getElementById('seq-name')?.value?.trim();
  const mode     = document.getElementById('seq-mode')?.value     || 'loop';
  const tracking = document.getElementById('seq-tracking')?.value || 'global';
  const actionIds = Array.from(document.querySelectorAll('.seq-step-action')).map(s=>s.value).filter(Boolean);
  document.getElementById('v-msg').style.display='none';
  if (!name)           { showError('Sequence name is required.'); return; }
  if (!actionIds.length){ showError('Add at least one step.'); return; }
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Saving…'; btn.disabled=true;
  try {
    await db.rpc('save_sequence',{
      p_shopify_user_id: SHOPIFY_GID,
      p_sequence_id:     state.editTarget?.type==='sequence'?state.editTarget.id:null,
      p_name:            name,
      p_mode:            mode,
      p_action_ids:      actionIds,
      p_tracking:        tracking
    });
    closeModal(); loadData();
  } catch(err) { showError(`Save failed: ${err.message}`); btn.disabled=false; btn.innerText='Confirm'; }
}

async function resetSequence(seqId) {
  try {
    await db.rpc('reset_sequence',{p_sequence_id:seqId,p_shopify_user_id:SHOPIFY_GID});
    loadData(); showToast('Sequence reset to step 1 ✓');
  } catch(err) { showToast(`Reset failed: ${err.message}`); }
}

function confirmDeleteSequence(id, name) {
  document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Delete Sequence?'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${name}</div><p style="color:var(--charis-danger);font-size:12px;margin-top:10px;">Tags and groups using this sequence will have sequencing disabled.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.className='btn-charis btn-danger'; btn.innerText='Delete Sequence';
  btn.onclick=async()=>{ btn.innerText='Deleting…';btn.disabled=true; try{await db.rpc('delete_sequence',{p_sequence_id:id,p_shopify_user_id:SHOPIFY_GID});closeModal();loadData();}catch(err){showError(`Delete failed: ${err.message}`);btn.disabled=false;btn.innerText='Delete Sequence';} };
  document.getElementById('charis-modal').classList.add('active');
}

// ─────────────────────────────────────────────────────────────────────────────
// GROUP CRUD
// ─────────────────────────────────────────────────────────────────────────────
function openNewGroup() {
  document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Create New Group'; document.getElementById('modal-subtitle').innerText='Group tags together and assign one action to all of them.';
  document.getElementById('modal-fields').innerHTML=`<label class="field-label">Group Name</label><input id="n-gn" class="charis-input" placeholder="e.g. Conference Room Tags">`;
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Create Group';
  btn.onclick=async()=>{ const name=document.getElementById('n-gn')?.value?.trim(); if(!name){const el=document.getElementById('n-gn');if(el){el.style.borderColor='#ef4444';el.style.boxShadow='0 0 0 3px rgba(239,68,68,0.15)';}showError('Group name is required.');return;} btn.innerText='Creating…';btn.disabled=true; try{await db.rpc('create_porter_group',{group_name:name,shopify_user_id:SHOPIFY_GID});closeModal();loadData();}catch(err){showError(`Failed: ${err.message}`);btn.disabled=false;btn.innerText='Create Group';} };
  document.getElementById('charis-modal').classList.add('active');
}

function openBatchMove() {
  if (!state.selected.length) return;
  const opts=state.groups.map(g=>`<option value="${g.id}">${g.name}</option>`).join('');
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Move to Group'; document.getElementById('modal-subtitle').innerText=`Moving ${state.selected.length} tag(s).`;
  document.getElementById('modal-fields').innerHTML=`<label class="field-label">Select Group</label><select id="t-gid" class="charis-input"><option value="">— Create New —</option>${opts}</select><label class="field-label">Or Create New Group</label><input id="n-gn" class="charis-input" placeholder="New group name">`;
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Move Tags';
  btn.onclick=async()=>{ btn.innerText='Moving…';btn.disabled=true; try{ let gid=document.getElementById('t-gid')?.value||null; const newName=document.getElementById('n-gn')?.value?.trim(); if(!gid&&newName){const{data}=await db.rpc('create_porter_group',{group_name:newName,shopify_user_id:SHOPIFY_GID});gid=data;} if(!gid){showError('Select or name a group.');btn.disabled=false;btn.innerText='Move Tags';return;} await db.rpc('batch_move_to_group',{porter_ids:state.selected,target_group_id:gid});clearSelection();closeModal();loadData();}catch(err){showError(`Failed: ${err.message}`);btn.disabled=false;btn.innerText='Move Tags';} };
  document.getElementById('charis-modal').classList.add('active');
}

function openNewLibraryAction() {
  state.activeAssignType=null; state.activeAssignId=null; state.editTarget=null; state.imageData={pfp:'',logo:'',banner:''};
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Add to Library'; document.getElementById('modal-subtitle').innerText='Create a reusable action. Assign it to tags later.';
  // v3.6.1: gate — A/B Split requires Professional tier; disable option if below tier
  const abOpt = canUse('ab_split')
    ? `<option value="ab_split">A/B Split Test</option>`
    : `<option value="ab_split" disabled class="tier-locked">A/B Split Test — Professional</option>`;
  document.getElementById('modal-fields').innerHTML=`${!canUse('ab_split') ? `<div class="tier-upgrade-banner"><span class="mi mi-sm">lock</span> A/B Split Testing requires Professional. <a href="#">Upgrade →</a></div>` : ''}<label class="field-label">Action Type</label><select id="a-type" class="charis-input" onchange="renderDynamicFields()"><option value="url">Website URL</option><option value="vcard">Contact Card</option><option value="phone">Phone Number</option><option value="sms">SMS Message</option><option value="email">Email</option><option value="wifi">WiFi Credentials</option><option value="webhook">Webhook</option><option value="message">Rich Text Message</option><option value="html">HTML Page</option><option value="form">Lead Capture Form</option>${abOpt}</select><label class="field-label">Internal Label</label><input type="text" id="a-title" class="charis-input" placeholder="e.g. Office WiFi..."><div id="dynamic-zone"></div>`;
  renderDynamicFields(); document.getElementById('modal-confirm-btn').onclick=saveAction;
  document.getElementById('charis-modal').classList.add('active');
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE CONFIRMATIONS
// ─────────────────────────────────────────────────────────────────────────────
function confirmDeleteAction(id, title) {
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Remove from Library?'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${title}</div><p style="color:var(--charis-danger);font-size:12px;margin-top:10px;"><span class='mi'>warning</span> This will unassign the action from all tags and groups that use it.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.className='btn-charis btn-danger'; btn.innerText='Delete';
  btn.onclick=async()=>{ btn.innerText='Deleting…';btn.disabled=true; await db.from('porters').update({individual_action_id:null}).eq('individual_action_id',id); await db.from('porter_groups').update({active_action_id:null}).eq('active_action_id',id); await db.from('actions').delete().eq('id',id); closeModal();loadData(); };
  document.getElementById('charis-modal').classList.add('active');
}

function confirmDeleteGroup(id, name) {
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Delete Group?'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${name}</div><p style="color:var(--charis-danger);font-size:12px;margin-top:10px;">Tags in this group will be ungrouped. The action stays in your library.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.className='btn-charis btn-danger'; btn.innerText='Delete Group';
  btn.onclick=async()=>{ btn.innerText='Deleting…';btn.disabled=true; await db.from('porters').update({group_id:null}).eq('group_id',id); await db.from('porter_groups').delete().eq('id',id); closeModal();loadData(); };
  document.getElementById('charis-modal').classList.add('active');
}

function confirmDeleteSchedule(id, name) {
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Delete Schedule?'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${name}</div><p style="color:var(--charis-danger);font-size:12px;margin-top:10px;">Tags or groups using this schedule will have scheduling disabled.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.className='btn-charis btn-danger'; btn.innerText='Delete Schedule';
  btn.onclick=async()=>{ btn.innerText='Deleting…';btn.disabled=true; try{await db.rpc('delete_schedule',{p_schedule_id:id,p_shopify_user_id:SHOPIFY_GID});closeModal();loadData();}catch(err){showError(`Delete failed: ${err.message}`);btn.disabled=false;btn.innerText='Delete Schedule';} };
  document.getElementById('charis-modal').classList.add('active');
}

function confirmUngroup(porterId) {
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Ungroup Tag?';
  document.getElementById('modal-fields').innerHTML=`<p style="color:var(--charis-secondary);">This tag will no longer follow its group's active action.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Ungroup';
  btn.onclick=async()=>{ await db.rpc('ungroup_porter',{target_porter_id:porterId}); closeModal();loadData(); };
  document.getElementById('charis-modal').classList.add('active');
}

// ─────────────────────────────────────────────────────────────────────────────
// RENAME / BATCH
// ─────────────────────────────────────────────────────────────────────────────
function renamePorter(id, currentName) {
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Rename Tag'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<label class="field-label">Nickname</label><input id="r-n" class="charis-input" value="${currentName}">`;
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Save';
  btn.onclick=async()=>{ try{ btn.innerText='Saving…';btn.disabled=true; await db.rpc('rename_porter',{target_id:id,new_name:document.getElementById('r-n').value}); closeModal();loadData(); }catch(err){showError(`Save failed: ${err.message}`);btn.disabled=false;btn.innerText='Save';} };
  document.getElementById('charis-modal').classList.add('active');
}

function togglePorterCheck(porterId) {
  const cb=document.querySelector(`.porter-cb[value="${porterId}"]`);
  if (cb) { cb.checked=!cb.checked; updateBatchBar(); }
}

function attachBatchListeners() {
  const master=document.getElementById('master-cb'); const cbs=document.querySelectorAll('.porter-cb');
  if (master) master.onchange=()=>{ cbs.forEach(c=>c.checked=master.checked); updateBatchBar(); };
  cbs.forEach(c=>c.onchange=updateBatchBar);
}

function updateBatchBar() {
  const prev=state.selected.length;
  state.selected=Array.from(document.querySelectorAll('.porter-cb:checked')).map(c=>c.value);
  document.getElementById('batch-bar').classList.toggle('active',state.selected.length>0);
  document.getElementById('batch-count').innerText=`${state.selected.length} Selected`;
  if ((prev===0)!==(state.selected.length===0)) renderGroups();
}

function clearSelection() {
  const master=document.getElementById('master-cb'); if(master) master.checked=false;
  document.querySelectorAll('.porter-cb').forEach(c=>c.checked=false);
  const had=state.selected.length>0; state.selected=[];
  document.getElementById('batch-bar').classList.remove('active');
  document.getElementById('batch-count').innerText='0 Selected';
  if (had) renderGroups();
}

async function batchUngroup() {
  if (!state.selected.length) return;
  await db.rpc('batch_ungroup_porters',{porter_ids:state.selected}); clearSelection(); loadData();
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN  (v3.5.0 — founder legacy, owner identity, deactivation)
// ─────────────────────────────────────────────────────────────────────────────

// Active filter: 'all' | 'claimed' | 'unclaimed' | 'deactivated'
state.adminFilter      = 'all';
state.adminSearch      = '';
state.adminOwnerSearch = '';
state.adminSort        = 'created_desc';
// NOTE: get_admin_porter_list RPC returns `porter_id` (not `id`) to avoid
//       PostgreSQL ambiguity with RETURNS TABLE out-param naming.
state.adminPorters  = [];
state.adminSelected = new Set(); // porter_ids selected for QR export

async function renderAdmin() {
  if (!state.isAdmin) return;

  // Direct two-query approach — bypasses PL/pgSQL auth.uid() issues entirely.
  // The admin_select_any_porter RLS policy restricts to admin users server-side.
  const { data: porters, error: pErr } = await db.from('porters')
    .select('id,identifier,nickname,owner_id,founder_legacy,is_deactivated,scan_count,scan_limit,expires_at,created_at')
    .order('created_at', { ascending: false });

  if (pErr) { showError(`Admin load failed: ${pErr.message}`); return; }

  // Fetch owner emails for all claimed porters in one shot
  const ownerIds = [...new Set((porters || []).filter(p => p.owner_id).map(p => p.owner_id))];
  let emailMap = {};
  if (ownerIds.length) {
    const { data: owners } = await db.from('profiles').select('id,email').in('id', ownerIds);
    (owners || []).forEach(o => { emailMap[o.id] = o.email; });
  }

  // Merge into a flat list with porter_id + owner_email
  state.adminPorters = (porters || []).map(p => ({
    porter_id:      p.id,
    identifier:     p.identifier,
    nickname:       p.nickname,
    owner_id:       p.owner_id,
    owner_email:    emailMap[p.owner_id] || null,
    founder_legacy: p.founder_legacy,
    is_deactivated: p.is_deactivated,
    scan_count:     p.scan_count,
    scan_limit:     p.scan_limit,
    expires_at:     p.expires_at,
    created_at:     p.created_at
  }));

  _renderAdminList();
  renderGateAnalytics();
}

function _renderAdminList() {
  const allPorters  = state.adminPorters;
  const claimed     = allPorters.filter(p => p.owner_id);
  const unclaimed   = allPorters.filter(p => !p.owner_id);
  const deactivated = allPorters.filter(p => p.is_deactivated);

  // Stat cards always reflect full list
  document.getElementById('admin-stat-total').innerText       = allPorters.length;
  document.getElementById('admin-stat-claimed').innerText     = claimed.length;
  document.getElementById('admin-stat-unclaimed').innerText   = unclaimed.length;
  document.getElementById('admin-stat-deactivated').innerText = deactivated.length;

  // Filter button highlight
  document.querySelectorAll('.admin-filter-btn').forEach(b =>
    b.classList.toggle('active', b.dataset.filter === state.adminFilter)
  );

  // Apply tab filter
  let filtered = allPorters;
  if (state.adminFilter === 'claimed')     filtered = claimed;
  if (state.adminFilter === 'unclaimed')   filtered = unclaimed;
  if (state.adminFilter === 'deactivated') filtered = deactivated;

  // Apply tag ID / nickname search
  const q = (state.adminSearch || '').trim().toLowerCase();
  if (q) {
    filtered = filtered.filter(p =>
      (p.identifier || '').toLowerCase().includes(q) ||
      (p.nickname   || '').toLowerCase().includes(q)
    );
  }

  // Apply owner email search (separate input)
  const ownerQ = (state.adminOwnerSearch || '').trim().toLowerCase();
  if (ownerQ) {
    filtered = filtered.filter(p =>
      (p.owner_email || '').toLowerCase().includes(ownerQ)
    );
  }

  // Apply sort
  const sort = state.adminSort || 'created_desc';
  filtered = [...filtered].sort((a, b) => {
    if (sort === 'created_desc')  return new Date(b.created_at||0) - new Date(a.created_at||0);
    if (sort === 'created_asc')   return new Date(a.created_at||0) - new Date(b.created_at||0);
    if (sort === 'scans_desc')    return (b.scan_count||0) - (a.scan_count||0);
    if (sort === 'identifier_asc')return (a.identifier||'').localeCompare(b.identifier||'');
    return 0;
  });

  const hasSearch = q || ownerQ;
  if (!filtered.length) {
    document.getElementById('admin-porter-list').innerHTML =
      `<p style="padding:20px;color:#888;text-align:center;">${hasSearch ? 'No results match your search.' : 'No porters match this filter.'}</p>`;
    return;
  }

  // Select-all row
  const allFilteredIds  = filtered.map(p => p.porter_id || p.id);
  const allChecked      = allFilteredIds.length > 0 && allFilteredIds.every(id => state.adminSelected.has(id));
  const someChecked     = !allChecked && allFilteredIds.some(id => state.adminSelected.has(id));
  let html = `
<div style="display:flex;align-items:center;gap:10px;padding:8px 14px;border-bottom:1.5px solid #f0f0f0;background:#fafafa;border-radius:12px 12px 0 0;">
  <input type="checkbox" id="admin-select-all-cb"
    ${allChecked ? 'checked' : ''}
    style="width:16px;height:16px;cursor:pointer;accent-color:#4f46e5;"
    onchange="toggleAdminSelectAll(this.checked)">
  <label for="admin-select-all-cb" style="font-size:12px;color:#666;cursor:pointer;user-select:none;">
    ${allChecked ? 'Deselect all' : someChecked ? `${allFilteredIds.filter(id=>state.adminSelected.has(id)).length} selected — select all ${filtered.length}` : `Select all ${filtered.length}`}
  </label>
  ${state.adminSelected.size > 0 ? `<span style="margin-left:auto;font-size:11px;font-weight:700;color:#4f46e5;">${state.adminSelected.size} total selected</span>` : ''}
</div>`;

  filtered.forEach(p => {
    // porter_id is the canonical ID field from the RPC (renamed to avoid PG ambiguity)
    const pid           = p.porter_id || p.id;
    const isClaimed     = !!p.owner_id;
    const isDeactivated = !!p.is_deactivated;
    const isFounder     = !!p.founder_legacy;
    const isSelected    = state.adminSelected.has(pid);
    // Show email if available, fall back to nothing (unclaimed)
    const ownerEmail    = p.owner_email || null;

    const claimBadge   = isClaimed
      ? `<span class="admin-badge admin-badge-claimed">✓ Claimed</span>`
      : `<span class="admin-badge admin-badge-unclaimed">○ Unclaimed</span>`;
    const deactBadge   = isDeactivated ? `<span class="admin-badge admin-badge-deactivated">🔒 Off</span>` : '';
    const founderBadge = isFounder     ? `<span class="admin-badge admin-badge-founder">⭐ Founder</span>` : '';

    const ownerLine = ownerEmail
      ? `<div class="admin-owner-line" title="${p.owner_id||''}">👤 ${ownerEmail}</div>`
      : `<div class="admin-owner-line" style="color:#bbb;">Unclaimed</div>`;

    const scanCount = p.scan_count || 0;
    const scanPill  = `<span class="admin-scan-pill" title="Total scans">⚡ ${scanCount.toLocaleString()}</span>`;

    html += `
<div class="admin-tag-row" id="admin-row-${pid}" style="${isSelected ? 'background:rgba(79,70,229,0.06);border-left:3px solid #4f46e5;' : ''}">
  <div style="display:flex;align-items:center;gap:10px;">
    <input type="checkbox" data-pid="${pid}" class="admin-select-cb"
      ${isSelected ? 'checked' : ''}
      style="width:16px;height:16px;cursor:pointer;accent-color:#4f46e5;flex-shrink:0;"
      onchange="toggleAdminSelect('${pid}')">
    <div style="flex:1;">
      <div class="admin-row-main">
        <div>
          <div class="admin-tag-id" onclick="copyToClipboard('${p.identifier}')" style="cursor:pointer;" title="Click to copy">${p.identifier}</div>
          <div class="admin-tag-nick">${p.nickname || 'Unnamed'}</div>
          ${ownerLine}
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;">
          <div class="admin-badge-row">${claimBadge}${founderBadge}${deactBadge}</div>
          ${scanPill}
        </div>
      </div>
      <div class="admin-row-actions">
        <button class="admin-action-btn ${isFounder ? 'admin-btn-active' : ''}"
          onclick="adminToggleFounder('${pid}')"
          title="${isFounder ? 'Remove Founder Legacy' : 'Grant Founder Legacy'}">
          ${isFounder ? '⭐ Founder' : '☆ Founder'}
        </button>
        <button class="admin-action-btn ${isDeactivated ? 'admin-btn-danger-active' : 'admin-btn-danger'}"
          onclick="adminToggleDeactivate('${pid}')"
          title="${isDeactivated ? 'Reactivate this porter' : 'Deactivate this porter'}">
          ${isDeactivated ? '▶ Reactivate' : '⏸ Deactivate'}
        </button>
      </div>
    </div>
  </div>
</div>`;
  });

  document.getElementById('admin-porter-list').innerHTML = html;
  _updateAdminSelectionBar();
}

function setAdminFilter(filter) {
  state.adminFilter = filter;
  _renderAdminList();
}

function setAdminSearch(val) {
  state.adminSearch = val;
  _renderAdminList();
}

function setAdminOwnerSearch(val) {
  state.adminOwnerSearch = val;
  _renderAdminList();
}

function setAdminSort(val) {
  state.adminSort = val;
  _renderAdminList();
}

// ── Admin QR selection helpers ────────────────────────────────────────────────
function toggleAdminSelect(pid) {
  if (state.adminSelected.has(pid)) {
    state.adminSelected.delete(pid);
  } else {
    state.adminSelected.add(pid);
  }
  // Update row highlight + header checkbox without full re-render
  const row = document.getElementById(`admin-row-${pid}`);
  if (row) {
    const sel = state.adminSelected.has(pid);
    row.style.background   = sel ? 'rgba(79,70,229,0.06)' : '';
    row.style.borderLeft   = sel ? '3px solid #4f46e5'    : '';
  }
  _updateAdminSelectionBar();
  // Sync select-all checkbox state
  const allCb = document.getElementById('admin-select-all-cb');
  if (allCb) {
    const allIds = [...document.querySelectorAll('.admin-select-cb')].map(c => c.dataset.pid);
    allCb.checked       = allIds.length > 0 && allIds.every(id => state.adminSelected.has(id));
    allCb.indeterminate = !allCb.checked && allIds.some(id => state.adminSelected.has(id));
  }
}

function toggleAdminSelectAll(checked) {
  document.querySelectorAll('.admin-select-cb').forEach(cb => {
    const pid = cb.dataset.pid;
    if (checked) { state.adminSelected.add(pid); }
    else         { state.adminSelected.delete(pid); }
    cb.checked = checked;
    const row = document.getElementById(`admin-row-${pid}`);
    if (row) {
      row.style.background = checked ? 'rgba(79,70,229,0.06)' : '';
      row.style.borderLeft = checked ? '3px solid #4f46e5'    : '';
    }
  });
  _updateAdminSelectionBar();
}

function _updateAdminSelectionBar() {
  const count = state.adminSelected.size;
  let bar = document.getElementById('admin-qr-selection-bar');
  if (!bar) {
    bar = document.createElement('div');
    bar.id = 'admin-qr-selection-bar';
    bar.style.cssText = [
      'position:sticky','bottom:16px','background:#4f46e5','color:#fff',
      'border-radius:14px','padding:12px 20px','display:flex','align-items:center',
      'justify-content:space-between','box-shadow:0 4px 20px rgba(79,70,229,0.35)',
      'margin-top:14px','transition:opacity 0.2s,transform 0.2s','z-index:50'
    ].join(';');
    const adminView = document.getElementById('view-admin');
    if (adminView) adminView.appendChild(bar);
  }
  if (count === 0) {
    bar.style.display = 'none';
    return;
  }
  bar.style.display = 'flex';
  bar.innerHTML = `
    <span style="font-weight:700;font-size:14px;">⬛ ${count} tag${count !== 1 ? 's' : ''} selected</span>
    <div style="display:flex;gap:8px;align-items:center;">
      <button onclick="state.adminSelected.clear();_renderAdminList();"
        style="padding:7px 14px;border:1.5px solid rgba(255,255,255,0.55);border-radius:8px;background:transparent;color:#fff;font-size:13px;cursor:pointer;font-family:inherit;font-weight:600;">
        Clear
      </button>
      <button onclick="exportAdminQrSheet()"
        style="padding:7px 20px;border:none;border-radius:8px;background:#fff;color:#4f46e5;font-size:13px;cursor:pointer;font-family:inherit;font-weight:700;white-space:nowrap;">
        🖨 Print QR Sheet
      </button>
    </div>`;
}

// ── Admin QR sheet export ─────────────────────────────────────────────────────
// Opens a print-ready 8.5×11" sheet of QR codes for the selected porters.
// QR codes are rendered inverted (white modules on black background) for
// laser engraving on black-top / white-base plastic stock.
// Layout: 6 columns × 7 rows = 42 per sheet, 30 mm per code.
function exportAdminQrSheet() {
  const selectedIds = [...state.adminSelected];
  if (!selectedIds.length) { showToast('No tags selected.'); return; }

  const porters = (state.adminPorters || []).filter(p =>
    selectedIds.includes(p.porter_id || p.id)
  );
  if (!porters.length) { showToast('Selected tag data not found.'); return; }

  showToast(`Opening QR sheet for ${porters.length} tag${porters.length !== 1 ? 's' : ''}…`);

  const tagData = porters.map(p => ({
    identifier: p.identifier,
    url:        `${TAG_BASE_URL}${p.identifier}`
  }));

  const win = window.open('', '_blank');
  if (!win) {
    showToast('Popup blocked — please allow popups for this page and try again.');
    return;
  }

  const QR_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
  const today  = new Date().toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'});

  win.document.write(`<!DOCTYPE html>
<html lang="en"><head>
<meta charset="utf-8">
<title>CharisHub QR Sheet — ${today}</title>
<script src="${QR_CDN}"><\/script>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  @page{size:letter portrait;margin:10mm;}
  body{background:#fff;font-family:'Courier New',Courier,monospace;
       -webkit-print-color-adjust:exact;print-color-adjust:exact;}

  /* ── Print toolbar (hidden on print) ── */
  #print-toolbar{
    position:fixed;top:12px;right:12px;display:flex;gap:8px;z-index:999;
    background:rgba(255,255,255,0.96);padding:10px 12px;
    border-radius:12px;box-shadow:0 2px 14px rgba(0,0,0,0.14);}
  #print-toolbar button{
    padding:9px 18px;border:none;border-radius:8px;font-size:13px;
    font-weight:700;cursor:pointer;font-family:inherit;}
  .btn-print{background:#4f46e5;color:#fff;}
  .btn-close{background:#f0f0f0;color:#333;}
  #print-toolbar small{display:block;font-size:10px;color:#888;text-align:center;margin-top:3px;}
  @media print{#print-toolbar{display:none;}}

  /* ── Sheet grid ── */
  /* 190 mm usable width (10 mm margins each side).
     5 cols × 35 mm = 175 mm + 4 gaps × 3 mm = 12 mm → 187 mm ≤ 190 mm. */
  #qr-sheet{
    width:190mm;
    display:flex;
    flex-wrap:wrap;
    gap:3mm;}

  /* ── Individual QR cell (tile + label below) ── */
  .qr-cell{
    display:flex;flex-direction:column;align-items:center;
    width:35mm;break-inside:avoid;}

  /* 35 mm black tile — this is the physical inlay footprint.
     print-color-adjust:exact forces the black background to survive PDF export. */
  .qr-tile{
    width:35mm;height:35mm;background:#000;
    display:flex;align-items:center;justify-content:center;
    -webkit-print-color-adjust:exact;print-color-adjust:exact;}

  /* 30 mm QR code centered inside the tile → 2.5 mm black border all sides.
     Canvas is hidden — we convert to <img> data URL after render (see JS below)
     so the image actually appears in PDF output. */
  .qr-wrap{
    width:30mm;height:30mm;overflow:hidden;}
  .qr-wrap img{
    width:30mm!important;height:30mm!important;display:block;
    image-rendering:crisp-edges;image-rendering:pixelated;}
  .qr-wrap canvas{display:none;}

  .qr-label{display:none;}
</style>
</head><body>

<div id="print-toolbar">
  <div>
    <button class="btn-print" onclick="window.print()">🖨 Print / Save PDF</button>
    <small>Set margins to None in print dialog</small>
  </div>
  <button class="btn-close" onclick="window.close()">✕ Close</button>
</div>

<div id="qr-sheet"></div>

<script>
(function(){
  var tags   = ${JSON.stringify(tagData)};
  var sheet  = document.getElementById('qr-sheet');

  tags.forEach(function(tag){
    var cell  = document.createElement('div');
    cell.className = 'qr-cell';

    // 35 mm black tile — matches physical inlay footprint
    var tile  = document.createElement('div');
    tile.className = 'qr-tile';

    // 30 mm QR, centered inside tile (2.5 mm black border all sides)
    var wrap  = document.createElement('div');
    wrap.className = 'qr-wrap';

    // Inverted: white modules on black background for laser engraving.
    // QRCode.js renders to a canvas; we convert it to an <img> data URL
    // below so the image actually appears in Print / Save as PDF output.
    new QRCode(wrap, {
      text:         tag.url,
      width:        300,
      height:       300,
      colorDark:    '#ffffff',
      colorLight:   '#000000',
      correctLevel: QRCode.CorrectLevel.H
    });

    tile.appendChild(wrap);
    cell.appendChild(tile);
    sheet.appendChild(cell);
  });

  // Canvas elements are skipped by most PDF renderers.
  // Wait for QRCode.js to finish drawing, then bake each canvas into an
  // <img> data URL — images always survive print/PDF export.
  setTimeout(function(){
    document.querySelectorAll('.qr-wrap').forEach(function(wrap){
      var canvas = wrap.querySelector('canvas');
      if (!canvas) return;
      var dataUrl = canvas.toDataURL('image/png');
      wrap.innerHTML = '';          // clear canvas + any extra img QRCode.js added
      var img = new Image();
      img.src = dataUrl;
      img.style.cssText = 'width:30mm!important;height:30mm!important;display:block;image-rendering:pixelated;';
      wrap.appendChild(img);
    });
  }, 400);  // 400 ms gives QRCode.js time to finish all draws
})();
<\/script>
</body></html>`);
  win.document.close();
}

function exportAdminCsv() {
  const all = state.adminPorters || [];
  if (!all.length) { showToast('No data to export.'); return; }
  const esc = v => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headers = ['Tag ID','Nickname','Owner Email','Status','Founder','Scans','Scan Limit','Expires At','Created At'];
  const rows = all.map(p => [
    esc(p.identifier),
    esc(p.nickname       || ''),
    esc(p.owner_email    || ''),
    esc(p.is_deactivated ? 'Deactivated' : p.owner_id ? 'Claimed' : 'Unclaimed'),
    esc(p.founder_legacy ? 'Yes' : 'No'),
    esc(p.scan_count     || 0),
    esc(p.scan_limit     != null ? p.scan_limit : ''),
    esc(p.expires_at     || ''),
    esc(p.created_at     || '')
  ].join(','));
  const csv  = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `tag-inventory-${new Date().toISOString().split('T')[0]}.csv`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  showToast(`Exported ${all.length} tags ↓`);
}

async function adminToggleFounder(porterId) {
  const btn = document.querySelector(`#admin-row-${porterId} button[onclick*="adminToggleFounder"]`);
  if (btn) { btn.disabled = true; btn.innerText = '…'; }
  try {
    const { data, error } = await db.rpc('toggle_founder_legacy', { p_porter_id: porterId });
    if (error) throw error;
    // Update cached entry — match by porter_id (RPC) or id (fallback)
    const p = state.adminPorters.find(x => (x.porter_id || x.id) === porterId);
    if (p) p.founder_legacy = data;
    _renderAdminList();
    showToast(data ? 'Founder Legacy granted ✓' : 'Founder Legacy removed');
  } catch (err) {
    if (btn) { btn.disabled = false; }
    showError(`Toggle failed: ${err.message}`);
    _renderAdminList();
  }
}

async function adminToggleDeactivate(porterId) {
  const btn = document.querySelector(`#admin-row-${porterId} button[onclick*="adminToggleDeactivate"]`);
  if (btn) { btn.disabled = true; btn.innerText = '…'; }
  try {
    const { data, error } = await db.rpc('toggle_porter_deactivated', { p_porter_id: porterId });
    if (error) throw error;
    const p = state.adminPorters.find(x => (x.porter_id || x.id) === porterId);
    if (p) p.is_deactivated = data;
    _renderAdminList();
    showToast(data ? 'Porter deactivated' : 'Porter reactivated ✓');
  } catch (err) {
    if (btn) { btn.disabled = false; }
    showError(`Toggle failed: ${err.message}`);
    _renderAdminList();
  }
}

async function mintNewPorter() {
  try {
    const { data: identifier, error: genErr } = await db.rpc('generate_tag_id');
    if (genErr) throw genErr;
    const { error: insErr } = await db.from('porters').insert({ identifier, owner_id: state.profileId });
    if (insErr) throw insErr;
    showToast(`Minted: ${identifier}`);
    renderAdmin();
  } catch (err) {
    alert(`Mint failed: ${err.message}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────────────────────
function toBase64(file, key) { if(!file) return; const r=new FileReader(); r.readAsDataURL(file); r.onload=()=>{state.imageData[key]=r.result;}; }

function initAddressAutocomplete() {
  const input=document.getElementById('d-street'); if(!input||typeof google==='undefined') return;
  const ac=new google.maps.places.Autocomplete(input,{types:['address']});
  ac.addListener('place_changed',()=>{ const place=ac.getPlace(); let street='',route=''; for(const comp of place.address_components||[]){ if(comp.types[0]==='street_number') street=comp.long_name; if(comp.types[0]==='route') route=comp.long_name; if(comp.types[0]==='locality'){const el=document.getElementById('d-city');if(el)el.value=comp.long_name;} if(comp.types[0]==='administrative_area_level_1'){const el=document.getElementById('d-state');if(el)el.value=comp.short_name;} if(comp.types[0]==='postal_code'){const el=document.getElementById('d-zip');if(el)el.value=comp.long_name;} if(comp.types[0]==='country'){const el=document.getElementById('d-country');if(el)el.value=comp.short_name;} } input.value=`${street} ${route}`.trim(); });
}

// ─────────────────────────────────────────────────────────────────────────────
// CLAIM TAG  (hotfix)
// Reads ?claim=IDENTIFIER from the URL and opens a confirmation modal.
// Requires the claim_porter RPC from charishub-hotfix-claim.sql.
// ─────────────────────────────────────────────────────────────────────────────
function checkClaimParam() {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('claim');
  if (!raw) return;

  const identifier = raw.toUpperCase();
  window.history.replaceState({}, '', window.location.pathname);

  // Not logged in — showLoginWall already handled this in loadData(),
  // but guard here in case checkClaimParam is ever called standalone.
  if (!state.profileId) {
    showLoginWall(identifier);
    return;
  }

  document.getElementById('charis-modal').classList.add('active');
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('v-msg').style.display = 'none';
  document.getElementById('modal-title').innerText = `Claim Tag ${identifier}`;
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('modal-fields').innerHTML =
    `<p style="font-size:15px;color:#444;line-height:1.6;margin:0;">
      Tag <strong>${identifier}</strong> isn't registered to any account yet.
      Claim it to add it to your fleet and start assigning actions.
    </p>`;
  const btn = document.getElementById('modal-confirm-btn');
  btn.className = 'btn-charis btn-primary';
  btn.innerText = 'Claim Tag';
  btn.disabled = false;
  btn.onclick = async () => {
    // v3.1.0: enforce porter count limits per tier
    const tierLimits = { starter: 5, essential: 25, professional: Infinity };
    const limit = tierLimits[state.tier] ?? 5;
    if (state.fleet.length >= limit) {
      const next = state.tier === 'starter' ? 'Essential (25 porters)' : 'Professional (unlimited)';
      showError(`Your ${state.tier === 'starter' ? 'Starter' : 'Essential'} plan allows up to ${limit} porters. Upgrade to ${next} to add more.`);
      return;
    }
    btn.innerText = 'Claiming…'; btn.disabled = true;
    try {
      const { data, error } = await db.rpc('claim_porter', {
        p_identifier: identifier, p_shopify_user_id: SHOPIFY_GID
      });
      if (error) throw error;
      if (!data) throw new Error('This tag was not found or has already been claimed by another account.');
      closeModal();
      showToast(`Tag ${identifier} claimed and added to your fleet!`);
      loadData();
    } catch (err) {
      showError(`Could not claim tag: ${err.message}`);
      btn.disabled = false; btn.innerText = 'Try Again';
    }
  };
}

function closeModal() {
  document.getElementById('charis-modal').classList.remove('active');
  document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('v-msg').style.display='none';
  document.getElementById('modal-subtitle').innerText='';
  const btn=document.getElementById('modal-confirm-btn'); btn.className='btn-charis btn-primary'; btn.disabled=false; btn.innerText='Confirm';
  state.editTarget=null; state._softWarnType=null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONDITIONAL ROUTES CRUD  (v11.0.0)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// SUBMISSIONS DRAWER  (v2.7.0)
// ─────────────────────────────────────────────────────────────────────────────
async function openSubmissions(actionId, actionTitle) {
  const drawer = document.getElementById('submissions-drawer');
  const overlay = document.getElementById('submissions-overlay');
  if (!drawer) return;
  document.getElementById('submissions-title').innerText = actionTitle || 'Submissions';
  document.getElementById('submissions-body').innerHTML = '<div style="color:#aaa;padding:20px 0;text-align:center;">Loading…</div>';
  drawer.classList.add('open');
  overlay.classList.add('active');

  const { data, error } = await db.from('form_submissions')
    .select('*').eq('action_id', actionId).eq('owner_id', state.profileId)
    .order('created_at', { ascending: false }).limit(200);

  if (error || !data || !data.length) {
    document.getElementById('submissions-body').innerHTML =
      '<div style="color:#aaa;padding:20px 0;text-align:center;">No submissions yet.</div>';
    return;
  }

  // Build column headers from the keys of the first submission's data
  const keys = Object.keys(data[0].data || {});
  const thead = `<tr><th>Date</th><th>Device</th><th>Country</th>${keys.map(k=>`<th>${k}</th>`).join('')}</tr>`;
  const tbody = data.map(r => {
    const date = new Date(r.created_at).toLocaleString();
    const cells = keys.map(k => `<td>${r.data[k]??''}</td>`).join('');
    return `<tr><td style="white-space:nowrap;">${date}</td><td>${r.device_type||'—'}</td><td>${r.country||'—'}</td>${cells}</tr>`;
  }).join('');

  document.getElementById('submissions-body').innerHTML =
    `<table class="submissions-table"><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
}

function closeSubmissions() {
  document.getElementById('submissions-drawer')?.classList.remove('open');
  document.getElementById('submissions-overlay')?.classList.remove('active');
}

function renderRoutes() {
  const container=document.getElementById('routes-list');
  if(!container) return;
  if(!state.routes.length){container.innerHTML=`<div class="empty-state"><div class="empty-state-icon"><span class='mi'>map</span></div><div class="empty-state-title">No routes yet</div><div class="empty-state-sub">Create a conditional route to serve different actions based on device type, country, or OS.</div></div>`;return;}
  let html='';
  state.routes.forEach(r=>{
    const ruleCount=r.rules?.length||0;
    const fallback=r.fallback?r.fallback.title:'None (pass through)';
    const rulePreviews=(r.rules||[]).slice(0,3).map(rule=>`<div style="font-size:11px;color:#555;margin-top:2px;"><span style="font-weight:700;text-transform:uppercase;color:#aaa;min-width:50px;display:inline-block;">${rule.condition}</span> ${(rule.values||[]).join(', ')}</div>`).join('');
    html+=`<div class="route-card">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:800;font-size:15px;margin-bottom:3px;">${r.name}</div>
          <div style="font-size:11px;color:var(--charis-secondary);">${ruleCount} rule${ruleCount!==1?'s':''} · Fallback: ${fallback}</div>
          ${rulePreviews}
        </div>
        <div style="display:flex;gap:5px;flex-shrink:0;">
          <button class="btn-charis" onclick="openEditRoute('${r.id}')">Edit</button>
          <button class="btn-charis btn-danger-outline" onclick="confirmDeleteRoute('${r.id}','${r.name.replace(/'/g,"\\'")}')"><span class='mi mi-sm'>close</span></button>
        </div>
      </div>
    </div>`;
  });
  container.innerHTML=html;
}

function openNewRoute(){
  // v3.6.1: gate — Conditional Routes require Professional tier
  if(!canUse('routing')){
    trackGateHit('routing');
    showUpgradeModal('routing');
    return;
  }
  if(!state.actions.length){
    showToast('Add at least one action to your library before creating a route.');
    return;
  }
  state.editTarget=null; document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Create Conditional Route'; document.getElementById('modal-subtitle').innerText='';
  renderRouteForm({}); document.getElementById('modal-confirm-btn').onclick=saveRoute;
  document.getElementById('charis-modal').classList.add('active');
}

function openEditRoute(routeId){
  const r=state.routes.find(x=>x.id===routeId); if(!r) return;
  state.editTarget={id:routeId,type:'route'}; document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText='Edit Route'; document.getElementById('modal-subtitle').innerText='';
  renderRouteForm(r); document.getElementById('modal-confirm-btn').onclick=saveRoute;
  document.getElementById('charis-modal').classList.add('active');
}

function renderRouteForm(r){
  r=r||{};
  document.getElementById('modal-fields').innerHTML=`
    <label class="field-label">Route Name</label>
    <input type="text" id="rte-name" class="charis-input" placeholder="e.g. Mobile vs Desktop" value="${r.name||''}">
    <hr class="hr-label">
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
      <div class="field-label" style="margin-bottom:0;">Rules</div>
      <span style="font-size:11px;color:#888;">(evaluated in order — first match wins)</span>
    </div>
    <div id="route-rules-container"></div>
    <button type="button" class="add-entry-btn" onclick="addRouteRule()">+ Add Rule</button>
    <hr class="hr-label">
    <label class="field-label">Fallback Action <span style="font-weight:400;color:#888;">(if no rule matches)</span></label>
    <select id="rte-fallback" class="charis-input">
      <option value="">— Pass through to schedule / default —</option>${buildActionOpts(r.fallback?.id)}
    </select>`;
  const existing=r.rules||[];
  if(existing.length>0) existing.forEach(rule=>addRouteRule(rule)); else addRouteRule();
}

// Render guided values picker based on condition type  (v2.7.0)
function renderRuleValuesPicker(cond, selected) {
  selected = selected || [];
  if (cond === 'device') {
    return ['Mobile','Tablet','Desktop'].map(v =>
      `<label class="rule-pill-label"><input type="checkbox" class="rte-check" value="${v}" ${selected.includes(v)?'checked':''}> ${v}</label>`
    ).join('');
  }
  if (cond === 'os') {
    return ['iOS','Android','Windows','macOS','Linux','ChromeOS'].map(v =>
      `<label class="rule-pill-label"><input type="checkbox" class="rte-check" value="${v}" ${selected.includes(v)?'checked':''}> ${v}</label>`
    ).join('');
  }
  const countries=[['US','United States'],['GB','United Kingdom'],['CA','Canada'],['AU','Australia'],['DE','Germany'],['FR','France'],['ES','Spain'],['IT','Italy'],['NL','Netherlands'],['SE','Sweden'],['NO','Norway'],['DK','Denmark'],['CH','Switzerland'],['AT','Austria'],['BE','Belgium'],['PT','Portugal'],['IE','Ireland'],['NZ','New Zealand'],['SG','Singapore'],['HK','Hong Kong'],['JP','Japan'],['KR','South Korea'],['IN','India'],['BR','Brazil'],['MX','Mexico'],['AE','UAE'],['SA','Saudi Arabia'],['ZA','South Africa'],['NG','Nigeria'],['GH','Ghana']];
  const opts=countries.map(([code,name])=>`<option value="${code}" ${selected.includes(code)?'selected':''}>${code} — ${name}</option>`).join('');
  return `<select class="charis-input rte-country-select" style="margin-bottom:0;" multiple size="4">${opts}</select><div style="font-size:11px;color:#aaa;margin-top:4px;">Ctrl/Cmd+click to select multiple</div>`;
}

function getRuleValues(row) {
  const cond = row.querySelector('.rte-condition')?.value;
  if (cond === 'device' || cond === 'os') {
    return Array.from(row.querySelectorAll('.rte-check:checked')).map(c => c.value);
  }
  return Array.from(row.querySelectorAll('.rte-country-select option:checked')).map(o => o.value);
}

function updateRuleValuesPicker(select) {
  const wrap = select.closest('.route-rule-row').querySelector('.rte-values-wrap');
  if (wrap) wrap.innerHTML = renderRuleValuesPicker(select.value, []);
}

function addRouteRule(prefill){
  const container=document.getElementById('route-rules-container'); if(!container) return;
  prefill=prefill||{}; const cond=prefill.condition||'device';
  const row=document.createElement('div'); row.className='window-row route-rule-row';
  row.innerHTML=`
    <div style="flex:0 0 130px;"><label class="field-label">Condition</label>
    <select class="charis-input rte-condition" style="margin-bottom:0;" onchange="updateRuleValuesPicker(this)">
      <option value="device"  ${cond==='device' ?'selected':''}>Device Type</option>
      <option value="country" ${cond==='country'?'selected':''}>Country</option>
      <option value="os"      ${cond==='os'     ?'selected':''}>OS</option>
    </select></div>
    <div style="flex:1;min-width:0;"><label class="field-label">Match</label>
    <div class="rte-values-wrap">${renderRuleValuesPicker(cond, prefill.values||[])}</div></div>
    <div style="flex:1;"><label class="field-label">Serve Action</label>
    <select class="charis-input rte-action" style="margin-bottom:0;"><option value="">— Select —</option>${buildActionOpts(prefill.action_id)}</select></div>
    <button type="button" class="remove-entry" onclick="this.parentElement.remove()" style="margin-top:22px;"><span class='mi mi-sm'>close</span></button>`;
  container.appendChild(row);
}

async function saveRoute(){
  // v3.6.1: gate — double-check on save in case the modal was opened by other means
  if(!canUse('routing')){trackGateHit('routing');showUpgradeModal('routing');return;}
  const name=document.getElementById('rte-name')?.value?.trim();
  const fallbackId=document.getElementById('rte-fallback')?.value||null;
  if(!name){showError('Route name is required.');return;}
  const rules=Array.from(document.querySelectorAll('.route-rule-row')).map(row=>({
    condition:row.querySelector('.rte-condition')?.value,
    values:getRuleValues(row),
    action_id:row.querySelector('.rte-action')?.value||null
  })).filter(r=>r.values.length>0&&r.action_id);
  if(!rules.length){showError('At least one complete rule is required — select a condition, choose matching values, and pick an action to serve.');return;}
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Saving…'; btn.disabled=true;
  try{
    await db.rpc('save_conditional_route',{
      p_owner_id:state.profileId, p_name:name, p_rules:rules,
      p_fallback_action_id:fallbackId,
      p_id:state.editTarget?.type==='route'?state.editTarget.id:null
    });
    closeModal(); loadData();
  }catch(err){showError(`Save failed: ${err.message}`);btn.disabled=false;btn.innerText='Confirm';}
}

function confirmDeleteRoute(id,name){
  document.getElementById('modal-tab-container').style.display='none'; document.getElementById('modal-title').innerText='Delete Route?'; document.getElementById('modal-subtitle').innerText='';
  document.getElementById('modal-fields').innerHTML=`<div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${name}</div><p style="color:var(--charis-danger);font-size:12px;margin-top:10px;">Tags and groups using this route will have routing disabled.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.className='btn-charis btn-danger'; btn.innerText='Delete Route';
  btn.onclick=async()=>{btn.innerText='Deleting…';btn.disabled=true;try{await db.rpc('delete_conditional_route',{p_owner_id:state.profileId,p_id:id});closeModal();loadData();}catch(err){showError(`Delete failed: ${err.message}`);btn.disabled=false;btn.innerText='Delete Route';}};
  document.getElementById('charis-modal').classList.add('active');
}

// ─────────────────────────────────────────────────────────────────────────────
// SCAN LIMITS & EXPIRATION  (v11.0.0)
// ─────────────────────────────────────────────────────────────────────────────
function openScanLimits(porterId, label){
  const item=state.fleet.find(p=>p.porter_id===porterId||p.id===porterId); if(!item) return;
  document.getElementById('modal-tab-container').style.display='none';
  document.getElementById('modal-title').innerText=`Scan Limits: ${label}`;
  document.getElementById('modal-subtitle').innerText='';
  const expiresVal=item.expires_at?item.expires_at.substring(0,16):'';
  document.getElementById('modal-fields').innerHTML=`
    <div style="background:#f5f5f7;border-radius:12px;padding:14px 18px;margin-bottom:16px;display:flex;align-items:center;justify-content:space-between;">
      <div><div style="font-size:11px;font-weight:800;color:#888;text-transform:uppercase;margin-bottom:2px;">Current Scans</div>
      <div style="font-size:22px;font-weight:800;">${item.scan_count||0}${item.scan_limit!=null?` / ${item.scan_limit}`:''}</div></div>
      <button class="btn-charis btn-small" onclick="resetScanCount('${porterId}')">Reset Count</button>
    </div>
    <label class="field-label">Scan Limit <span style="font-weight:400;color:#888;">(blank = unlimited)</span></label>
    <input type="number" id="d-scan-limit" class="charis-input" min="1" placeholder="e.g. 500" value="${item.scan_limit!=null?item.scan_limit:''}">
    <label class="field-label">Expires At <span style="font-weight:400;color:#888;">(blank = never)</span></label>
    <input type="datetime-local" id="d-expires-at" class="charis-input" value="${expiresVal}">
    <label class="field-label">Limit Action <span style="font-weight:400;color:#888;">(served when limit/expiry hit)</span></label>
    <select id="d-limit-action" class="charis-input">
      <option value="">— Show generic expired page —</option>${buildActionOpts(item.limit_action_id)}
    </select>
    <p style="font-size:11px;color:#888;margin-top:8px;">When the scan count reaches the limit or the expiry time passes, the limit action is served instead of the tag's normal action.</p>`;
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Save Limits';
  btn.onclick=()=>saveScanLimits(porterId);
  document.getElementById('charis-modal').classList.add('active');
}

async function saveScanLimits(porterId){
  const limitVal=document.getElementById('d-scan-limit')?.value;
  const expiresVal=document.getElementById('d-expires-at')?.value;
  const limitActionId=document.getElementById('d-limit-action')?.value||null;
  const scanLimit=limitVal?parseInt(limitVal):null;
  // ── v3.3.1: reject negative or zero scan limits ──
  if(scanLimit!==null&&(isNaN(scanLimit)||scanLimit<1)){
    const el=document.getElementById('d-scan-limit');
    if(el){el.style.borderColor='#ef4444';el.style.boxShadow='0 0 0 3px rgba(239,68,68,0.15)';}
    showError('Scan limit must be 1 or greater. Leave blank for unlimited.');
    return;
  }
  const expiresAt=expiresVal?new Date(expiresVal).toISOString():null;
  const btn=document.getElementById('modal-confirm-btn'); btn.innerText='Saving…'; btn.disabled=true;
  try{
    await db.from('porters').update({scan_limit:scanLimit,expires_at:expiresAt,limit_action_id:limitActionId}).eq('id',porterId);
    closeModal(); loadData(); showToast('Scan limits saved ✓');
  }catch(err){showError(`Save failed: ${err.message}`);btn.disabled=false;btn.innerText='Save Limits';}
}

async function resetScanCount(porterId){
  try{
    await db.rpc('reset_scan_count',{p_owner_id:state.profileId,p_porter_id:porterId});
    showToast('Scan count reset ✓'); closeModal(); loadData();
  }catch(err){showToast(`Reset failed: ${err.message}`);}
}

// ─────────────────────────────────────────────────────────────────────────────
// BATCH QR ZIP DOWNLOAD  (v11.0.0)
// ─────────────────────────────────────────────────────────────────────────────
async function downloadQrZip(mode){
  let tags;
  if(mode==='selected'){
    if(!state.selected.length){showToast('Select tags first.');return;}
    tags=state.fleet.filter(t=>state.selected.includes(t.porter_id||t.id));
  } else {
    // re-apply current filters same way renderFleet does
    tags=state.fleet.filter(item=>{
      const matchSearch=(item.nickname||'').toLowerCase().includes(state.searchTerm.toLowerCase())||(item.group_name||'').toLowerCase().includes(state.searchTerm.toLowerCase())||(item.identifier||'').toLowerCase().includes(state.searchTerm.toLowerCase());
      const matchGroups=state.groupFilters.length===0||state.groupFilters.includes(item.group_name);
      const matchLibrary=state.libraryFilters.length===0||state.libraryFilters.includes(item.active_action_id);
      return matchSearch&&matchGroups&&matchLibrary;
    });
  }
  if(!tags.length){showToast('No tags to download.');return;}
  if(typeof JSZip==='undefined'){showToast('JSZip not loaded — check liquid file.');return;}
  if(typeof QRCode==='undefined'){showToast('QRCode library not loaded.');return;}
  showToast(`Generating ${tags.length} QR code${tags.length!==1?'s':''}…`);
  try{
    const zip=new JSZip();
    for(const tag of tags){
      const url=`${TAG_BASE_URL}${tag.identifier}`;
      const tmpDiv=document.createElement('div');
      tmpDiv.style.cssText='position:absolute;left:-9999px;top:-9999px;';
      document.body.appendChild(tmpDiv);
      new QRCode(tmpDiv,{text:url,width:300,height:300,colorDark:'#1d1d1f',colorLight:'#ffffff',correctLevel:QRCode.CorrectLevel.H});
      await new Promise(r=>setTimeout(r,80));
      const canvas=tmpDiv.querySelector('canvas');
      if(canvas){
        const blob=await new Promise(res=>canvas.toBlob(res,'image/png'));
        const fname=`${(tag.nickname||tag.identifier).replace(/[^a-zA-Z0-9_-]/g,'_')}_${tag.identifier}.png`;
        zip.file(fname,blob);
      }
      document.body.removeChild(tmpDiv);
    }
    const content=await zip.generateAsync({type:'blob'});
    const a=document.createElement('a');
    a.href=URL.createObjectURL(content);
    a.download=`charishub-qr-${new Date().toISOString().split('T')[0]}.zip`;
    a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href),1000);
    showToast(`${tags.length} QR code${tags.length!==1?'s':''} downloaded ✓`);
  }catch(err){console.error('QR ZIP error:',err);showToast('ZIP generation failed — see console.');}
}

// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING TOUR  (v2.8.3)
// ─────────────────────────────────────────────────────────────────────────────
const TOUR_STORAGE_KEY = 'charishub_tour_complete_v1';
let tourStep   = 0;
let tourActive = false;

const TOUR_STEPS = [
  {
    id: 'welcome',
    centered: true,
    title: `<span class='mi mi-sm'>waving_hand</span> Welcome to Smart Tag Control Center`,
    body: `<p>Built by <strong>Charis Studios</strong>, Smart Tag Control Center lets you control what happens when someone scans one of your smart tags — from anywhere, at any time.</p><p>This quick tour walks you through the basics. It only takes a minute.</p>`,
    nextLabel: 'Start Tour',
  },
  {
    id: 'fleet',
    target: '#fleet-container',
    side: 'top',
    title: `<span class='mi'>wifi_tethering</span> Your Fleet`,
    body: `<p>Every smart tag you own appears here. Each row shows the tag's name, its current action, and a mode badge. Click any row to assign or update its action.</p>`,
    nextLabel: 'Next',
  },
  {
    // Spotlight the library panel; we switch to that tab when this step shows.
    id: 'library',
    target: '#cpanel-library',
    tabSwitch: 'library',          // switches the command tab before spotlighting
    pulseEl: '#ctab-library',      // pulsing ring added to the tab button
    side: 'top',
    title: `<span class='mi'>bolt</span> Action Library`,
    body: `<p>Actions define what happens when a tag is scanned — a URL, phone number, SMS, Wi-Fi, and more.</p><p>Click the <strong><span class='mi'>bolt</span> Library</strong> tab, then tap <strong>+ New Action</strong> to create one. Assign it to any tag directly from the New Action form.</p>`,
    nextLabel: 'Next',
  },
  {
    // Switch back to Groups for this step.
    id: 'groups',
    target: '#cpanel-groups',
    tabSwitch: 'groups',
    side: 'top',
    title: `<span class='mi'>folder</span> Groups`,
    body: `<p>Groups let you control multiple tags at once. Add tags to a group and assign one action — every tag in the group updates instantly. Perfect for product lines or physical locations.</p>`,
    nextLabel: 'Next',
  },
  {
    id: 'advanced',
    centered: true,
    title: `<span class='mi'>bolt</span> Going Further`,
    body: `<div class="tour-feature-grid">
      <div class="tour-feature-card"><div class="tour-feature-icon"><span class='mi'>calendar_month</span></div><div><strong>Schedules</strong><span>Rotate actions by time of day or day of week — automatically.</span></div></div>
      <div class="tour-feature-card"><div class="tour-feature-icon"><span class='mi'>link</span></div><div><strong>Sequences</strong><span>Guide repeat scanners through a multi-step journey, one tap at a time.</span></div></div>
      <div class="tour-feature-card"><div class="tour-feature-icon"><span class='mi'>alt_route</span></div><div><strong>Routes</strong><span>Send scanners to different destinations based on device, country, or OS.</span></div></div>
    </div><p style="font-size:12px;color:#9090a0;margin:14px 0 0;line-height:1.5;">The Help Center has step-by-step guides for all of these. Open it anytime using the <strong>? Help</strong> button in the nav.</p>`,
    nextLabel: 'Done',
    showHelp: true,   // renders "Open Help →" secondary button on this step
  },
];

function initOnboarding() {
  injectTourHelpBtn();
  if (localStorage.getItem(TOUR_STORAGE_KEY)) return; // already completed
  // If a claim modal is open (user arriving via ?claim= link), skip the auto-start.
  // The tour button is still injected so they can trigger it manually afterward.
  if (document.getElementById('charis-modal')?.classList.contains('active')) return;
  setTimeout(() => startTour(), 500);
}

function injectTourHelpBtn() {
  if (document.getElementById('tour-help-btn')) return;
  const anchor = document.querySelector('.help-nav-btn');
  if (!anchor) return;
  const btn = document.createElement('button');
  btn.id          = 'tour-help-btn';
  btn.title       = 'Replay walkthrough';
  btn.textContent = 'Tour';
  btn.onclick     = () => startTour();
  anchor.parentElement.insertBefore(btn, anchor);
}

function startTour() {
  tourStep   = 0;
  tourActive = true;
  showTourStep(0);
}

function showTourStep(n) {
  removeTourUI();
  if (n < 0 || n >= TOUR_STEPS.length) { completeTour(); return; }
  tourStep = n;
  const step    = TOUR_STEPS[n];
  const isFirst = n === 0;
  const isLast  = n === TOUR_STEPS.length - 1;

  // Switch command tab if this step specifies one
  if (step.tabSwitch) {
    switchCommandTab(step.tabSwitch);
  }

  // Pulse-highlight a nav/tab element if specified
  if (step.pulseEl) {
    document.querySelector(step.pulseEl)?.classList.add('tour-pulse');
  }

  // Build progress dots
  let dotsHtml = '<div class="tour-dots">';
  TOUR_STEPS.forEach((_, i) => {
    dotsHtml += `<div class="tour-dot${i === n ? ' active' : ''}"></div>`;
  });
  dotsHtml += '</div>';

  // Build action buttons — last step gets an optional "Open Help →" secondary button
  const primaryBtn = `<button class="tour-btn-next" onclick="${isLast ? 'completeTour()' : `showTourStep(${n + 1})`}">${step.nextLabel}</button>`;
  const leftBtn    = isFirst
    ? `<button class="tour-btn-skip" onclick="skipTour()">Skip</button>`
    : `<button class="tour-btn-back" onclick="showTourStep(${n - 1})">Back</button>`;
  const helpBtn    = (isLast && step.showHelp)
    ? `<button class="tour-btn-back" onclick="completeTour();openHelp()">Open Help →</button>`
    : '';

  // Build tooltip
  const tooltip = document.createElement('div');
  tooltip.id = 'tour-tooltip';
  if (step.centered) tooltip.classList.add('tour-centered');
  tooltip.innerHTML = `
    <div class="tour-step-label">Step ${n + 1} of ${TOUR_STEPS.length}</div>
    <div class="tour-title">${step.title}</div>
    <div class="tour-body">${step.body}</div>
    <div class="tour-actions">
      ${dotsHtml}
      <div class="tour-btn-row">
        ${leftBtn}
        ${helpBtn}
        ${primaryBtn}
      </div>
    </div>
  `;
  document.body.appendChild(tooltip);

  // Spotlight + tooltip positioning for non-centered steps
  if (!step.centered && step.target) {
    const targetEl = document.querySelector(step.target);
    if (targetEl) { positionTourSpotlight(targetEl, tooltip, step.side); return; }
  }
  // Centered steps need no spotlight
}

function positionTourSpotlight(targetEl, tooltip, side) {
  const PAD  = 10;
  const GAP  = 14;
  const TW   = 340;
  const rect = targetEl.getBoundingClientRect();
  const vw   = window.innerWidth;
  const vh   = window.innerHeight;

  // Spotlight div — transparent hole; box-shadow darkens everything outside it
  const spotlight = document.createElement('div');
  spotlight.id = 'tour-spotlight';
  spotlight.style.top    = `${rect.top  - PAD}px`;
  spotlight.style.left   = `${rect.left - PAD}px`;
  spotlight.style.width  = `${rect.width  + PAD * 2}px`;
  spotlight.style.height = `${rect.height + PAD * 2}px`;
  document.body.appendChild(spotlight);

  // Initial off-screen placement; refined after paint
  tooltip.style.width = `${TW}px`;
  tooltip.style.top   = '-9999px';
  tooltip.style.left  = '-9999px';

  requestAnimationFrame(() => {
    const th = tooltip.offsetHeight;
    let top, left;

    if (side === 'top') {
      top  = rect.top - PAD - GAP - th;
      left = rect.left + rect.width / 2 - TW / 2;
    } else if (side === 'left') {
      top  = rect.top + rect.height / 2 - th / 2;
      left = rect.left - PAD - GAP - TW;
    } else if (side === 'right') {
      top  = rect.top + rect.height / 2 - th / 2;
      left = rect.right + PAD + GAP;
    } else { // bottom
      top  = rect.bottom + PAD + GAP;
      left = rect.left + rect.width / 2 - TW / 2;
    }

    // Flip to opposite side if tooltip would go off-screen
    if (side === 'top' && top < 12) {
      top = rect.bottom + PAD + GAP;
    }
    if (side === 'left' && left < 12) {
      left = rect.right + PAD + GAP;
    }

    // Clamp within viewport
    left = Math.max(12, Math.min(left, vw - TW - 12));
    top  = Math.max(12, Math.min(top,  vh - th - 12));

    tooltip.style.top  = `${top}px`;
    tooltip.style.left = `${left}px`;
  });
}

function removeTourUI() {
  document.getElementById('tour-spotlight')?.remove();
  document.getElementById('tour-tooltip')?.remove();
  // Remove any pulsing highlights added by the current step
  document.querySelectorAll('.tour-pulse').forEach(el => el.classList.remove('tour-pulse'));
}

function skipTour() {
  removeTourUI();
  tourActive = false;
  localStorage.setItem(TOUR_STORAGE_KEY, '1');
}

function completeTour() {
  removeTourUI();
  tourActive = false;
  localStorage.setItem(TOUR_STORAGE_KEY, '1');
  showToast('Tour complete! Tap Tour in the nav to replay anytime. ');
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SEQUENCES  (v3.0.0)
// ─────────────────────────────────────────────────────────────────────────────

// ── Helpers ──────────────────────────────────────────────────────────────────

const ADVANCE_LABEL = { scan: 'Scan-gated', calendar: 'Calendar-gated' };
const CALENDAR_INTERVAL_LABEL = { hour: 'Hourly', day: 'Daily', week: 'Weekly', month: 'Monthly' };
const ANCHOR_TYPE_LABEL = {
  publish:      'Starts when published',
  subscription: 'Starts on subscription',
  date:         'Starts on a set date'
};

function getSharedModeBadgeColor(advanceOn) {
  return advanceOn === 'calendar' ? '#5856d6' : '#0071e3';
}

// ── Render publisher-side list ────────────────────────────────────────────────

function renderShared() {
  const container = document.getElementById('shared-list');
  if (!container) return;

  if (!state.publishedSequences.length) {
    container.innerHTML = `
      <div class="shared-coming-soon">
        <span class="mi" style="font-size:36px;color:#c5c5d8;display:block;margin-bottom:10px;">share</span>
        <div style="font-weight:700;font-size:14px;color:#888;margin-bottom:6px;">No shared sequences yet</div>
        <div style="font-size:12px;color:#aaa;line-height:1.6;max-width:260px;margin:0 auto;">
          Publish a sequence once and let any tag subscribe to it.
          All subscribers rotate in sync — perfect for campaigns, menus, and more.
        </div>
      </div>`;
    return;
  }

  let html = '';
  state.publishedSequences.forEach(ps => {
    const stepCount   = ps.action_ids ? ps.action_ids.length : 0;
    const color       = getSharedModeBadgeColor(ps.advance_on);
    const advLabel    = ADVANCE_LABEL[ps.advance_on] || ps.advance_on;
    const calLabel    = ps.advance_on === 'calendar'
      ? ` · ${CALENDAR_INTERVAL_LABEL[ps.calendar_interval] || ps.calendar_interval}`
      : '';
    const anchorLabel = ps.advance_on === 'calendar'
      ? `<div style="font-size:11px;color:var(--charis-secondary);margin-top:2px;">${ANCHOR_TYPE_LABEL[ps.calendar_anchor_type] || ''}</div>`
      : '';
    const isPublic    = ps.is_public;

    html += `
      <div class="shared-seq-card${!ps.active ? ' shared-seq-inactive' : ''}">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:4px;">
              <div style="font-weight:800;font-size:15px;">${ps.name}</div>
              <span class="status-badge" style="background:${color}15;color:${color};">${advLabel}${calLabel}</span>
              <span class="status-badge" style="background:${isPublic?'#34c75915':'#86868b15'};color:${isPublic?'#34c759':'#86868b'};">${isPublic?'Public':'Unlisted'}</span>
            </div>
            ${ps.description ? `<div style="font-size:12px;color:var(--charis-secondary);margin-bottom:4px;">${ps.description}</div>` : ''}
            ${anchorLabel}
            <div style="font-size:11px;color:var(--charis-secondary);margin-top:4px;">
              <span class="mi mi-sm" style="vertical-align:middle;">format_list_numbered</span> ${stepCount} step${stepCount!==1?'s':''}
              &nbsp;·&nbsp;
              <span class="mi mi-sm" style="vertical-align:middle;">label</span> ${ps.subscriber_count} subscriber${ps.subscriber_count!==1?'s':''}
              &nbsp;·&nbsp;
              <span class="mi mi-sm" style="vertical-align:middle;">loop</span> ${ps.mode}
            </div>
          </div>
          <div style="display:flex;gap:5px;flex-shrink:0;flex-wrap:wrap;justify-content:flex-end;">
            <button class="btn-charis" onclick="copyShareLink('${ps.id}')" title="Copy subscribe link">
              <span class="mi mi-sm">link</span>
            </button>
            <button class="btn-charis" onclick="openEditSharedSequence('${ps.id}')">
              <span class="mi mi-sm">edit</span>
            </button>
            <button class="btn-charis btn-danger-outline" onclick="confirmDeleteSharedSequence('${ps.id}','${ps.name.replace(/'/g,"\\'")}')">
              <span class="mi mi-sm">close</span>
            </button>
          </div>
        </div>
      </div>`;
  });

  container.innerHTML = html;
}

// ── Copy shareable subscribe link ─────────────────────────────────────────────

function copyShareLink(publishedSeqId) {
  const base = window.location.href.split('?')[0];
  const link = `${base}?subscribe=${publishedSeqId}`;
  navigator.clipboard.writeText(link).then(() => {
    showToast('Subscribe link copied to clipboard!');
  }).catch(() => {
    // Fallback for browsers without clipboard API
    prompt('Copy this link:', link);
  });
}

// ── ?subscribe= URL param handler ────────────────────────────────────────────

async function checkSubscribeParam() {
  const subscribeId = new URLSearchParams(window.location.search).get('subscribe');
  if (!subscribeId) return;

  // Remove the param from the URL without reloading
  const cleanUrl = window.location.href.replace(/[?&]subscribe=[^&]+/, '').replace(/[?&]$/, '');
  window.history.replaceState({}, '', cleanUrl);

  // Fetch the published sequence (may be from any owner — public or linked)
  const { data: ps, error } = await db.from('published_sequences')
    .select('*').eq('id', subscribeId).eq('active', true).maybeSingle();

  if (error || !ps) {
    showToast('Shared sequence not found or no longer active.'); return;
  }

  openSubscribeModal(ps);
}

// ── Subscribe modal ───────────────────────────────────────────────────────────

function openSubscribeModal(ps) {
  if (!state.fleet.length) {
    showToast('Register a Smart Tag before subscribing.'); return;
  }

  const stepCount  = ps.action_ids ? ps.action_ids.length : 0;
  const color      = getSharedModeBadgeColor(ps.advance_on);
  const advLabel   = ADVANCE_LABEL[ps.advance_on] || ps.advance_on;
  const calLine    = ps.advance_on === 'calendar'
    ? `<div style="font-size:12px;color:var(--charis-secondary);margin-top:4px;">
         ${CALENDAR_INTERVAL_LABEL[ps.calendar_interval] || ''} · ${ANCHOR_TYPE_LABEL[ps.calendar_anchor_type] || ''}
       </div>`
    : '';

  // Tags that are already subscribed to THIS sequence
  const alreadySubbed = new Set(
    state.sharedSubscriptions
      .filter(s => s.published_sequence_id === ps.id)
      .map(s => s.porter_id)
  );

  const tagOptions = state.fleet.map(p => {
    const label   = p.nickname || p.identifier;
    const subbed  = alreadySubbed.has(p.porter_id);
    return `<option value="${p.porter_id}" ${subbed ? 'disabled' : ''}>
      ${label}${subbed ? ' (already subscribed)' : ''}
    </option>`;
  }).join('');

  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText = 'Subscribe a Tag';
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('v-msg').innerText = '';
  document.getElementById('modal-fields').innerHTML = `
    <div style="background:#f5f5f7;border-radius:14px;padding:14px 16px;margin-bottom:18px;">
      <div style="font-weight:800;font-size:15px;margin-bottom:4px;">${ps.name}</div>
      ${ps.description ? `<div style="font-size:12px;color:var(--charis-secondary);margin-bottom:6px;">${ps.description}</div>` : ''}
      <span class="status-badge" style="background:${color}15;color:${color};">${advLabel}</span>
      ${calLine}
      <div style="font-size:11px;color:var(--charis-secondary);margin-top:6px;">
        ${stepCount} step${stepCount!==1?'s':''} · ${ps.mode} · ${ps.subscriber_count} subscriber${ps.subscriber_count!==1?'s':''}
      </div>
    </div>
    <label class="field-label">Tag to subscribe</label>
    <select id="sub-porter-select" class="charis-input">
      <option value="">— Select a tag —</option>
      ${tagOptions}
    </select>
    <p style="font-size:11px;color:var(--charis-secondary);margin-top:6px;">
      Subscribing will override this tag's current action assignment while the subscription is active.
    </p>`;

  const btn = document.getElementById('modal-confirm-btn');
  btn.className = 'btn-charis btn-primary';
  btn.innerText = 'Subscribe';
  btn.onclick = async () => {
    const porterId = document.getElementById('sub-porter-select')?.value;
    if (!porterId) { showError('Select a tag first.'); return; }
    btn.innerText = 'Subscribing…'; btn.disabled = true;
    try {
      const { error } = await db.rpc('subscribe_tag_to_shared_sequence', {
        p_porter_id:             porterId,
        p_published_sequence_id: ps.id,
        p_subscriber_profile_id: state.profileId
      });
      if (error) throw error;
      closeModal();
      loadData();
      showToast(`Tag subscribed to "${ps.name}"!`);
    } catch (err) {
      showError(`Subscribe failed: ${err.message}`);
      btn.disabled = false; btn.innerText = 'Subscribe';
    }
  };

  document.getElementById('charis-modal').classList.add('active');
}

// ── Create / Edit shared sequence ─────────────────────────────────────────────

function openNewSharedSequence() {
  if (!state.actions.length) {
    showToast('Add at least one action to your library before creating a shared sequence.');
    return;
  }
  state.editTarget = null;
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText = 'New Shared Sequence';
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('v-msg').innerText = '';
  renderSharedSequenceForm({});
  document.getElementById('modal-confirm-btn').onclick = saveSharedSequence;
  document.getElementById('charis-modal').classList.add('active');
}

function openEditSharedSequence(id) {
  const ps = state.publishedSequences.find(x => x.id === id);
  if (!ps) return;
  state.editTarget = id;
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText = 'Edit Shared Sequence';
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('v-msg').innerText = '';
  renderSharedSequenceForm(ps);
  document.getElementById('modal-confirm-btn').onclick = saveSharedSequence;
  document.getElementById('charis-modal').classList.add('active');
}

function renderSharedSequenceForm(ps) {
  const isEdit     = !!state.editTarget;
  const actionOpts = state.actions.map(a =>
    `<option value="${a.id}">${a.title} (${a.type})</option>`
  ).join('');

  const selectedIds  = ps.action_ids || [];
  const stepsHtml    = selectedIds.map((id, i) => {
    const a = state.actions.find(x => x.id === id);
    return `<div class="shared-step-row" data-step-id="${id}">
      <input type="hidden" class="step-action-id" value="${id}">
      <span class="mi mi-sm" style="color:#888;cursor:grab;">drag_indicator</span>
      <span style="font-size:13px;flex:1;">${i+1}. ${a ? a.title : '(deleted action)'}</span>
      <button type="button" class="remove-entry" onclick="this.parentElement.remove()"><span class='mi mi-sm'>close</span></button>
    </div>`;
  }).join('');

  const calVisible   = (ps.advance_on || 'scan') === 'calendar';
  const anchorType   = ps.calendar_anchor_type || 'publish';
  const anchorDateVal= ps.calendar_anchor ? ps.calendar_anchor.slice(0,10) : '';

  document.getElementById('modal-fields').innerHTML = `
    <label class="field-label">Name</label>
    <input type="text" id="ps-name" class="charis-input" placeholder="e.g. Word of the Day, Weekly Feature…" value="${ps.name||''}">

    <label class="field-label">Description <span style="font-weight:400;color:#aaa;">(optional)</span></label>
    <textarea id="ps-desc" class="charis-input" rows="2" placeholder="Briefly describe what subscribers will receive…" style="resize:vertical;">${ps.description||''}</textarea>

    <label class="field-label">Playback Mode</label>
    <select id="ps-mode" class="charis-input">
      <option value="loop"     ${(ps.mode||'loop')==='loop'     ?'selected':''}>Loop — repeats from step 1</option>
      <option value="linear"   ${ps.mode==='linear'             ?'selected':''}>Linear — stays on last step</option>
      <option value="one-shot" ${ps.mode==='one-shot'           ?'selected':''}>One-Shot — deactivates after last step</option>
    </select>

    <label class="field-label">Advancement</label>
    <select id="ps-advance" class="charis-input" onchange="toggleSharedCalendarFields()">
      <option value="scan"     ${(ps.advance_on||'scan')==='scan'    ?'selected':''}>Scan-gated — advances on each scan</option>
      <option value="calendar" ${ps.advance_on==='calendar'          ?'selected':''}>Calendar-gated — advances on a time interval</option>
    </select>

    <div id="ps-calendar-fields" style="display:${calVisible?'block':'none'};">
      <label class="field-label">Interval</label>
      <select id="ps-interval" class="charis-input">
        <option value="hour"  ${ps.calendar_interval==='hour' ?'selected':''}>Hourly</option>
        <option value="day"   ${(ps.calendar_interval||'day')==='day'  ?'selected':''}>Daily</option>
        <option value="week"  ${ps.calendar_interval==='week' ?'selected':''}>Weekly</option>
        <option value="month" ${ps.calendar_interval==='month'?'selected':''}>Monthly</option>
      </select>

      <label class="field-label">Anchor — when does Step 1 begin?</label>
      <select id="ps-anchor-type" class="charis-input" onchange="toggleSharedAnchorDate()">
        <option value="publish"      ${anchorType==='publish'     ?'selected':''}>On publish (all subscribers share the same timeline)</option>
        <option value="subscription" ${anchorType==='subscription'?'selected':''}>On subscription (each tag has its own Day 1)</option>
        <option value="date"         ${anchorType==='date'        ?'selected':''}>On a specific date</option>
      </select>

      <div id="ps-anchor-date-wrap" style="display:${anchorType==='date'?'block':'none'};">
        <label class="field-label">Start Date</label>
        <input type="date" id="ps-anchor-date" class="charis-input" value="${anchorDateVal}">
      </div>
    </div>

    <label class="field-label">Visibility</label>
    <select id="ps-public" class="charis-input">
      <option value="false" ${!ps.is_public?'selected':''}>Unlisted — only accessible via direct link</option>
      <option value="true"  ${ps.is_public ?'selected':''}>Public — discoverable by other users</option>
    </select>

    <label class="field-label" style="margin-top:16px;">Steps
      <span style="font-weight:400;color:#aaa;font-size:11px;">(in order)</span>
    </label>
    <div id="ps-steps-list" style="display:flex;flex-direction:column;gap:6px;margin-bottom:8px;">
      ${stepsHtml}
    </div>
    <div style="display:flex;gap:8px;align-items:center;">
      <select id="ps-add-action" class="charis-input" style="flex:1;margin-bottom:0;">
        <option value="">— Add a step —</option>
        ${actionOpts}
      </select>
      <button type="button" class="btn-charis" onclick="addSharedStep()" style="flex-shrink:0;">
        <span class='mi mi-sm'>add</span> Add
      </button>
    </div>

    ${isEdit ? `<button type="button" style="margin-top:18px;background:none;border:none;color:var(--charis-danger);font-size:12px;cursor:pointer;padding:0;" onclick="confirmDeleteSharedSequence('${state.editTarget}','${(ps.name||'').replace(/'/g,"\\'")}',true)">Delete this shared sequence</button>` : ''}`;

  const btn = document.getElementById('modal-confirm-btn');
  btn.className  = 'btn-charis btn-primary';
  btn.innerText  = isEdit ? 'Save Changes' : 'Publish Sequence';
}

function toggleSharedCalendarFields() {
  const show = document.getElementById('ps-advance')?.value === 'calendar';
  const wrap  = document.getElementById('ps-calendar-fields');
  if (wrap) wrap.style.display = show ? 'block' : 'none';
}

function toggleSharedAnchorDate() {
  const show = document.getElementById('ps-anchor-type')?.value === 'date';
  const wrap  = document.getElementById('ps-anchor-date-wrap');
  if (wrap) wrap.style.display = show ? 'block' : 'none';
}

function addSharedStep() {
  const sel = document.getElementById('ps-add-action');
  const id  = sel?.value; if (!id) return;
  const a   = state.actions.find(x => x.id === id); if (!a) return;
  const list = document.getElementById('ps-steps-list');
  const pos  = list.children.length + 1;
  const div  = document.createElement('div');
  div.className = 'shared-step-row';
  div.dataset.stepId = id;
  div.innerHTML = `
    <input type="hidden" class="step-action-id" value="${id}">
    <span class="mi mi-sm" style="color:#888;cursor:grab;">drag_indicator</span>
    <span style="font-size:13px;flex:1;">${pos}. ${a.title}</span>
    <button type="button" class="remove-entry" onclick="this.parentElement.remove()"><span class='mi mi-sm'>close</span></button>`;
  list.appendChild(div);
  sel.value = '';
}

async function saveSharedSequence() {
  const name       = document.getElementById('ps-name')?.value?.trim();
  const desc       = document.getElementById('ps-desc')?.value?.trim() || null;
  const mode       = document.getElementById('ps-mode')?.value;
  const advanceOn  = document.getElementById('ps-advance')?.value;
  const isPublic   = document.getElementById('ps-public')?.value === 'true';

  if (!name) { showError('Name is required.'); return; }

  // Collect steps in DOM order via hidden inputs (reliable for both HTML-rendered and JS-created rows)
  const actionIds = Array.from(
    document.querySelectorAll('#ps-steps-list .step-action-id')
  ).map(inp => inp.value).filter(Boolean);
  if (actionIds.length < 1) { showError('Add at least one step.'); return; }

  let calInterval = null, calAnchorType = null, calAnchor = null;
  if (advanceOn === 'calendar') {
    calInterval   = document.getElementById('ps-interval')?.value;
    calAnchorType = document.getElementById('ps-anchor-type')?.value;
    if (calAnchorType === 'date') {
      const dateVal = document.getElementById('ps-anchor-date')?.value;
      if (!dateVal) { showError('Specify the start date.'); return; }
      calAnchor = new Date(dateVal).toISOString();
    } else if (calAnchorType === 'publish') {
      calAnchor = state.editTarget
        ? (state.publishedSequences.find(p=>p.id===state.editTarget)?.calendar_anchor || new Date().toISOString())
        : new Date().toISOString();
    }
    // 'subscription' → calAnchor stays null (per-subscriber anchor from subscribed_at)
  }

  const btn = document.getElementById('modal-confirm-btn');
  btn.innerText = 'Saving…'; btn.disabled = true;

  try {
    const payload = {
      name, description: desc, action_ids: actionIds,
      mode, advance_on: advanceOn, is_public: isPublic,
      calendar_interval:    calInterval,
      calendar_anchor_type: calAnchorType,
      calendar_anchor:      calAnchor
    };

    if (state.editTarget) {
      const { error } = await db.from('published_sequences')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', state.editTarget).eq('owner_id', state.profileId);
      if (error) throw error;
      showToast('Shared sequence updated!');
    } else {
      const { error } = await db.from('published_sequences')
        .insert({ ...payload, owner_id: state.profileId });
      if (error) throw error;
      showToast('Shared sequence published!');
    }
    closeModal(); loadData();
  } catch (err) {
    showError(`Save failed: ${err.message}`);
    btn.disabled = false; btn.innerText = state.editTarget ? 'Save Changes' : 'Publish Sequence';
  }
}

// ── Delete shared sequence ────────────────────────────────────────────────────

function confirmDeleteSharedSequence(id, name, fromEditModal = false) {
  if (fromEditModal) closeModal();
  const title = `Delete "${name}"?`;
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText = title;
  document.getElementById('modal-subtitle').innerText = '';
  document.getElementById('v-msg').innerText = '';
  document.getElementById('modal-fields').innerHTML =
    `<div style="padding:16px;background:#f5f5f7;border-radius:12px;font-weight:700;">${name}</div>
     <p style="color:var(--charis-danger);font-size:12px;margin-top:10px;">
       <span class='mi'>warning</span> All subscriptions to this sequence will be cancelled and subscribed tags will lose their assignment.
     </p>`;
  const btn = document.getElementById('modal-confirm-btn');
  btn.className  = 'btn-charis';
  btn.style.background = 'var(--charis-danger)'; btn.style.color = '#fff';
  btn.innerText  = 'Delete Shared Sequence';
  btn.onclick    = async () => {
    btn.innerText = 'Deleting…'; btn.disabled = true;
    try {
      // Cascade delete also removes sequence_subscriptions via FK ON DELETE CASCADE
      const { error } = await db.from('published_sequences')
        .delete().eq('id', id).eq('owner_id', state.profileId);
      if (error) throw error;
      closeModal(); loadData();
      showToast('Shared sequence deleted.');
    } catch(err) {
      showError(`Delete failed: ${err.message}`);
      btn.disabled = false; btn.innerText = 'Delete Shared Sequence';
    }
  };
  document.getElementById('charis-modal').classList.add('active');
}

// =============================================================================
// ORGANIZATION TAB  (v4.0.0)
// Multi-user / RBAC support for Enterprise Edition.
// Owner can create an org, invite members, assign roles, remove members.
// =============================================================================

// ── Lazy-load gate ────────────────────────────────────────────────────────────
function loadOrgDataIfNeeded() {
  if (!state.orgLoaded) loadOrgData();
}

// ── Primary data fetch ────────────────────────────────────────────────────────
async function loadOrgData() {
  const el = document.getElementById('org-panel-content');
  if (!el) return;
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:120px;color:#aaa;font-size:13px;"><span class="mi" style="margin-right:6px;font-size:18px;">corporate_fare</span> Loading…</div>`;

  try {
    if (!state.profileId) { state.orgLoaded = true; renderOrgPanel(); return; }

    // 1. Look up the org (and our role) via SECURITY DEFINER RPC so RLS
    //    doesn't block dashboard users whose auth.uid() = NULL.
    const { data: orgRows, error: orgErr } = await db.rpc('get_my_org', {
      p_shopify_user_id: SHOPIFY_GID,
    });
    if (orgErr) throw orgErr;

    if (orgRows && orgRows.length > 0) {
      const row = orgRows[0];
      state.org = {
        id:            row.id,
        name:          row.name,
        slug:          row.slug,
        owner_id:      row.owner_id,
        logo_url:      row.logo_url,
        custom_domain: row.custom_domain,
        created_at:    row.created_at,
      };
      state.myOrgRole = row.my_role;

      // 2. Load members
      const { data: members, error: memErr } = await db.rpc('get_org_members', {
        p_org_id:          state.org.id,
        p_shopify_user_id: SHOPIFY_GID,
      });
      if (memErr) throw memErr;
      state.orgMembers = members || [];

      // 3. Load pending invites (owner / admin only)
      if (state.myOrgRole === 'owner' || state.myOrgRole === 'admin') {
        const { data: invites, error: invErr } = await db.rpc('get_org_invites', {
          p_org_id:          state.org.id,
          p_shopify_user_id: SHOPIFY_GID,
        });
        if (invErr) throw invErr;
        state.orgInvites = invites || [];
      } else {
        state.orgInvites = [];
      }

    } else {
      state.org = null;
      state.myOrgRole = null;
      state.orgMembers = [];
      state.orgInvites = [];
    }

    state.orgLoaded = true;
    renderOrgPanel();
  } catch(err) {
    state.orgLoaded = true;
    const el2 = document.getElementById('org-panel-content');
    if (el2) el2.innerHTML = `<div style="padding:20px;color:var(--charis-danger);font-size:13px;"><span class="mi mi-sm">error</span> Failed to load org data: ${err.message}</div>`;
  }
}

// ── Render the panel ──────────────────────────────────────────────────────────
function renderOrgPanel() {
  const el = document.getElementById('org-panel-content');
  if (!el) return;

  if (!state.profileId) {
    el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:120px;color:#aaa;font-size:13px;">Sign in to manage your organization.</div>`;
    return;
  }

  if (!state.org) {
    // ── No-org hero ──────────────────────────────────────────────────────────
    el.innerHTML = `
      <div class="fleet-card" style="padding:28px 24px;text-align:center;max-width:460px;margin:8px auto;">
        <span class="mi" style="font-size:40px;color:#c7d2fe;display:block;margin-bottom:10px;">corporate_fare</span>
        <div style="font-weight:700;font-size:16px;color:#1d1d1f;margin-bottom:6px;">Create Your Organization</div>
        <div style="font-size:13px;color:#666;line-height:1.5;margin-bottom:20px;max-width:320px;margin-left:auto;margin-right:auto;">Invite team members to collaborate on your Smart Tag fleet with role-based access control.</div>
        <div style="text-align:left;">
          <label class="field-label">Organization Name</label>
          <input type="text" id="org-create-name" class="charis-input" placeholder="Acme Corporation" maxlength="80" oninput="_orgAutoSlug()">
          <label class="field-label" style="margin-top:10px;">URL Slug</label>
          <div style="display:flex;align-items:center;gap:0;">
            <span style="font-size:13px;color:#888;padding:0 6px 0 0;white-space:nowrap;">charishub.com/org/</span>
            <input type="text" id="org-create-slug" class="charis-input" placeholder="acme-corporation" maxlength="50" style="flex:1;" oninput="_orgSlugSanitize(this)">
          </div>
          <p style="font-size:11px;color:#888;margin-top:4px;">Lowercase letters, numbers, and hyphens only.</p>
          <div id="org-create-err" style="color:var(--charis-danger);font-size:12px;display:none;margin-top:6px;"></div>
          <button class="btn-charis btn-primary" style="margin-top:16px;width:100%;" onclick="createOrg()">
            <span class="mi mi-sm">add_business</span> Create Organization
          </button>
        </div>
      </div>`;
    return;
  }

  // ── Org exists ───────────────────────────────────────────────────────────
  const isAdminOrOwner = state.myOrgRole === 'owner' || state.myOrgRole === 'admin';
  const canInvite      = state.myOrgRole === 'owner' || state.myOrgRole === 'admin' || state.myOrgRole === 'manager';

  const roleBadge = (role, isOwner) => {
    if (isOwner) return `<span style="font-size:10px;font-weight:700;background:#f0fdf4;color:#16a34a;padding:2px 7px;border-radius:20px;border:1px solid #bbf7d0;">Owner</span>`;
    const cfg = { admin: ['#ede9fe','#7c3aed','#ddd6fe'], manager: ['#eff6ff','#2563eb','#bfdbfe'], member: ['#f5f5f7','#555','#e0e0e0'] };
    const [bg, fg, br] = cfg[role] || cfg.member;
    return `<span style="font-size:10px;font-weight:700;background:${bg};color:${fg};padding:2px 7px;border-radius:20px;border:1px solid ${br};">${role.charAt(0).toUpperCase()+role.slice(1)}</span>`;
  };

  // Member rows
  const memberRows = state.orgMembers.map(m => {
    const name    = m.display_name || m.email || 'Unknown';
    const email   = m.email   || '';
    const company = m.company || '';
    const subline = [email, company].filter(Boolean).join(' · ');
    const canRemove     = isAdminOrOwner && !m.is_owner;
    const canChangeRole = isAdminOrOwner && !m.is_owner;

    const roleSelect = canChangeRole
      ? `<select class="charis-input" style="font-size:12px;padding:4px 8px;height:auto;min-width:100px;" onchange="updateOrgMemberRole('${m.profile_id}', this.value)">
           <option value="admin"   ${m.role==='admin'   ?'selected':''}>Admin</option>
           <option value="manager" ${m.role==='manager' ?'selected':''}>Manager</option>
           <option value="member"  ${m.role==='member'  ?'selected':''}>Member</option>
         </select>`
      : roleBadge(m.role, m.is_owner);

    const removeBtn = canRemove
      ? `<button class="icon-btn" title="Remove member" onclick="removeOrgMember('${m.profile_id}','${name.replace(/'/g,"\\'")}')"><span class="mi mi-sm">person_remove</span></button>`
      : '';

    return `<tr>
      <td style="font-weight:600;font-size:13px;">
        ${name}
        ${subline ? `<div style="font-size:11px;color:#888;font-weight:400;margin-top:1px;">${subline}</div>` : ''}
      </td>
      <td>${m.is_owner ? roleBadge(m.role, true) : roleSelect}</td>
      <td style="text-align:right;">${removeBtn}</td>
    </tr>`;
  }).join('');

  // Pending invite rows
  const inviteRows = state.orgInvites.map(inv => {
    const inviteLink = `${window.location.origin}/pages/charishub-org?invite=${inv.token}`;
    const expiresDate = new Date(inv.expires_at).toLocaleDateString(undefined,{month:'short',day:'numeric'});
    return `<tr>
      <td style="font-size:13px;">${inv.email}</td>
      <td>${roleBadge(inv.role, false)}</td>
      <td style="font-size:11px;color:#888;">Expires ${expiresDate}</td>
      <td style="text-align:right;white-space:nowrap;">
        <button class="icon-btn" title="Copy invite link" onclick="navigator.clipboard.writeText('${inviteLink.replace(/'/g,"\\'")}').then(()=>showToast('Invite link copied!'))"><span class="mi mi-sm">link</span></button>
        <button class="icon-btn" title="Resend invite email" onclick="resendOrgInviteFor('${inv.email.replace(/'/g,"\\'")}','${inv.role}')"><span class="mi mi-sm">send</span></button>
        <button class="icon-btn" title="Revoke invite" onclick="revokeOrgInvite('${inv.id}','${inv.email.replace(/'/g,"\\'")}')"><span class="mi mi-sm">cancel</span></button>
      </td>
    </tr>`;
  }).join('');

  // Invite form (managers and above)
  const inviteForm = canInvite ? `
    <div class="fleet-card" style="padding:16px 18px;margin-top:8px;">
      <div style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.04em;margin-bottom:12px;">Invite Member</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
        <div style="flex:2;min-width:180px;">
          <label class="field-label">Email Address</label>
          <input type="email" id="org-invite-email" class="charis-input" placeholder="team@example.com">
        </div>
        <div style="flex:1;min-width:110px;">
          <label class="field-label">Role</label>
          <select id="org-invite-role" class="charis-input">
            <option value="member">Member</option>
            <option value="manager">Manager</option>
            ${isAdminOrOwner ? '<option value="admin">Admin</option>' : ''}
          </select>
        </div>
        <div style="flex-shrink:0;padding-bottom:1px;">
          <button class="btn-charis btn-primary" onclick="inviteOrgMember()">
            <span class="mi mi-sm">person_add</span> Send Invite
          </button>
        </div>
      </div>
      <div id="org-invite-err" style="color:var(--charis-danger);font-size:12px;display:none;margin-top:6px;"></div>
    </div>` : '';

  // Non-owner members can leave the org
  const canLeave = state.myOrgRole !== 'owner';

  el.innerHTML = `
    <!-- Org header -->
    <div class="fleet-card" style="padding:14px 18px;margin-top:0;">
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        <span class="mi" style="font-size:28px;color:#6366f1;">corporate_fare</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;font-size:16px;color:#1d1d1f;line-height:1.2;">${state.org.name}</div>
          <div style="font-size:12px;color:#888;margin-top:2px;">/${state.org.slug} &nbsp;·&nbsp; ${roleBadge(state.myOrgRole === 'owner' ? 'admin' : state.myOrgRole, state.myOrgRole === 'owner')}</div>
        </div>
        <div style="display:flex;gap:4px;align-items:center;">
          ${isAdminOrOwner ? `<button class="icon-btn" title="Rename organization" onclick="openRenameOrg()"><span class="mi mi-sm">edit</span></button>` : ''}
          ${canLeave ? `<button class="icon-btn ib-danger" title="Leave organization" onclick="leaveOrg()"><span class="mi mi-sm">logout</span></button>` : ''}
          <button class="icon-btn" title="Refresh org data" onclick="state.orgLoaded=false;loadOrgData()"><span class="mi mi-sm">refresh</span></button>
        </div>
      </div>
    </div>

    <!-- Members -->
    <div class="fleet-card" style="padding:0;overflow:hidden;margin-top:8px;">
      <div style="padding:12px 18px 8px;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.04em;">
        Members <span style="font-weight:400;color:#bbb;">(${state.orgMembers.length})</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead><tr style="background:#f9f9fb;">
          <th style="padding:6px 18px;text-align:left;font-size:11px;color:#aaa;font-weight:600;">Name</th>
          <th style="padding:6px 12px;text-align:left;font-size:11px;color:#aaa;font-weight:600;">Email</th>
          <th style="padding:6px 12px;text-align:left;font-size:11px;color:#aaa;font-weight:600;">Role</th>
          <th style="padding:6px 18px;"></th>
        </tr></thead>
        <tbody style="divide-y:#f0f0f0;">${memberRows || `<tr><td colspan="4" style="padding:16px 18px;color:#aaa;font-size:13px;">No members yet.</td></tr>`}</tbody>
      </table>
    </div>

    ${state.orgInvites.length ? `
    <!-- Pending Invites -->
    <div class="fleet-card" style="padding:0;overflow:hidden;margin-top:8px;">
      <div style="padding:12px 18px 8px;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.04em;">
        Pending Invites <span style="font-weight:400;color:#bbb;">(${state.orgInvites.length})</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <tbody>${inviteRows}</tbody>
      </table>
    </div>` : ''}

    ${inviteForm}

    <!-- Live Activity Feed -->
    <div class="fleet-card" style="padding:0;overflow:hidden;margin-top:8px;" id="org-live-feed-card">
      <div style="padding:12px 18px 8px;display:flex;align-items:center;gap:8px;">
        <div style="font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.04em;flex:1;">
          Live Activity
        </div>
        <span id="org-live-dot" style="width:8px;height:8px;border-radius:50%;background:#d1d5db;display:inline-block;transition:background 0.3s;" title="Connecting…"></span>
        <span id="org-live-label" style="font-size:11px;color:#aaa;">Connecting…</span>
      </div>
      <div id="org-live-feed" style="max-height:220px;overflow-y:auto;padding:0 0 8px;">
        <div style="padding:12px 18px;color:#aaa;font-size:12px;">Waiting for scans…</div>
      </div>
    </div>

    <!-- Live Map -->
    ${isAdminOrOwner ? `
    <div class="fleet-card" style="padding:0;overflow:hidden;margin-top:8px;">
      <div style="padding:12px 18px 8px;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.04em;">
        Live Scan Map <span style="font-size:10px;font-weight:400;color:#bbb;text-transform:none;margin-left:4px;">— last 50 scans</span>
      </div>
      <div id="org-live-map" style="height:280px;width:100%;border-radius:0 0 12px 12px;background:#f0f4f8;"></div>
    </div>` : ''}
  `;

  // Start Realtime feed and map after DOM is ready
  requestAnimationFrame(() => {
    startOrgLiveFeed();
    if (isAdminOrOwner) initOrgLiveMap();
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ORG: LIVE ACTIVITY FEED  (v4.2.0)
// ─────────────────────────────────────────────────────────────────────────────
let _orgRealtimeChannel = null;
let _orgFeedItems = [];

function startOrgLiveFeed() {
  // Tear down any existing subscription first
  if (_orgRealtimeChannel) {
    db.removeChannel(_orgRealtimeChannel);
    _orgRealtimeChannel = null;
  }
  if (!state.org) return;

  const dotEl   = document.getElementById('org-live-dot');
  const labelEl = document.getElementById('org-live-label');

  // Build a comma-separated list of porter IDs owned by org members (and owner)
  // We'll subscribe to all scans and filter client-side by org porter IDs
  const orgPorterIds = new Set(state.fleet.map(f => f.porter_id));

  _orgRealtimeChannel = db
    .channel('org-live-scans')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'scans' },
      (payload) => {
        const scan = payload.new;
        if (!orgPorterIds.has(scan.porter_id) && orgPorterIds.size > 0) return; // filter to org
        const porter = state.fleet.find(f => f.porter_id === scan.porter_id);
        const tagName = porter ? (porter.nickname || porter.identifier) : (scan.identifier || scan.porter_id?.substring(0,8));
        const loc = [scan.city_name || scan.city, scan.country_code || scan.country].filter(Boolean).join(', ') || null;
        const item = {
          id:      scan.id || Date.now(),
          tagName,
          device:  scan.device_type || scan.os_name || 'Unknown',
          loc,
          time:    new Date(scan.scanned_at || scan.created_at || Date.now()),
          lat:     scan.latitude  || null,
          lng:     scan.longitude || null,
          country: scan.country_code || scan.country || null,
        };
        _orgFeedItems.unshift(item);
        if (_orgFeedItems.length > 50) _orgFeedItems.pop();
        _renderOrgLiveFeedList();
        _addOrgMapPin(item);
        // Real-time scan toast
        const locStr = item.loc ? ` · ${item.loc}` : '';
        showToast(`📡 Scan: ${item.tagName}${locStr}`);
      }
    )
    .subscribe((status) => {
      const connected = status === 'SUBSCRIBED';
      if (dotEl)   dotEl.style.background   = connected ? '#22c55e' : '#ef4444';
      if (labelEl) labelEl.textContent       = connected ? 'Live' : (status === 'CHANNEL_ERROR' ? 'Error' : 'Connecting…');
      if (labelEl) labelEl.style.color       = connected ? '#22c55e' : '#ef4444';
      if (connected && dotEl) {
        dotEl.style.boxShadow = '0 0 0 3px rgba(34,197,94,0.25)';
      }
    });
}

function _renderOrgLiveFeedList() {
  const feed = document.getElementById('org-live-feed');
  if (!feed) return;
  if (!_orgFeedItems.length) {
    feed.innerHTML = `<div style="padding:12px 18px;color:#aaa;font-size:12px;">Waiting for scans…</div>`;
    return;
  }
  feed.innerHTML = _orgFeedItems.map((item, idx) => {
    const timeStr = item.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const isNew   = idx === 0;
    return `<div style="display:flex;gap:10px;align-items:flex-start;padding:7px 18px;border-top:1px solid #f0f0f4;${isNew?'animation:org-feed-in 0.35s ease;background:#f9f9ff;':''}">
      <span class="mi" style="font-size:15px;color:#6366f1;margin-top:1px;flex-shrink:0;">sensors</span>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:700;font-size:12px;color:#1d1d1f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHtmlSimple(item.tagName)}</div>
        <div style="font-size:11px;color:#888;">${escHtmlSimple(item.device)}${item.loc ? ' &nbsp;·&nbsp; ' + escHtmlSimple(item.loc) : ''}</div>
      </div>
      <div style="font-size:10px;color:#aaa;white-space:nowrap;flex-shrink:0;margin-top:2px;">${timeStr}</div>
    </div>`;
  }).join('');
}

// ─────────────────────────────────────────────────────────────────────────────
// ORG: LIVE MAP  (v4.2.0 — Leaflet.js + OpenStreetMap)
// ─────────────────────────────────────────────────────────────────────────────
let _orgMap = null;
let _orgMapMarkers = [];

// Country centroid fallbacks (ISO → [lat, lng]) for when we have country but no lat/lng
const COUNTRY_CENTROIDS = {
  US:[38,-97],GB:[54,-2],CA:[60,-95],AU:[-25,133],DE:[51,10],FR:[46,2],JP:[36,138],
  IN:[20,77],BR:[-15,-47],MX:[23,-102],ZA:[-29,25],NG:[10,8],CN:[35,105],RU:[60,100],
  KR:[37,127],AR:[-34,-64],IT:[42,12],ES:[40,-4],NL:[52,5],SE:[60,18],NO:[62,10],
  DK:[56,10],PL:[52,20],PT:[39,-8],BE:[50,4],CH:[47,8],AT:[47,14],NZ:[-41,174],
  SG:[1,104],AE:[24,54],SA:[24,45],IL:[31,35],TR:[39,35],PH:[13,122],TH:[13,101],
  VN:[16,108],ID:[-5,120],MY:[3,110],PK:[30,70],EG:[27,30],ZA:[-29,25],
};

async function initOrgLiveMap() {
  const mapEl = document.getElementById('org-live-map');
  if (!mapEl) return;

  // Load Leaflet if not already loaded
  if (!window.L) {
    await _loadLeaflet();
  }
  if (!window.L) { mapEl.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#aaa;font-size:12px;">Map unavailable</div>`; return; }

  // Destroy old map instance if re-initializing
  if (_orgMap) { _orgMap.remove(); _orgMap = null; _orgMapMarkers = []; }

  _orgMap = L.map('org-live-map', { zoomControl: true, scrollWheelZoom: false, attributionControl: true });
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 18,
  }).addTo(_orgMap);
  _orgMap.setView([20, 0], 2);

  // Load last 50 scans with geo for org's tags
  const orgPorterIds = state.fleet.map(f => f.porter_id).filter(Boolean);
  if (!orgPorterIds.length) return;

  try {
    const { data: recentScans } = await db
      .from('scans')
      .select('porter_id,latitude,longitude,country_code,country,city_name,city,device_type,os_name,scanned_at')
      .in('porter_id', orgPorterIds.slice(0, 100))
      .not('country_code', 'is', null)
      .order('scanned_at', { ascending: false })
      .limit(50);

    (recentScans || []).forEach(s => {
      const porter = state.fleet.find(f => f.porter_id === s.porter_id);
      const tagName = porter ? (porter.nickname || porter.identifier) : s.porter_id?.substring(0, 8);
      let lat = s.latitude, lng = s.longitude;
      if (!lat && s.country_code && COUNTRY_CENTROIDS[s.country_code]) {
        [lat, lng] = COUNTRY_CENTROIDS[s.country_code];
      }
      if (!lat) return;
      _addOrgMapPin({ tagName, lat, lng, device: s.device_type || s.os_name || 'Unknown',
        loc: [s.city_name || s.city, s.country_code].filter(Boolean).join(', '),
        time: new Date(s.scanned_at) });
    });
  } catch(e) { /* ignore — map still usable without historical pins */ }
}

function _addOrgMapPin(item) {
  if (!_orgMap || !window.L || !item.lat) return;

  // Slight jitter to avoid perfect stacking for same-country centroids
  const jLat = item.lat + (Math.random() - 0.5) * 0.8;
  const jLng = item.lng + (Math.random() - 0.5) * 0.8;

  const isLive = (Date.now() - item.time) < 10000; // <10 sec old = "live"
  const markerHtml = `<div style="
    width:${isLive?14:10}px;height:${isLive?14:10}px;
    border-radius:50%;
    background:${isLive?'#6366f1':'#a5b4fc'};
    border:2px solid white;
    box-shadow:0 1px 4px rgba(0,0,0,0.25);
    ${isLive?'animation:org-map-pulse 1.5s ease infinite;':''}
  "></div>`;

  const icon = L.divIcon({ html: markerHtml, className: '', iconSize: [isLive?14:10, isLive?14:10], iconAnchor: [isLive?7:5, isLive?7:5] });
  const timeStr = item.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const marker = L.marker([jLat, jLng], { icon })
    .addTo(_orgMap)
    .bindPopup(`<strong>${escHtmlSimple(item.tagName)}</strong><br>${escHtmlSimple(item.device)}${item.loc ? '<br>' + escHtmlSimple(item.loc) : ''}<br><span style="color:#aaa;font-size:11px;">${timeStr}</span>`);

  _orgMapMarkers.push(marker);
  if (_orgMapMarkers.length > 50) {
    const old = _orgMapMarkers.shift();
    _orgMap.removeLayer(old);
  }
}

async function _loadLeaflet() {
  return new Promise(resolve => {
    if (window.L) { resolve(); return; }
    // Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);
    }
    // Leaflet JS
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
    s.onload = resolve; s.onerror = resolve;
    document.head.appendChild(s);
  });
}

// ── Helper: auto-generate slug from name ──────────────────────────────────────
function _orgAutoSlug() {
  const name = document.getElementById('org-create-name')?.value || '';
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);
  const slugEl = document.getElementById('org-create-slug');
  if (slugEl && !slugEl.dataset.userEdited) slugEl.value = slug;
}
function _orgSlugSanitize(el) {
  el.dataset.userEdited = '1';
  el.value = el.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
}

// ── Create organization ───────────────────────────────────────────────────────
async function createOrg() {
  const name = document.getElementById('org-create-name')?.value?.trim();
  const slug = document.getElementById('org-create-slug')?.value?.trim();
  const errEl = document.getElementById('org-create-err');

  const setErr = (msg) => { if(errEl){ errEl.innerText=msg; errEl.style.display='block'; } };

  if (!name) { setErr('Organization name is required.'); return; }
  if (!slug || slug.length < 3) { setErr('Slug must be at least 3 characters.'); return; }
  if (!/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/.test(slug)) { setErr('Slug: lowercase letters, numbers, and hyphens only.'); return; }

  const btn = document.querySelector('#org-panel-content .btn-primary');
  if (btn) { btn.innerText = 'Creating…'; btn.disabled = true; }

  try {
    const { data, error } = await db.rpc('create_organization', { p_name: name, p_slug: slug, p_shopify_user_id: SHOPIFY_GID });
    if (error) throw error;
    state.orgLoaded = false;
    await loadOrgData();
    showToast('Organization created ✓');
  } catch(err) {
    setErr(`Failed: ${err.message}`);
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="mi mi-sm">add_business</span> Create Organization'; }
  }
}

// ── Invite member ─────────────────────────────────────────────────────────────
// Edge Function URL for sending invite emails via Supabase Auth admin API.
// Deploy `Supabase Edge Functions/send-org-invite/index.ts` and paste the URL here.
// If blank or unreachable, inviteOrgMember() falls back to copy-link mode automatically.
const ORG_INVITE_EDGE_FN = 'https://mkmflvqaglqjemugliau.supabase.co/functions/v1/send-org-invite';

async function inviteOrgMember() {
  if (!state.org) return;
  const email = document.getElementById('org-invite-email')?.value?.trim();
  const role  = document.getElementById('org-invite-role')?.value || 'member';
  const errEl = document.getElementById('org-invite-err');

  const setErr = (msg) => { if(errEl){ errEl.innerText=msg; errEl.style.display='block'; } };
  if (errEl) errEl.style.display = 'none';

  if (!email || !email.includes('@')) { setErr('Enter a valid email address.'); return; }

  const btn = document.querySelector('#org-panel-content .btn-primary');
  if (btn) { btn.innerHTML = '<span class="mi mi-sm">sync</span> Sending…'; btn.disabled = true; }

  try {
    // Step 1: Create the invite record in the DB and get the secure token
    const { data, error } = await db.rpc('invite_org_member', {
      p_org_id:           state.org.id,
      p_email:            email,
      p_role:             role,
      p_shopify_user_id:  SHOPIFY_GID,
    });
    if (error) throw error;

    const token      = data?.token;
    const inviteLink = token ? `${window.location.origin}/pages/charishub-org?invite=${token}` : null;

    // Step 2: Attempt to send the actual email via Edge Function (v4.0.0).
    // Falls back silently to copy-link mode if the function isn't deployed yet.
    let emailSent = false;
    if (token && ORG_INVITE_EDGE_FN) {
      try {
        // Get the current session JWT to authenticate the edge function call
        const { data: { session } } = await db.auth.getSession();
        const resp = await fetch(ORG_INVITE_EDGE_FN, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': session?.access_token ? `Bearer ${session.access_token}` : '',
            'apikey':        SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email,
            token,
            org_name:        state.org.name,
            role,
            redirect_origin: window.location.origin,
          }),
        });
        if (resp.ok) {
          emailSent = true;
        } else {
          // Edge function returned an error — extract message for debugging but don't surface to user
          const errBody = await resp.json().catch(() => ({}));
          console.warn('send-org-invite edge fn error:', errBody?.error || resp.status);
        }
      } catch (fetchErr) {
        // Network error or function not deployed — fall through to copy-link
        console.warn('send-org-invite edge fn unreachable:', fetchErr.message);
      }
    }

    // Step 3: Clear form and reload org panel
    const emailEl = document.getElementById('org-invite-email');
    if (emailEl) emailEl.value = '';
    state.orgLoaded = false;
    await loadOrgData();

    // Step 4: Notify user
    if (emailSent) {
      showToast(`Invite email sent to ${email} ✓`);
    } else if (inviteLink) {
      // Fallback: copy the invite link to clipboard
      await navigator.clipboard.writeText(inviteLink).catch(()=>{});
      showToast('Invite created — link copied to clipboard!');
    } else {
      showToast('Invite created ✓');
    }

  } catch(err) {
    setErr(`Failed: ${err.message}`);
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="mi mi-sm">person_add</span> Send Invite'; }
  }
}

// ── Revoke invite ─────────────────────────────────────────────────────────────
async function revokeOrgInvite(inviteId, email) {
  if (!confirm(`Revoke invite for ${email}?`)) return;
  try {
    const { error } = await db.rpc('revoke_org_invite', {
      p_invite_id:       inviteId,
      p_shopify_user_id: SHOPIFY_GID,
    });
    if (error) throw error;
    state.orgLoaded = false;
    await loadOrgData();
    showToast('Invite revoked.');
  } catch(err) {
    showToast(`Failed: ${err.message}`);
  }
}

// ── Remove member ─────────────────────────────────────────────────────────────
async function removeOrgMember(profileId, displayName) {
  if (!state.org) return;
  if (!confirm(`Remove ${displayName} from your organization?`)) return;
  try {
    const { error } = await db.rpc('remove_org_member', {
      p_org_id:           state.org.id,
      p_profile_id:       profileId,
      p_shopify_user_id:  SHOPIFY_GID,
    });
    if (error) throw error;
    state.orgLoaded = false;
    await loadOrgData();
    showToast(`${displayName} removed from org.`);
  } catch(err) {
    showToast(`Failed: ${err.message}`);
  }
}

// ── Update member role ────────────────────────────────────────────────────────
async function updateOrgMemberRole(profileId, newRole) {
  if (!state.org) return;
  try {
    const { error } = await db.rpc('update_org_member_role', {
      p_org_id:           state.org.id,
      p_profile_id:       profileId,
      p_new_role:         newRole,
      p_shopify_user_id:  SHOPIFY_GID,
    });
    if (error) throw error;
    state.orgLoaded = false;
    await loadOrgData();
    showToast('Role updated ✓');
  } catch(err) {
    showToast(`Failed: ${err.message}`);
    // Reload to restore correct state in the select
    state.orgLoaded = false;
    await loadOrgData();
  }
}

// ── Rename organization ───────────────────────────────────────────────────────
// Opens the existing charis-modal with a rename form.
// Calls rename_organization RPC (v4.1.0 migration SQL).
function openRenameOrg() {
  if (!state.org) return;
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText = 'Rename Organization';
  document.getElementById('modal-subtitle').innerText = 'Update the display name and URL slug for your org.';
  document.getElementById('v-msg').innerText = '';
  document.getElementById('modal-fields').innerHTML = `
    <label class="field-label">Organization Name</label>
    <input type="text" id="org-rename-name" class="charis-input" value="${(state.org.name||'').replace(/"/g,'&quot;')}" maxlength="80" placeholder="Acme Corporation">
    <label class="field-label">URL Slug</label>
    <div style="display:flex;align-items:center;gap:6px;">
      <span style="font-size:12px;color:#888;white-space:nowrap;">charishub.com/org/</span>
      <input type="text" id="org-rename-slug" class="charis-input" style="margin-bottom:0;flex:1;" value="${(state.org.slug||'').replace(/"/g,'&quot;')}" maxlength="50" placeholder="berne-apparel" oninput="this.value=this.value.toLowerCase().replace(/[^a-z0-9-]/g,'')">
    </div>
    <p style="font-size:11px;color:#aaa;margin-top:4px;">Lowercase letters, numbers, and hyphens only. Changing the slug updates your org&#x2019;s share URL.</p>`;
  const btn = document.getElementById('modal-confirm-btn');
  btn.className = 'btn-charis btn-primary';
  btn.innerText = 'Save Changes';
  btn.onclick = async () => {
    const newName = document.getElementById('org-rename-name')?.value?.trim();
    const newSlug = document.getElementById('org-rename-slug')?.value?.trim();
    if (!newName) { showError('Name is required.'); return; }
    if (!newSlug || newSlug.length < 3) { showError('Slug must be at least 3 characters.'); return; }
    btn.innerText = 'Saving…'; btn.disabled = true;
    try {
      const { error } = await db.rpc('rename_organization', {
        p_org_id:           state.org.id,
        p_new_name:         newName,
        p_new_slug:         newSlug,
        p_shopify_user_id:  SHOPIFY_GID,
      });
      if (error) throw error;
      closeModal();
      state.orgLoaded = false;
      await loadOrgData();
      showToast('Organization renamed ✓');
    } catch(err) {
      showError(`Failed: ${err.message}`);
      btn.disabled = false; btn.innerText = 'Save Changes';
    }
  };
  document.getElementById('charis-modal').classList.add('active');
}

// ── Leave organization (non-owner members only) ───────────────────────────────
async function leaveOrg() {
  if (!state.org || state.myOrgRole === 'owner') return;
  if (!confirm(`Leave ${state.org.name}? You will lose access to the organization's tags.`)) return;
  try {
    const { error } = await db.rpc('remove_org_member', {
      p_org_id:           state.org.id,
      p_profile_id:       state.profileId,
      p_shopify_user_id:  SHOPIFY_GID,
    });
    if (error) throw error;
    state.org = null; state.orgMembers = []; state.orgInvites = [];
    state.myOrgRole = null; state.orgLoaded = false;
    renderOrgPanel();
    showToast('You have left the organization.');
  } catch(err) {
    showToast(`Failed: ${err.message}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ORG: ASSIGN PORTER TO MEMBER  (v4.1.0)
// ─────────────────────────────────────────────────────────────────────────────
// Opens the shared charis-modal with a member select dropdown.
// Calls assign_porter_to_member RPC on confirm.
function openAssignPorterToMember(porterId, nickname) {
  if (!state.org || !state.orgMembers.length) {
    showToast('No org members to assign to.'); return;
  }
  // Build options: exclude the current user (owner of the tag is presumably the caller)
  const opts = state.orgMembers.map(m => {
    const label = m.display_name || m.full_name || m.email || m.profile_id.substring(0, 8);
    return `<option value="${m.profile_id}">${label}</option>`;
  }).join('');

  const modalTitle = document.getElementById('modal-title');
  const modalBody  = document.getElementById('modal-body');
  const modalSave  = document.getElementById('modal-save');

  if (!modalTitle || !modalBody || !modalSave) { showToast('Modal not ready.'); return; }

  modalTitle.innerText = `Assign "${nickname}" to Member`;
  modalBody.innerHTML = `
    <p style="font-size:13px;color:#555;margin:0 0 12px;">
      Transfer ownership of this tag to an organization member.
      The member will gain full control of the tag.
    </p>
    <label class="field-label">Select Member</label>
    <select id="assign-member-select" class="charis-input">${opts}</select>
    <div id="assign-member-err" style="color:var(--charis-danger);font-size:12px;display:none;margin-top:6px;"></div>
  `;

  // Wire the save button dynamically
  modalSave.onclick = async function() {
    const select = document.getElementById('assign-member-select');
    const errEl  = document.getElementById('assign-member-err');
    if (!select) return;
    const targetProfileId = select.value;
    if (!targetProfileId) { errEl.textContent='Please select a member.'; errEl.style.display='block'; return; }

    modalSave.disabled = true;
    modalSave.textContent = 'Assigning…';
    errEl.style.display = 'none';

    try {
      const { error } = await db.rpc('assign_porter_to_member', {
        p_porter_id:           porterId,
        p_target_profile_id:   targetProfileId,
        p_shopify_user_id:     SHOPIFY_GID,
      });
      if (error) throw error;
      document.getElementById('charis-modal').style.display = 'none';
      showToast('Tag assigned successfully!');
      await loadData(); // refresh fleet so new owner_id is reflected
    } catch(err) {
      errEl.textContent = err.message || 'Assignment failed.';
      errEl.style.display = 'block';
    } finally {
      modalSave.disabled = false;
      modalSave.textContent = 'Assign';
    }
  };

  // Update button label
  modalSave.textContent = 'Assign';
  document.getElementById('charis-modal').style.display = 'flex';
}


// ─────────────────────────────────────────────────────────────────────────────
// ORG: RESEND INVITE EMAIL  (v4.1.0)
// ─────────────────────────────────────────────────────────────────────────────
// Re-uses the same invite_org_member + edge function flow but targets an
// already-invited email, effectively resending without creating a new DB row.
// The RPC will find the existing pending invite and return its token.
async function resendOrgInviteFor(email, role) {
  if (!state.org) { showToast('No org loaded.'); return; }
  try {
    // Re-call invite_org_member — the RPC is idempotent for pending invites:
    // it updates expires_at and returns the existing (or new) token.
    const { data: token, error } = await db.rpc('invite_org_member', {
      p_org_id:          state.org.id,
      p_email:           email,
      p_role:            role,
      p_shopify_user_id: SHOPIFY_GID,
    });
    if (error) throw error;

    const inviteLink = `${window.location.origin}/pages/charishub-org?invite=${token}`;

    // Attempt edge function for email delivery
    let emailSent = false;
    try {
      const { data: { session } } = await db.auth.getSession();
      const resp = await fetch(ORG_INVITE_EDGE_FN, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          ...(session?.access_token ? { 'x-user-token': session.access_token } : {}),
        },
        body: JSON.stringify({
          email,
          token,
          org_name:        state.org.name,
          role,
          redirect_origin: window.location.origin,
        }),
      });
      if (resp.ok) emailSent = true;
    } catch(_) { /* edge function not deployed — fall through */ }

    if (emailSent) {
      showToast(`Invite resent to ${email}.`);
    } else {
      await navigator.clipboard.writeText(inviteLink).catch(()=>{});
      showToast(`Couldn't send email — invite link copied to clipboard.`);
    }
  } catch(err) {
    showToast(`Resend failed: ${err.message}`);
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// EVENT LISTENERS + INIT
// ─────────────────────────────────────────────────────────────────────────────
document.getElementById('tag-search').addEventListener('input', e=>{ state.searchTerm=e.target.value; renderFleet(); });
document.getElementById('help-overlay').addEventListener('click', closeHelp);
document.addEventListener('DOMContentLoaded', loadData);


// ─────────────────────────────────────────────────────────────────────────────
// DARK MODE (v4.2.2)
// ─────────────────────────────────────────────────────────────────────────────
function initDarkMode() {
  if (localStorage.getItem('charishub-dark') === '1') {
    document.body.classList.add('dark-mode');
    const btn = document.getElementById('dark-mode-btn');
    if (btn) btn.textContent = '☀️';
  }
}

function toggleDarkMode() {
  const isDark = document.body.classList.toggle('dark-mode');
  localStorage.setItem('charishub-dark', isDark ? '1' : '0');
  const btn = document.getElementById('dark-mode-btn');
  if (btn) btn.textContent = isDark ? '☀️' : '🌙';
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS STRIP (v4.2.2)
// ─────────────────────────────────────────────────────────────────────────────
function renderScanStatsStrip() {
  const strip = document.getElementById('scan-stats-strip');
  if (!strip) return;
  const scans = state.scans || [];
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const weekAgo    = new Date(Date.now() - 6 * 86400000);

  const scansToday = scans.filter(s => new Date(s.scanned_at || s.created_at) >= todayStart).length;
  const scansWeek  = scans.filter(s => new Date(s.scanned_at || s.created_at) >= weekAgo).length;
  const activeTags = new Set(
    scans.filter(s => new Date(s.scanned_at || s.created_at) >= weekAgo).map(s => s.porter_id || s.identifier)
  ).size;

  const countryCount = {};
  scans.forEach(s => { const c = s.country_code || s.country; if (c) countryCount[c] = (countryCount[c] || 0) + 1; });
  const topCountry = Object.entries(countryCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  strip.innerHTML = [
    `<div class="stat-pill"><span class="mi stat-icon">today</span><span class="stat-value">${scansToday}</span>&nbsp;today</div>`,
    `<div class="stat-pill"><span class="mi stat-icon">date_range</span><span class="stat-value">${scansWeek}</span>&nbsp;this week</div>`,
    `<div class="stat-pill"><span class="mi stat-icon">nfc</span><span class="stat-value">${activeTags}</span>&nbsp;active tags</div>`,
    `<div class="stat-pill"><span class="mi stat-icon">public</span><span class="stat-value">${topCountry}</span>&nbsp;top country</div>`,
  ].join('');
}

// ─────────────────────────────────────────────────────────────────────────────
// COMMAND PALETTE  ⌘K / Ctrl+K  (v4.2.2)
// ─────────────────────────────────────────────────────────────────────────────
let _cmdFocusIdx = -1;

function openCommandPalette() {
  const overlay = document.getElementById('cmd-palette-overlay');
  if (!overlay) return;
  overlay.classList.add('open');
  const input = document.getElementById('cmd-palette-input');
  if (!input) return;
  input.value = '';
  _cmdSearch('');
  requestAnimationFrame(() => input.focus());
}

function closeCommandPalette() {
  const overlay = document.getElementById('cmd-palette-overlay');
  if (overlay) overlay.classList.remove('open');
  _cmdFocusIdx = -1;
}

function _cmdSearch(query) {
  const q = (query || '').trim().toLowerCase();
  const results = [];

  // 1. Navigation actions
  const navItems = [
    { icon: 'folder',              label: 'Groups',        sub: 'Navigate → tab', action: () => switchCommandTab('groups') },
    { icon: 'bolt',                label: 'Library',       sub: 'Navigate → tab', action: () => switchCommandTab('library') },
    { icon: 'calendar_month',      label: 'Schedules',     sub: 'Navigate → tab', action: () => switchCommandTab('schedules') },
    { icon: 'format_list_numbered',label: 'Sequences',     sub: 'Navigate → tab', action: () => switchCommandTab('sequences') },
    { icon: 'alt_route',           label: 'Routes',        sub: 'Navigate → tab', action: () => switchCommandTab('routes') },
    { icon: 'share',               label: 'Shared',        sub: 'Navigate → tab', action: () => switchCommandTab('shared') },
    { icon: 'campaign',            label: 'Campaigns',     sub: 'Navigate → tab', action: () => switchCommandTab('campaigns') },
    { icon: 'corporate_fare',      label: 'Organization',  sub: 'Navigate → tab', action: () => { switchCommandTab('org'); loadOrgDataIfNeeded(); } },
    { icon: 'insights',            label: 'Insights',      sub: 'Navigate → view', action: () => switchView('insights') },
    { icon: 'analytics',           label: 'Analytics',     sub: 'Navigate → view', action: () => switchView('analytics') },
    { icon: 'dark_mode',           label: 'Toggle Dark Mode', sub: 'Appearance',  action: () => toggleDarkMode() },
    { icon: 'help',                label: 'Help',          sub: 'Open docs',      action: () => openHelp() },
  ];
  navItems.forEach(t => {
    if (!q || t.label.toLowerCase().includes(q) || (t.sub||'').toLowerCase().includes(q))
      results.push(t);
  });

  // 2. Tags / fleet
  (state.fleet || []).forEach(f => {
    const name = f.nickname || f.identifier || '';
    if (!q || name.toLowerCase().includes(q) || (f.identifier||'').toLowerCase().includes(q) || (f.group_name||'').toLowerCase().includes(q)) {
      results.push({
        icon: 'nfc',
        label: name,
        sub: f.identifier || '',
        action: () => { closeCommandPalette(); copyToClipboard(f.identifier); }
      });
    }
  });

  // 3. Groups
  (state.groups || []).forEach(g => {
    if (!q || (g.name||'').toLowerCase().includes(q)) {
      results.push({
        icon: 'folder',
        label: g.name,
        sub: 'Group',
        action: () => { closeCommandPalette(); switchCommandTab('groups'); }
      });
    }
  });

  // 4. Library actions
  (state.actions || []).forEach(a => {
    if (!q || (a.title||'').toLowerCase().includes(q) || (a.type||'').toLowerCase().includes(q)) {
      results.push({
        icon: 'bolt',
        label: a.title || 'Action',
        sub: `${(a.type||'').toUpperCase()} · Library`,
        action: () => { closeCommandPalette(); switchCommandTab('library'); }
      });
    }
  });

  _cmdFocusIdx = -1;
  _renderCmdResults(results.slice(0, 24));
}

function _renderCmdResults(results) {
  const container = document.getElementById('cmd-palette-results');
  if (!container) return;
  if (!results.length) {
    container.innerHTML = '<div id="cmd-palette-empty">No results — try a tag name, group, or action.</div>';
    container._cmdActions = [];
    return;
  }
  container.innerHTML = results.map((r, i) => `
    <div class="cmd-result-item" data-idx="${i}" onclick="_cmdActivate(${i})" onmouseover="_cmdFocus(${i})">
      <span class="material-symbols-outlined cmd-result-icon">${r.icon}</span>
      <span class="cmd-result-label">${_esc(r.label)}</span>
      <span class="cmd-result-sub">${_esc(r.sub || '')}</span>
    </div>
  `).join('');
  container._cmdActions = results.map(r => r.action);
}

function _cmdFocus(idx) {
  _cmdFocusIdx = idx;
  document.querySelectorAll('.cmd-result-item').forEach((el, i) => el.classList.toggle('focused', i === idx));
}

function _cmdActivate(idx) {
  const container = document.getElementById('cmd-palette-results');
  if (!container || !container._cmdActions) return;
  const fn = container._cmdActions[idx];
  closeCommandPalette();
  if (typeof fn === 'function') fn();
}

function _cmdKeyNav(e) {
  const items = document.querySelectorAll('.cmd-result-item');
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    _cmdFocusIdx = Math.min(_cmdFocusIdx + 1, items.length - 1);
    _cmdFocus(_cmdFocusIdx);
    items[_cmdFocusIdx]?.scrollIntoView({ block: 'nearest' });
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    _cmdFocusIdx = Math.max(_cmdFocusIdx - 1, 0);
    _cmdFocus(_cmdFocusIdx);
    items[_cmdFocusIdx]?.scrollIntoView({ block: 'nearest' });
  } else if (e.key === 'Enter') {
    if (_cmdFocusIdx >= 0) _cmdActivate(_cmdFocusIdx);
    else if (items.length > 0) _cmdActivate(0);
  } else if (e.key === 'Escape') {
    closeCommandPalette();
  }
}

// HTML-escape helper used by command palette
function _esc(s) {
  return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}


// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER IDENTITY LAYER (v4.3.0)
// ─────────────────────────────────────────────────────────────────────────────

function openCustomerIdentity(porterId, tagName) {
  const fleet = state.fleet || [];
  const item  = fleet.find(f => f.porter_id === porterId || f.id === porterId);
  state.editTarget = { id: porterId, type: 'customer_identity' };

  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText    = 'Customer Identity';
  document.getElementById('modal-subtitle').innerText = `Tag this porter to a specific customer — ${tagName}`;

  document.getElementById('modal-fields').innerHTML = `
    <label class="field-label">Customer Name / Label</label>
    <input type="text" id="ci-label" class="charis-input" placeholder="e.g. John Smith, Room 204, Badge #7" value="${escHtmlSimple(item?.customer_label || '')}">

    <label class="field-label">External Customer ID <span style="font-weight:400;color:#aaa;">(optional — your CRM/system ID)</span></label>
    <input type="text" id="ci-id" class="charis-input" placeholder="e.g. CUST-001, SF-0012345" value="${escHtmlSimple(item?.customer_ext_id || '')}">

    <label class="field-label">Customer Email <span style="font-weight:400;color:#aaa;">(optional — for personalisation)</span></label>
    <input type="email" id="ci-email" class="charis-input" placeholder="customer@example.com" value="${escHtmlSimple(item?.customer_email || '')}">

    <p style="font-size:11px;color:#888;margin-top:4px;line-height:1.5;">Customer data is attached to this tag and included in webhook payloads and scan notifications. Clear all fields to remove the identity.</p>
  `;

  document.getElementById('modal-confirm-btn').innerText = 'Save Identity';
  document.getElementById('modal-confirm-btn').onclick   = saveCustomerIdentity;
  document.getElementById('charis-modal').classList.add('active');
}

async function saveCustomerIdentity() {
  if (!state.editTarget?.id) return;
  const porterId     = state.editTarget.id;
  const label        = document.getElementById('ci-label')?.value?.trim() || null;
  const customerId   = document.getElementById('ci-id')?.value?.trim()    || null;
  const email        = document.getElementById('ci-email')?.value?.trim() || null;
  const btn          = document.getElementById('modal-confirm-btn');
  btn.disabled = true; btn.innerText = 'Saving…';
  try {
    const { error } = await db.rpc('set_customer_identity', {
      p_porter_id:      porterId,
      p_shopify_user_id: SHOPIFY_GID,
      p_customer_label: label,
      p_customer_id:    customerId,
      p_customer_email: email,
    });
    if (error) throw error;
    closeModal();
    showToast(label ? `Customer set: ${label}` : 'Customer identity cleared');
    loadData();
  } catch (err) {
    showError(`Save failed: ${err.message}`);
    btn.disabled = false; btn.innerText = 'Save Identity';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SCAN NOTIFICATIONS TOGGLE (v4.3.0)
// ─────────────────────────────────────────────────────────────────────────────

// Rendered into #scan-notify-strip (added to liquid)
function renderNotifyToggle() {
  const strip = document.getElementById('scan-notify-strip');
  if (!strip) return;
  const on = !!state.notifyOnScan;
  strip.innerHTML = `
    <button onclick="openNotifySettings()" style="display:flex;align-items:center;gap:7px;padding:6px 14px;border-radius:20px;background:${on?'#f0fdf4':'#f3f4f6'};font-size:12px;font-weight:600;color:${on?'#16a34a':'#6b7280'};border:1.5px solid ${on?'#bbf7d0':'#e5e7eb'};cursor:pointer;white-space:nowrap;" class="${on?'':''}">
      <span class="mi" style="font-size:15px;color:${on?'#16a34a':'#9ca3af'};">notifications${on?'_active':''}</span>
      Scan alerts ${on ? 'ON' : 'OFF'}
    </button>
  `;
}

function openNotifySettings() {
  document.getElementById('modal-tab-container').style.display = 'none';
  document.getElementById('modal-title').innerText    = 'Scan Notifications';
  document.getElementById('modal-subtitle').innerText = 'Get an email whenever any of your tags are scanned.';

  const currentEmail = state.notifyEmail || '';
  const isOn         = !!state.notifyOnScan;

  document.getElementById('modal-fields').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;padding:14px;background:#f9f9fb;border-radius:12px;margin-bottom:16px;">
      <div>
        <div style="font-weight:700;font-size:14px;">Email alerts on scan</div>
        <div style="font-size:12px;color:#888;margin-top:2px;">Sends an email each time any tag is scanned.</div>
      </div>
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
        <input type="checkbox" id="notify-toggle" ${isOn ? 'checked' : ''} style="width:18px;height:18px;cursor:pointer;">
      </label>
    </div>
    <label class="field-label">Notification email <span style="font-weight:400;color:#aaa;">(leave blank to use account email)</span></label>
    <input type="email" id="notify-email" class="charis-input" placeholder="you@example.com" value="${escHtmlSimple(currentEmail)}">
    <p style="font-size:11px;color:#888;margin-top:4px;line-height:1.5;">Notifications are sent via CharisHub. You can disable at any time.</p>
  `;

  document.getElementById('modal-confirm-btn').innerText = 'Save Preferences';
  document.getElementById('modal-confirm-btn').onclick   = saveNotifySettings;
  document.getElementById('charis-modal').classList.add('active');
}

async function saveNotifySettings() {
  const enabled = document.getElementById('notify-toggle')?.checked || false;
  const email   = document.getElementById('notify-email')?.value?.trim() || null;
  const btn     = document.getElementById('modal-confirm-btn');
  btn.disabled = true; btn.innerText = 'Saving…';
  try {
    const { error } = await db.rpc('set_notify_on_scan', {
      p_enabled:         enabled,
      p_email:           email,
      p_shopify_user_id: SHOPIFY_GID,
    });
    if (error) throw error;
    state.notifyOnScan = enabled;
    state.notifyEmail  = email;
    renderNotifyToggle();
    closeModal();
    showToast(enabled ? '🔔 Scan notifications enabled' : '🔕 Scan notifications off');
  } catch (err) {
    showError(`Save failed: ${err.message}`);
    btn.disabled = false; btn.innerText = 'Save Preferences';
  }
}

// ─── v4.4.0: User Profile Editor ─────────────────────────────────────────────

function openProfileEditor() {
  const sc = window.SHOPIFY_CUSTOMER || {};
  // Pre-fill from state (saved profile) or fall back to Shopify customer data
  const name    = state.displayName    || [sc.firstName, sc.lastName].filter(Boolean).join(' ') || '';
  const email   = state.profileEmail   || sc.email   || '';
  const phone   = state.profilePhone   || sc.phone   || '';
  const company = state.profileCompany || sc.company || '';

  document.getElementById('profile-display-name').value = name;
  document.getElementById('profile-email').value         = email;
  document.getElementById('profile-phone').value         = phone;
  document.getElementById('profile-company').value       = company;

  // Live preview in avatar row
  const preview = document.getElementById('profile-display-preview');
  if (preview) preview.innerText = name || '—';
  const idLabel = document.getElementById('profile-shopify-id-label');
  if (idLabel) idLabel.innerText = SHOPIFY_GID.replace('gid://shopify/Customer/', 'Shopify ID: ');

  // Wire up live preview on name input
  const nameInput = document.getElementById('profile-display-name');
  if (nameInput) {
    nameInput.oninput = () => {
      if (preview) preview.innerText = nameInput.value.trim() || '—';
    };
  }

  const btn = document.getElementById('profile-save-btn');
  if (btn) { btn.innerText = 'Save Profile'; btn.disabled = false; }

  document.getElementById('profile-modal').classList.add('active');
}

function closeProfileEditor() {
  document.getElementById('profile-modal').classList.remove('active');
}

async function saveProfile() {
  const displayName = document.getElementById('profile-display-name').value.trim();
  const email       = document.getElementById('profile-email').value.trim();
  const phone       = document.getElementById('profile-phone').value.trim();
  const company     = document.getElementById('profile-company').value.trim();

  const btn = document.getElementById('profile-save-btn');
  btn.innerText = 'Saving…'; btn.disabled = true;

  try {
    const { error } = await db.rpc('set_profile_display', {
      p_shopify_user_id: SHOPIFY_GID,
      p_display_name:    displayName,
      p_email:           email,
      p_phone:           phone,
      p_company:         company,
    });
    if (error) throw error;

    // Update local state
    state.displayName    = displayName || null;
    state.profileEmail   = email       || null;
    state.profilePhone   = phone       || null;
    state.profileCompany = company     || null;

    closeProfileEditor();
    showToast('Profile saved ✓');

    // Refresh org panel if open so member list shows updated name
    if (document.getElementById('view-org')?.style.display !== 'none') {
      loadOrgData();
    }
  } catch (err) {
    showError(`Save failed: ${err.message}`);
    btn.innerText = 'Save Profile'; btn.disabled = false;
  }
}
